import { useState, useCallback, useRef, useEffect, useMemo, lazy, Suspense } from "react";
import LoginScreen from "./LoginScreen";
import NewsTicker from "./NewsTicker";
import Portfolio from "./Portfolio";
import AlertCenter from "./AlertCenter";
import PolicyEvents, { PolicyQuickNav } from "./PolicyEvents";
import MemoryPrices from "./MemoryPrices";
import OpinionPage from "./CommunityIndex"; // 유튜브 댓글 + 주식 커뮤니티 지수 (탭)
import MarketFunds from "./MarketFunds";
import { computeDividendDerived, backfillDividends, aggregateByYear } from "./lib/dividends";
import FinancialIncomeTax from "./FinancialIncomeTax";
import DividendHoldings, { applyHoldingDefaults } from "./DividendHoldings";
import { planAutoDividends, applyAutoPlan, kstToday } from "./lib/divAuto";
import "./styles/common.css";
import "./styles/sidebar.css";
import "./styles/table.css";
import { Ico } from "./lib/icons";
import { ymd } from "./lib/dates";
import { getAuth, clearAuth, refreshAuthIfNeeded } from "./lib/authFetch";

const Home = lazy(() => import("./Home"));
const StockAnalysis = lazy(() => import("./StockAnalysis"));
const MarketIndicesLoader = lazy(() => import("./StockAnalysis").then(m => ({ default: m.MarketIndices })));
const BondYieldsLoader = lazy(() => import("./StockAnalysis").then(m => ({ default: m.BondYields })));
const DailyMarketAnalysis = lazy(() => import("./DailyMarketAnalysis"));
const NewsPage = lazy(() => import("./NewsTicker").then(m => ({ default: m.NewsPage })));
const MarketCalendar = lazy(() => import("./MarketCalendar"));
const EarningsPage = lazy(() => import("./StockAnalysis").then(m => ({ default: m.EarningsCalendar })));
const BrokerReports = lazy(() => import("./BrokerResearch").then(m => ({ default: m.BrokerReports })));
const Settings = lazy(() => import("./Settings"));

const LazyFallback = () => <div style={{ textAlign: "center", padding: 60, color: "#4a5568" }}>로딩중...</div>;

const SK = "sjv3-trades";
const USK = "sjv3-us-trades";
const DK = "sjv3-dividends";
const BASE_API = process.env.REACT_APP_API_URL || "http://localhost:3001";

// xlsx (~430KB)는 엑셀 양식/임포트/익스포트에서만 사용 → 초기 번들 절감 위한 lazy load
// 모든 entry point(downloadTemplate / exportJournal / handleImport)는 await ensureXLSX() 먼저 호출
let XLSX = null;
async function ensureXLSX() {
  if (XLSX) return XLSX;
  const mod = await import("xlsx");
  XLSX = mod.default || mod;
  return XLSX;
}

const fmt = (n) => n == null ? "-" : Math.round(n).toLocaleString("ko-KR") + "원";
const fmtD = (n) => n == null ? "-" : (n < 0 ? "-$" : "$") + Math.abs(n < 100 ? parseFloat(n.toFixed(2)) : Math.round(n)).toLocaleString();
const fmtPct = (n) => n == null ? "-" : (n >= 0 ? "+" : "") + n.toFixed(2) + "%";
const today = () => ymd(); // 한국 시간 기준 오늘 (lib/dates.js)
const daysBetween = (a, b) => Math.round((new Date(b) - new Date(a)) / 86400000);

// Design Ref: position-tracking §4.3 — 종목 식별자 (D-02: name + broker 정확 일치)
const posKey = (name, broker) => `${(name||"").trim()}|${(broker||"").trim()}`;

// Design Ref: position-tracking §4.1 — 레거시 행 → 가상 [BUY, SELL] 분리 (D-05)
function legacyToEvents(t) {
  const qty = parseInt(t.quantity) || 0;
  const buyPriceRaw = t.buyPrice ?? t.buyPriceUsd;
  const sellPriceRaw = t.sellPrice ?? t.sellPriceUsd;
  const buyPrice = parseFloat(buyPriceRaw) || 0;
  const sellPrice = parseFloat(sellPriceRaw) || 0;
  if (t.kind === "BUY")  return [{ kind: "BUY",  date: t.buyDate,  price: buyPrice,  qty, rowId: t.id, isLegacy: false }];
  if (t.kind === "SELL") return [{ kind: "SELL", date: t.sellDate, price: sellPrice, qty, rowId: t.id, isLegacy: false }];
  // 레거시 미체결 → 가상 BUY 1개
  if (!t.sellDate || !sellPriceRaw) {
    return [{ kind: "BUY", date: t.buyDate, price: buyPrice, qty, rowId: `${t.id}#buy`, isLegacy: true }];
  }
  // 레거시 체결 → 가상 BUY + SELL
  return [
    { kind: "BUY",  date: t.buyDate,  price: buyPrice,  qty, rowId: `${t.id}#buy`,  isLegacy: true },
    { kind: "SELL", date: t.sellDate, price: sellPrice, qty, rowId: `${t.id}#sell`, isLegacy: true },
  ];
}

// Design Ref: position-tracking §4.2 — 종목별 집계 (BUY 평균단가, 매도 시점별 실현손익)
function computePositions(trades) {
  const events = trades.flatMap(legacyToEvents);
  const tradeById = new Map(trades.map(t => [t.id, t]));
  const groups = new Map();
  for (const t of trades) {
    const k = posKey(t.name, t.broker);
    if (!groups.has(k)) groups.set(k, { key: k, name: t.name, broker: t.broker || "", events: [], rowIds: new Set() });
  }
  for (const ev of events) {
    const baseId = ev.rowId.split("#")[0];
    const t = tradeById.get(baseId);
    if (!t) continue;
    const k = posKey(t.name, t.broker);
    const g = groups.get(k);
    g.events.push(ev);
    g.rowIds.add(baseId);
  }
  const positions = [];
  for (const [key, g] of groups) {
    const sorted = g.events.slice().sort((a, b) => (a.date || "").localeCompare(b.date || ""));
    let buyQty = 0, buyAmt = 0, sellQty = 0, realized = 0;
    for (const ev of sorted) {
      if (ev.kind === "BUY") { buyQty += ev.qty; buyAmt += ev.price * ev.qty; }
      else {
        const avgAtSell = buyQty > 0 ? buyAmt / buyQty : 0;
        realized += (ev.price - avgAtSell) * ev.qty;
        sellQty += ev.qty;
      }
    }
    const avgCost = buyQty > 0 ? buyAmt / buyQty : 0;
    const qty = buyQty - sellQty;
    positions.push({
      key, name: g.name, broker: g.broker,
      qty, totalBuyQty: buyQty, totalSellQty: sellQty,
      avgCost, totalBuyAmt: buyAmt, realizedPnl: realized,
      events: sorted, rowIds: Array.from(g.rowIds),
      lastDate: sorted.length ? sorted[sorted.length - 1].date : "",
      isActive: qty > 0,
    });
  }
  // Design Ref: position-tracking Addendum — 활성(보유 중) 위, 완료 아래, 각각 lastDate desc
  return positions.sort((a, b) => {
    if (a.isActive !== b.isActive) return a.isActive ? -1 : 1;
    return (b.lastDate || "").localeCompare(a.lastDate || "");
  });
}

// 저장 직후 단일 종목의 평단/잔여를 빠르게 계산 (토스트용)
function computePostSavePosition(trades, name, broker) {
  const groupKey = posKey(name, broker);
  const events = trades.filter(t => posKey(t.name, t.broker) === groupKey).flatMap(legacyToEvents);
  let buyQty = 0, buyAmt = 0, sellQty = 0;
  events.forEach(ev => {
    if (ev.kind === "BUY") { buyQty += ev.qty; buyAmt += ev.price * ev.qty; }
    else { sellQty += ev.qty; }
  });
  return { qty: buyQty - sellQty, avgCost: buyQty > 0 ? buyAmt / buyQty : 0 };
}

// Design Ref: §3.1 — broker(broker-field) + kind(position-tracking, D-09: BUY 기본값)
const EMPTY_KR = { kind: "BUY", broker: "", buyDate: today(), sellDate: "", name: "", buyPrice: "", sellPrice: "", quantity: "", reason: "", memo: "" };
const EMPTY_US = { kind: "BUY", broker: "", buyDate: today(), sellDate: "", name: "", buyPriceUsd: "", sellPriceUsd: "", quantity: "", reason: "", memo: "" };
// Design Ref: dividend-tax-tracking §3.1 — tax / afterTax / perShare / taxExempt (자동 계산)
const EMPTY_DIV = { broker: "", date: today(), name: "", amount: "", tax: "", afterTax: "", perShare: "", rate: "", taxExempt: false, taxBasePerShare: "", taxPerShare: "", currency: "KRW", shares: "", memo: "" };

// Design Ref: §3.2 — 자주 쓰는 증권사 (drop-down, 자유입력 가능)
const BROKER_KR = ["키움증권", "NH투자증권", "미래에셋증권", "삼성증권", "한국투자증권", "토스증권", "KB증권", "신한투자증권", "유안타증권", "하나증권", "대신증권", "메리츠증권", "한화투자증권", "카카오페이증권", "교보증권", "하이투자증권", "LS증권", "DB금융투자", "현대차증권", "IBK투자증권", "SK증권", "신영증권", "유진투자증권", "다올투자증권", "BNK투자증권"];
const BROKER_US = ["키움증권", "미래에셋증권", "한국투자증권", "삼성증권", "토스증권", "유안타증권", "NH투자증권", "KB증권", "신한투자증권", "하나증권", "대신증권", "메리츠증권", "카카오페이증권", "Interactive Brokers", "Charles Schwab", "Firstrade"];
const BROKER_DIV = Array.from(new Set([...BROKER_KR, ...BROKER_US]));

const KR_TABS = [
  { id: "overview", label: "대시보드" },
  { id: "journal", label: "거래일지" },
  { id: "holding", label: "보유현황" },
  { id: "monthly", label: "월별손익" },
  { id: "stats", label: "종목통계" },
];
const US_TABS = [
  { id: "us-overview", label: "대시보드" },
  { id: "us-journal", label: "거래일지" },
  { id: "us-holding", label: "보유현황" },
  { id: "us-monthly", label: "월별손익" },
  { id: "us-stats", label: "종목통계" },
];

const LISTING_DATES = { "에이텀": "2023-12-01" };

function loadLS(key, fallback) {
  try { const v = localStorage.getItem(key); return v ? JSON.parse(v) : fallback; } catch { return fallback; }
}
function saveLS(key, val) {
  try { localStorage.setItem(key, JSON.stringify(val)); } catch {}
}

// 배당 수령일 기준 USD→KRW 환율 (서버 /exchange-rate?date= → frankfurter ECB). 날짜별 캐시.
const _histRateCache = {};
async function fetchHistoricalRate(date) {
  if (!date || !/^\d{4}-\d{2}-\d{2}$/.test(date)) return null;
  if (_histRateCache[date] != null) return _histRateCache[date];
  try {
    const res = await fetch(`${BASE_API}/exchange-rate?date=${date}`);
    if (!res.ok) return null;
    const d = await res.json();
    if (d && d.rate) { _histRateCache[date] = d.rate; return d.rate; }
  } catch {}
  return null;
}

// ETF 분배금 주당 과세표준 — 운용사 공식 사이트 조회 (서버 /etf-tax/:code). 과세표준은 운용사가 정해서 증권사와 무관.
const ETF_TAX_BRANDS = ["KODEX", "TIGER", "ACE", "RISE", "SOL", "PLUS"];
const etfBrandOf = (name) => { const n = String(name || "").trim().toUpperCase(); return ETF_TAX_BRANDS.find(b => n.startsWith(b)) || null; };
const _etfCodeCache = {};
async function resolveKrCode(name) {
  const key = String(name || "").replace(/\s+/g, "").toLowerCase();
  if (!key) return null;
  if (_etfCodeCache[key] !== undefined) return _etfCodeCache[key];
  try {
    const res = await fetch(`${BASE_API}/stockSearch?q=${encodeURIComponent(String(name).trim())}`);
    const r = res.ok ? await res.json() : [];
    const hit = (Array.isArray(r) ? r : []).find(s => String(s.name || "").replace(/\s+/g, "").toLowerCase() === key);
    _etfCodeCache[key] = hit ? hit.code : null;
  } catch { return null; }
  return _etfCodeCache[key];
}
async function fetchUsDist(ticker, date) {
  const res = await fetch(`${BASE_API}/us-dist/${encodeURIComponent(ticker)}${date ? `?date=${date}` : ""}`);
  if (!res.ok) throw new Error(String(res.status));
  return res.json();
}
async function fetchEtfTaxBase(code, name, date) {
  const res = await fetch(`${BASE_API}/etf-tax/${encodeURIComponent(code)}?name=${encodeURIComponent(name)}${date ? `&date=${date}` : ""}`);
  if (!res.ok) throw new Error(String(res.status));
  return res.json();
}

function parseKoreanDate(val, stockName) {
  if (!val) return "";
  const s = String(val).trim();
  if (!s || s === "-") return "";
  if (s === "상장일") return (stockName && LISTING_DATES[stockName]) || "";
  const m1 = s.match(/^(\d{2})\.(\d{2})\.(\d{2})$/);
  if (m1) return `20${m1[1]}-${m1[2]}-${m1[3]}`;
  const m2 = s.match(/^(\d{4})\.(\d{2})\.(\d{2})$/);
  if (m2) return `${m2[1]}-${m2[2]}-${m2[3]}`;
  return "";
}

// Design Ref: position-tracking M6 — kind 분기 헬퍼 (BUY/SELL 정규화)
function parseKindCell(raw) {
  if (!raw) return null;
  const v = String(raw).trim().toUpperCase();
  if (v === "매수" || v === "BUY" || v === "매수입력") return "BUY";
  if (v === "매도" || v === "SELL" || v === "매도입력") return "SELL";
  return null;
}

// 양식 파일 임포트 (국내주식 시트) — 신/구 형식 자동 분기
function parseTemplateKr(workbook) {
  const ws = workbook.Sheets["국내주식"];
  if (!ws) return [];
  const rows = XLSX.utils.sheet_to_json(ws, { header: 1, defval: null });
  if (!rows.length) return [];
  const header = (rows[0] || []).map(h => String(h || "").trim());
  // 헤더에 "구분" 컬럼 존재 여부로 신/구 분기
  const kindIdx = header.findIndex(h => h.startsWith("구분"));
  if (kindIdx >= 0) return parseTemplateKrNew(rows, header, kindIdx);
  return parseTemplateKrLegacy(rows);
}

// 신 형식 (이벤트 단위, kind 컬럼)
function parseTemplateKrNew(rows, header, kindIdx) {
  const findIdx = (label) => header.findIndex(h => h === label || h.startsWith(label));
  const brokerIdx = findIdx("증권사");
  const dateIdx   = findIdx("거래일");
  const nameIdx   = findIdx("종목명");
  const priceIdx  = findIdx("단가");
  const qtyIdx    = findIdx("수량");
  const reasonIdx = findIdx("매매이유");
  const memoIdx   = findIdx("메모");
  const result = [];
  for (let i = 1; i < rows.length; i++) {
    const row = rows[i];
    if (!row) continue;
    const name = nameIdx >= 0 && row[nameIdx] ? String(row[nameIdx]).trim() : "";
    if (!name || name === "종목명") continue;
    const kind = parseKindCell(row[kindIdx]);
    if (!kind) continue;  // 구분 미입력/잘못된 값 행은 스킵
    const price = parseFloat(row[priceIdx]) || 0;
    const quantity = parseInt(row[qtyIdx]) || 0;
    if (!price || !quantity) continue;
    const broker = brokerIdx >= 0 && row[brokerIdx] ? String(row[brokerIdx]).trim() : "";
    const date   = dateIdx   >= 0 && row[dateIdx]   ? String(row[dateIdx]).trim()   : today();
    const reason = reasonIdx >= 0 && row[reasonIdx] ? String(row[reasonIdx]).trim() : "";
    const memo   = memoIdx   >= 0 && row[memoIdx]   ? String(row[memoIdx]).trim()   : "";
    const entry = {
      id: `tmpl_${Date.now()}_${Math.random().toString(36).slice(2)}_${i}`,
      kind, broker, name, quantity: String(quantity), reason, memo,
      buyDate: kind === "BUY" ? date : "",
      buyPrice: kind === "BUY" ? String(price) : "",
      sellDate: kind === "SELL" ? date : "",
      sellPrice: kind === "SELL" ? String(price) : "",
    };
    result.push(entry);
  }
  return result;
}

// 구 형식 (1행=매수+매도 결합형, 레거시) — broker는 메모 prefix 대신 broker 필드로 직접 매핑
function parseTemplateKrLegacy(rows) {
  const result = [];
  for (let i = 1; i < rows.length; i++) {
    const row = rows[i];
    if (!row || !row[3]) continue;
    const name = String(row[3]).trim();
    if (!name || name === "종목명") continue;
    const buyPrice = parseFloat(row[4]) || 0;
    const quantity = parseInt(row[6]) || 0;
    if (!buyPrice || !quantity) continue;
    const broker = row[0] ? String(row[0]).trim() : "";
    result.push({
      id: `tmpl_${Date.now()}_${Math.random().toString(36).slice(2)}_${i}`,
      broker,                                           // broker-field M4: 메모 prefix → 정식 broker 필드
      buyDate: row[1] ? String(row[1]).trim() : today(),
      sellDate: row[2] ? String(row[2]).trim() : "",
      name,
      buyPrice: String(buyPrice),
      sellPrice: parseFloat(row[5]) ? String(parseFloat(row[5])) : "",
      quantity: String(quantity),
      reason: row[7] ? String(row[7]).trim() : "",
      memo: row[8] ? String(row[8]).trim() : "",
      // kind 미설정 = 레거시 행으로 client에서 자동 감지
    });
  }
  return result;
}

// 양식 파일 임포트 (미국주식 시트) — 신/구 형식 자동 분기
function parseTemplateUs(workbook) {
  const ws = workbook.Sheets["미국주식"];
  if (!ws) return [];
  const rows = XLSX.utils.sheet_to_json(ws, { header: 1, defval: null });
  if (!rows.length) return [];
  const header = (rows[0] || []).map(h => String(h || "").trim());
  const kindIdx = header.findIndex(h => h.startsWith("구분"));
  if (kindIdx >= 0) return parseTemplateUsNew(rows, header, kindIdx);
  return parseTemplateUsLegacy(rows);
}

function parseTemplateUsNew(rows, header, kindIdx) {
  const findIdx = (label) => header.findIndex(h => h === label || h.startsWith(label));
  const brokerIdx = findIdx("증권사");
  const dateIdx   = findIdx("거래일");
  const nameIdx   = findIdx("종목명");
  const priceIdx  = findIdx("단가");
  const qtyIdx    = findIdx("수량");
  const reasonIdx = findIdx("매매이유");
  const memoIdx   = findIdx("메모");
  const result = [];
  for (let i = 1; i < rows.length; i++) {
    const row = rows[i];
    if (!row) continue;
    const name = nameIdx >= 0 && row[nameIdx] ? String(row[nameIdx]).trim() : "";
    if (!name || name === "종목명") continue;
    const kind = parseKindCell(row[kindIdx]);
    if (!kind) continue;
    const price = parseFloat(row[priceIdx]) || 0;
    const quantity = parseInt(row[qtyIdx]) || 0;
    if (!price || !quantity) continue;
    const broker = brokerIdx >= 0 && row[brokerIdx] ? String(row[brokerIdx]).trim() : "";
    const date   = dateIdx   >= 0 && row[dateIdx]   ? String(row[dateIdx]).trim()   : today();
    const reason = reasonIdx >= 0 && row[reasonIdx] ? String(row[reasonIdx]).trim() : "";
    const memo   = memoIdx   >= 0 && row[memoIdx]   ? String(row[memoIdx]).trim()   : "";
    const entry = {
      id: `tmpl_us_${Date.now()}_${Math.random().toString(36).slice(2)}_${i}`,
      kind, broker, name, quantity: String(quantity), reason, memo,
      buyDate: kind === "BUY" ? date : "",
      buyPriceUsd: kind === "BUY" ? String(price) : "",
      sellDate: kind === "SELL" ? date : "",
      sellPriceUsd: kind === "SELL" ? String(price) : "",
    };
    result.push(entry);
  }
  return result;
}

function parseTemplateUsLegacy(rows) {
  const result = [];
  for (let i = 1; i < rows.length; i++) {
    const row = rows[i];
    if (!row || !row[3]) continue;
    const name = String(row[3]).trim();
    if (!name || name === "종목명") continue;
    const buyPriceUsd = parseFloat(row[4]) || 0;
    const quantity = parseInt(row[6]) || 0;
    if (!buyPriceUsd || !quantity) continue;
    const broker = row[0] ? String(row[0]).trim() : "";
    result.push({
      id: `tmpl_us_${Date.now()}_${Math.random().toString(36).slice(2)}_${i}`,
      broker,
      buyDate: row[1] ? String(row[1]).trim() : today(),
      sellDate: row[2] ? String(row[2]).trim() : "",
      name,
      buyPriceUsd: String(buyPriceUsd),
      sellPriceUsd: parseFloat(row[5]) ? String(parseFloat(row[5])) : "",
      quantity: String(quantity),
      reason: "",
      memo: row[7] ? String(row[7]).trim() : "",
    });
  }
  return result;
}

function parseKrSheet(workbook, sheetName) {
  const ws = workbook.Sheets[sheetName];
  if (!ws) return [];
  const rows = XLSX.utils.sheet_to_json(ws, { header: 1, defval: null });
  const result = [];
  for (let i = 3; i < rows.length; i++) {
    const row = rows[i];
    if (!row || !row[0]) continue;
    const name = String(row[0]).trim();
    if (!name || name === "종목명") continue;
    if (/^(날짜|주식 총|에이텀으로|\d{2}년\s*\d{1,2}월|\d{4}년)/.test(name)) continue;
    const buyPrice = parseFloat(row[3]) || 0;
    const quantity = parseInt(row[5]) || 0;
    if (!buyPrice || !quantity) continue;
    result.push({
      id: `kr_${Date.now()}_${Math.random().toString(36).slice(2)}`,
      buyDate: parseKoreanDate(row[1], name) || today(),
      sellDate: parseKoreanDate(row[2], name) || "",
      name,
      buyPrice: String(buyPrice),
      sellPrice: parseFloat(row[4]) ? String(parseFloat(row[4])) : "",
      quantity: String(quantity),
      reason: "",
      memo: row[8] ? String(row[8]).trim() : "",
    });
  }
  return result;
}

function parseIsaKr(workbook) {
  const ws = workbook.Sheets["키움증권 ISA 배당주 및 미국주식"];
  if (!ws) return [];
  const rows = XLSX.utils.sheet_to_json(ws, { header: 1, defval: null });
  const result = [];
  for (let i = 22; i < rows.length; i++) {
    const row = rows[i];
    if (!row || !row[0]) continue;
    const name = String(row[0]).trim();
    if (!name || name === "종목명") continue;
    if (/^(날짜|주식 총|에이텀으로|\d{2}년\s*\d{1,2}월|\d{4}년)/.test(name)) continue;
    const buyPrice = parseFloat(row[3]) || 0;
    const quantity = parseInt(row[5]) || 0;
    if (!buyPrice || !quantity) continue;
    result.push({
      id: `isa_${Date.now()}_${Math.random().toString(36).slice(2)}`,
      buyDate: parseKoreanDate(row[1], name) || today(),
      sellDate: parseKoreanDate(row[2], name) || "",
      name,
      buyPrice: String(buyPrice),
      sellPrice: parseFloat(row[4]) ? String(parseFloat(row[4])) : "",
      quantity: String(quantity),
      reason: "",
      memo: row[8] ? `[ISA] ${String(row[8]).trim()}` : "[ISA]",
    });
  }
  return result;
}

function parseUsSheet(workbook) {
  const ws = workbook.Sheets["키움증권 ISA 배당주 및 미국주식"];
  if (!ws) return [];
  const rows = XLSX.utils.sheet_to_json(ws, { header: 1, defval: null });
  const result = [];
  for (let i = 2; i < 20; i++) {
    const row = rows[i];
    if (!row || !row[0]) continue;
    const name = String(row[0]).trim();
    if (!name || name === "종목명") continue;
    if (/^ISA/.test(name)) break;
    const buyPriceUsd = parseFloat(row[3]) || 0;
    const quantity = parseInt(row[7]) || 0;
    if (!buyPriceUsd || !quantity) continue;
    result.push({
      id: `us_${Date.now()}_${Math.random().toString(36).slice(2)}`,
      buyDate: parseKoreanDate(row[1], name) || today(),
      sellDate: parseKoreanDate(row[2], name) || "",
      name,
      buyPriceUsd: String(buyPriceUsd),
      sellPriceUsd: parseFloat(row[5]) ? String(parseFloat(row[5])) : "",
      quantity: String(quantity),
      reason: "",
      memo: row[10] ? String(row[10]).trim() : "",
    });
  }
  return result;
}

function Tag({ children, blue }) {
  return (
    <span style={{
      background: blue ? "rgba(56,189,248,0.12)" : "rgba(251,191,36,0.12)",
      color: blue ? "#38bdf8" : "#fbbf24",
      border: blue ? "1px solid rgba(56,189,248,0.3)" : "1px solid rgba(251,191,36,0.3)",
      borderRadius: 6, padding: "2px 10px", fontSize: 11, fontWeight: 600, marginLeft: 8,
    }}>
      {children}
    </span>
  );
}

function Stat({ label, value, value2, color, small }) {
  return (
    <div className="ta-stat">
      <div className="ta-stat-label">{label}</div>
      <div className="ta-stat-value" style={{ fontSize: small ? 18 : 22, color: color || "var(--text)" }}>{value}</div>
      {value2 && <div style={{ fontSize: 12, color: "var(--text-sub)", fontFamily: "'DM Mono', monospace", marginTop: 4 }}>{value2}</div>}
    </div>
  );
}

function Modal({ title, onClose, onSave, children }) {
  return (
    <div className="ta-modal-overlay">
      <div className="ta-modal">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
          <div style={{ fontSize: 18, fontWeight: 700, color: "var(--text)" }}>{title}</div>
          <button onClick={onClose} style={{ background: "none", border: "none", color: "var(--text-sub)", cursor: "pointer", fontSize: 22, lineHeight: 1 }}>×</button>
        </div>
        {children}
        <div style={{ display: "flex", gap: 10, marginTop: 24, justifyContent: "flex-end" }}>
          <button className="ta-btn-outline" onClick={onClose}>취소</button>
          <button className="ta-btn-primary" onClick={onSave}>저장</button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <div style={{ fontSize: 11, color: "var(--text-sub)", marginBottom: 6, letterSpacing: "0.06em", textTransform: "uppercase", fontFamily: "'DM Mono', monospace" }}>{label}</div>
      {children}
    </div>
  );
}

// Design Ref: position-tracking §5.1 — KindSegment (매수/매도 토글, D-07: 단순 button 2개)
function KindSegment({ value, onChange }) {
  const buttonStyle = (active, kind) => ({
    flex: 1, padding: "9px 14px", border: 0,
    background: active ? (kind === "BUY" ? "#f04452" : "#3182f6") : "var(--bg-input)",
    color: active ? "#fff" : "var(--text-sub)",
    fontSize: 13, fontWeight: 600, cursor: "pointer", transition: "background 0.15s",
    fontFamily: "inherit",
  });
  return (
    <Field label="구분 *">
      <div style={{ display: "flex", gap: 0, border: "1px solid var(--border)", borderRadius: 8, overflow: "hidden" }}>
        <button type="button" style={buttonStyle(value === "BUY", "BUY")}   onClick={() => onChange("BUY")}>매수</button>
        <button type="button" style={buttonStyle(value === "SELL", "SELL")} onClick={() => onChange("SELL")}>매도</button>
      </div>
    </Field>
  );
}

// Design Ref: position-tracking Addendum (2nd) — 종목 그룹 카드 (접고/펼치기)
function PositionGroupCard({ pos, expanded, onToggle, currency, onAddBuy, onAddSell, children }) {
  const isActive = pos.isActive;
  const fmt = currency === "USD" ? (n) => `$${Math.abs(n).toFixed(2)}` : (n) => `${Math.abs(Math.round(n)).toLocaleString()}원`;
  const profitColor = pos.realizedPnl > 0 ? "#f04452" : pos.realizedPnl < 0 ? "#3182f6" : "#a0aec0";
  const sign = pos.realizedPnl > 0 ? "+" : pos.realizedPnl < 0 ? "-" : "";
  const headerBg = isActive ? "var(--bg-card)" : "rgba(45,55,72,0.4)";
  return (
    <div style={{ border: "1px solid var(--border-light)", borderRadius: 12, marginBottom: 10, overflow: "hidden", opacity: isActive ? 1 : 0.92 }}>
      <div style={{ padding: "12px 18px", display: "flex", justifyContent: "space-between", alignItems: "center", background: headerBg, borderBottom: expanded ? "1px solid var(--border-light)" : "none", gap: 12, flexWrap: "wrap" }}>
        <div onClick={onToggle} style={{ cursor: "pointer", display: "flex", alignItems: "center", gap: 12, flex: 1, minWidth: 0 }}>
          <span style={{ fontSize: 11, color: "var(--text-sub)", width: 12, display: "inline-block" }}>{expanded ? "▼" : "▶"}</span>
          <div>
            <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
              <span style={{ fontWeight: 700, fontSize: 14, color: "var(--text)" }}>{pos.name}</span>
              {isActive ? <Tag>보유 중</Tag> : <span style={{ background: "rgba(160,174,192,0.12)", color: "#a0aec0", padding: "2px 8px", borderRadius: 6, fontSize: 10, fontWeight: 600 }}>완료</span>}
            </div>
            <div style={{ fontSize: 11, color: "var(--text-sub)", marginTop: 2 }}>{pos.broker || "—"}</div>
          </div>
        </div>
        <div style={{ display: "flex", gap: 18, alignItems: "center", fontSize: 12, fontFamily: "'DM Mono', monospace", flexWrap: "wrap" }}>
          <div><span style={{ color: "var(--text-sub)" }}>보유 </span><strong style={{ color: pos.qty > 0 ? "var(--text)" : pos.qty < 0 ? "#fb7185" : "#a0aec0" }}>{pos.qty}주</strong></div>
          <div><span style={{ color: "var(--text-sub)" }}>평단 </span><strong>{pos.avgCost > 0 ? fmt(pos.avgCost) : "—"}</strong></div>
          <div><span style={{ color: "var(--text-sub)" }}>실현 </span><strong style={{ color: profitColor }}>{sign}{fmt(pos.realizedPnl)}</strong></div>
        </div>
        <div style={{ display: "flex", gap: 6 }}>
          <button onClick={onAddBuy} style={{ background: "none", border: "1px solid rgba(34,197,94,0.3)", borderRadius: 6, color: "#22c55e", padding: "4px 10px", fontSize: 11, fontFamily: "inherit" }}>+매수</button>
          <button onClick={onAddSell} style={{ background: "none", border: "1px solid rgba(239,68,68,0.3)", borderRadius: 6, color: "#ef4444", padding: "4px 10px", fontSize: 11, fontFamily: "inherit" }}>+매도</button>
        </div>
      </div>
      {expanded && children}
    </div>
  );
}

// Design Ref: position-tracking §5.1 (Addendum) — 매도 결과 모달 (사용자 피드백 2026-04-29)
function SellResultModal({ result, onClose }) {
  if (!result) return null;
  const { name, broker, sellPrice, qty, avg, realized, realizedPercent, currency } = result;
  const isProfit = realized > 0;
  const isLoss = realized < 0;
  const profitColor = isProfit ? "#f04452" : isLoss ? "#3182f6" : "#a0aec0";
  const fmt = currency === "USD" ? (n) => `$${Math.abs(n).toFixed(2)}` : (n) => `${Math.abs(Math.round(n)).toLocaleString()}원`;
  const sign = isProfit ? "+" : isLoss ? "-" : "";
  return (
    <div className="ta-modal-overlay" onClick={onClose}>
      <div className="ta-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 420 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
          <div style={{ fontSize: 18, fontWeight: 700, color: "var(--text)" }}>매도 결과</div>
          <button onClick={onClose} style={{ background: "none", border: "none", color: "var(--text-sub)", cursor: "pointer", fontSize: 22, lineHeight: 1 }}>×</button>
        </div>
        <div>
          <div style={{ marginBottom: 18 }}>
            <div style={{ fontSize: 11, color: "var(--text-sub)", marginBottom: 2 }}>{broker || "—"}</div>
            <div style={{ fontSize: 18, fontWeight: 600, color: "var(--text)" }}>{name}</div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 18 }}>
            <div><div style={{ fontSize: 11, color: "var(--text-sub)", marginBottom: 3 }}>평단</div><div style={{ fontSize: 14, fontFamily: "'DM Mono', monospace", color: "var(--text)" }}>{fmt(avg)}</div></div>
            <div><div style={{ fontSize: 11, color: "var(--text-sub)", marginBottom: 3 }}>매도가</div><div style={{ fontSize: 14, fontFamily: "'DM Mono', monospace", color: "var(--text)" }}>{fmt(sellPrice)}</div></div>
            <div><div style={{ fontSize: 11, color: "var(--text-sub)", marginBottom: 3 }}>수량</div><div style={{ fontSize: 14, fontFamily: "'DM Mono', monospace", color: "var(--text)" }}>{qty}주</div></div>
            <div><div style={{ fontSize: 11, color: "var(--text-sub)", marginBottom: 3 }}>수익률</div><div style={{ fontSize: 14, fontFamily: "'DM Mono', monospace", color: profitColor }}>{realizedPercent != null ? `${isProfit ? "+" : ""}${realizedPercent.toFixed(2)}%` : "—"}</div></div>
          </div>
          <div style={{ padding: 16, background: isProfit ? "rgba(240,68,82,0.08)" : isLoss ? "rgba(49,130,246,0.08)" : "var(--bg-input)", border: `1px solid ${isProfit ? "rgba(240,68,82,0.35)" : isLoss ? "rgba(49,130,246,0.35)" : "var(--border)"}`, borderRadius: 8, textAlign: "center" }}>
            <div style={{ fontSize: 11, color: "var(--text-sub)", marginBottom: 4, letterSpacing: "0.06em", textTransform: "uppercase" }}>실현 손익</div>
            <div style={{ fontSize: 26, fontWeight: 700, color: profitColor, fontFamily: "'DM Mono', monospace" }}>{sign}{fmt(realized)}</div>
          </div>
        </div>
        <div style={{ display: "flex", gap: 10, marginTop: 22, justifyContent: "flex-end" }}>
          <button className="ta-btn-primary" onClick={onClose}>확인</button>
        </div>
      </div>
    </div>
  );
}

// Design Ref: §4.1 — BrokerField shared component (DRY across 3 modals: KR/US/배당)
// 즐겨찾기(★) 범용 picker — 증권사/종목명 등에 재사용. ★ 토글 시 "자주 쓰는" 섹션 노출.
function FavPicker({ label, value, onChange, options = [], favs = [], onToggleFav, placeholder }) {
  const IS = { width: "100%", padding: "9px 12px", background: "var(--bg-input)", border: "1px solid var(--border)", borderRadius: 8, color: "var(--text)", fontSize: 14, outline: "none" };
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const q = (value || "").trim().toLowerCase();
  const favSet = new Set(favs);
  const matches = (b) => !q || b.toLowerCase().includes(q);
  const favShown = options.filter(b => favSet.has(b) && matches(b));
  const allShown = options.filter(matches);

  const pick = (b) => { onChange(b); setOpen(false); };
  const toggleStar = (b, e) => { e.stopPropagation(); e.preventDefault(); onToggleFav && onToggleFav(b); };

  const hdr = { padding: "6px 12px", fontSize: 10, color: "var(--text-sub)", fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase", position: "sticky", top: 0, background: "var(--bg-card)" };

  const Row = (b) => (
    <div
      key={b}
      onMouseDown={(e) => { e.preventDefault(); pick(b); }}
      onMouseEnter={(e) => { e.currentTarget.style.background = "var(--bg-input)"; }}
      onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
      style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 12px", cursor: "pointer", fontSize: 13, color: "var(--text)", background: "transparent" }}
    >
      <span>{b}</span>
      {onToggleFav && (
        <button
          type="button"
          title={favSet.has(b) ? "즐겨찾기 해제" : "즐겨찾기 추가"}
          onMouseDown={(e) => toggleStar(b, e)}
          style={{ background: "none", border: "none", cursor: "pointer", fontSize: 16, color: favSet.has(b) ? "#f0c040" : "#4a5568", padding: "0 2px", lineHeight: 1 }}
        >
          {favSet.has(b) ? "★" : "☆"}
        </button>
      )}
    </div>
  );

  return (
    <Field label={label}>
      <div ref={wrapRef} style={{ position: "relative" }}>
        <input
          type="text"
          placeholder={placeholder || "선택 또는 직접 입력"}
          value={value || ""}
          onChange={e => onChange(e.target.value)}
          onFocus={() => setOpen(true)}
          onClick={() => setOpen(true)}
          style={IS}
        />
        {open && allShown.length > 0 && (
          <div style={{ position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0, zIndex: 50, maxHeight: 260, overflowY: "auto", background: "var(--bg-card)", border: "1px solid var(--border)", borderRadius: 8, boxShadow: "0 8px 24px rgba(0,0,0,0.4)" }}>
            {favShown.length > 0 && (
              <>
                <div style={hdr}><Ico n="star" />자주 쓰는</div>
                {favShown.map(Row)}
                <div style={{ height: 1, background: "var(--divider-row)" }} />
              </>
            )}
            <div style={hdr}>전체</div>
            {allShown.map(Row)}
          </div>
        )}
      </div>
    </Field>
  );
}

// 증권사 즐겨찾기 — FavPicker wrapper
function BrokerField({ value, onChange, options, favBrokers = [], onToggleFav }) {
  return <FavPicker label="증권사" value={value} onChange={onChange} options={options} favs={favBrokers} onToggleFav={onToggleFav} placeholder="선택 또는 직접 입력 (선택)" />;
}

const IS = { width: "100%", background: "var(--bg-input)", border: "1px solid var(--border)", borderRadius: "var(--radius-sm)", color: "var(--text)", padding: "11px 14px", fontSize: 13, outline: "none", boxSizing: "border-box", fontFamily: "inherit", transition: "border-color 0.15s" };

// ── DB 동기화 (서버에 저장) — 2026-10-14 저장 결과 확인 ──
// 예전: 응답을 보지 않고 오류도 버려서, 서버가 거부(409)하거나 연결이 끊겨도 화면엔 저장된 것처럼 보였음
//       (서버는 한 번에 5건 이상 줄거나 0건이 되는 저장을 '오래된 데이터 덮어쓰기' 로 보고 막음 — 2026-06-04 chch 사고 대비)
// 지금: 종류(kr/us/dividends/watchlist)별로 한 번에 하나씩 보내고, 보내는 사이 또 바뀌면 마지막 것만 보냄.
//       결과를 SYNC 상태에 남기고 AppMain 의 SyncBanner 가 '보류됨/실패' 를 보여 줌 (그대로 저장 · 서버 것 불러오기 · 다시 시도)
const SYNC = { state: {}, listeners: new Set(), pending: {}, running: {} };
const emitSync = () => SYNC.listeners.forEach(f => f({ ...SYNC.state }));
async function postSave({ userId, type, data, confirmDangerous }) {
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), 20000);
  try {
    const res = await fetch(`${BASE_API}/user/save`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ userId, type, data, ...(confirmDangerous ? { confirmDangerous: true } : {}) }),
      signal: ctrl.signal,
    });
    const j = await res.json().catch(() => ({}));
    if (res.ok) return { status: "ok" };
    if (res.status === 409) {
      const m = String(j.error || "").match(/erase (\d+)/);
      return { status: "conflict", userId, data, prevCount: m ? +m[1] : null, newCount: Array.isArray(data) ? data.length : null };
    }
    return { status: "error", userId, data, message: `서버 오류 (${res.status})` };
  } catch (e) {
    return { status: "error", userId, data, message: e.name === "AbortError" ? "서버 응답이 없습니다" : "서버에 연결할 수 없습니다" };
  } finally { clearTimeout(timer); }
}
function syncToServer(userId, type, data, opts = {}) {
  if (!userId) return Promise.resolve({ status: "ok", skipped: true });
  SYNC.pending[type] = { userId, type, data, confirmDangerous: !!opts.confirmDangerous };
  if (SYNC.running[type]) return SYNC.running[type]; // 보내는 중이면 끝난 뒤 최신 것으로 한 번 더
  SYNC.running[type] = (async () => {
    let r = { status: "ok" };
    while (SYNC.pending[type]) {
      const job = SYNC.pending[type]; SYNC.pending[type] = null;
      r = await postSave(job);
      const before = SYNC.state[type]?.status;
      if (r.status === "ok") { if (before) { delete SYNC.state[type]; emitSync(); } }
      else { SYNC.state[type] = r; emitSync(); }
    }
    SYNC.running[type] = null;
    return r;
  })();
  return SYNC.running[type];
}
// 연결이 돌아오면 실패한 저장을 자동으로 다시 보냄 (보류된 409 는 사용자가 정해야 해서 제외)
if (typeof window !== "undefined") {
  window.addEventListener("online", () => {
    for (const [type, st] of Object.entries(SYNC.state)) if (st.status === "error") syncToServer(st.userId, type, st.data);
  });
}

const SYNC_LABEL = { kr: "국내 매매일지", us: "해외 매매일지", dividends: "배당금 기록", watchlist: "관심종목" };
// 받침에 따라 을/를
const withObj = (w) => { const c = w.charCodeAt(w.length - 1) - 0xac00; return w + (c >= 0 && c <= 11171 && c % 28 ? "을" : "를"); };
function SyncBanner({ userId, onReload }) {
  const [st, setSt] = useState({});
  const [busy, setBusy] = useState(null);
  useEffect(() => { SYNC.listeners.add(setSt); setSt({ ...SYNC.state }); return () => { SYNC.listeners.delete(setSt); }; }, []);
  const items = Object.entries(st).filter(([, v]) => v && v.userId === userId && (v.status === "conflict" || v.status === "error"));
  if (!items.length) return null;
  const btn = (bg, fg) => ({ padding: "6px 12px", borderRadius: 8, border: "none", background: bg, color: fg, fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", whiteSpace: "nowrap" });
  const run = async (key, fn) => { setBusy(key); try { await fn(); } finally { setBusy(null); } };
  return (
    <div style={{ position: "fixed", left: "50%", bottom: 20, transform: "translateX(-50%)", zIndex: 9998, width: "min(640px, calc(100vw - 32px))", display: "flex", flexDirection: "column", gap: 8 }}>
      {items.map(([type, v]) => {
        const label = SYNC_LABEL[type] || type;
        const isConflict = v.status === "conflict";
        return (
          <div key={type} role="alert" style={{ background: "var(--bg-card)", border: `1px solid ${isConflict ? "rgba(251,191,36,0.5)" : "rgba(251,113,133,0.5)"}`, borderRadius: 12, padding: "12px 14px", boxShadow: "0 8px 32px rgba(0,0,0,0.45)", fontSize: 13, lineHeight: 1.6, color: "var(--text)" }}>
            <div style={{ fontWeight: 700, color: isConflict ? "#fbbf24" : "#fb7185", marginBottom: 2 }}>
              {isConflict ? `${label} 저장이 보류됐습니다` : `${withObj(label)} 서버에 저장하지 못했습니다`}
            </div>
            <div style={{ color: "var(--text-sub)", fontSize: 12.5 }}>
              {isConflict
                ? <>서버에는 {v.prevCount != null ? `${v.prevCount}건` : "기존 기록"}이 있는데 {v.newCount != null ? `${v.newCount}건` : "더 적은 수"}으로 크게 줄어드는 저장이라, 오래된 화면이 최신 기록을 덮어쓰는 사고를 막으려고 멈췄습니다. 직접 지운 게 맞으면 [그대로 저장], 아니면 [서버 기록 불러오기]를 눌러 주세요. 서버에는 저장 직전 기록이 따로 보관돼 있습니다.</>
                : <>{v.message}. 이 기기에는 저장돼 있어서 화면은 그대로입니다. 연결이 돌아오면 자동으로 다시 보냅니다.</>}
            </div>
            <div style={{ display: "flex", gap: 6, marginTop: 8, flexWrap: "wrap" }}>
              {isConflict ? <>
                <button disabled={!!busy} onClick={() => run(type + "save", () => syncToServer(v.userId, type, v.data, { confirmDangerous: true }))} style={btn("#fbbf24", "#0d1117")}>{busy === type + "save" ? "저장 중…" : "그대로 저장"}</button>
                <button disabled={!!busy} onClick={() => run(type + "load", async () => { if (await onReload(type)) { delete SYNC.state[type]; emitSync(); } })} style={btn("rgba(255,255,255,0.08)", "var(--text)")}>{busy === type + "load" ? "불러오는 중…" : "서버 기록 불러오기"}</button>
              </> : (
                <button disabled={!!busy} onClick={() => run(type + "retry", () => syncToServer(v.userId, type, v.data))} style={btn("#fb7185", "#0d1117")}>{busy === type + "retry" ? "보내는 중…" : "다시 시도"}</button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function AppLogin({ onLogin }) {
  return <LoginScreen onLogin={onLogin} />;
}

function AppMain({ user, onLogout, theme, toggleTheme }) {
  const [page, _setPage] = useState(() => loadLS("sjv3-page", "home"));
  const [market, _setMarket] = useState(() => loadLS("sjv3-market", "KR"));
  const [krTrades, setKrTrades] = useState(() => loadLS(SK, []));
  const [usTrades, setUsTrades] = useState(() => loadLS(USK, []));
  const [dividends, setDividends] = useState(() => loadLS(DK, []));
  const [watchlist, setWatchlist] = useState(() => loadLS("sjv3-watchlist", []));
  const [tab, _setTab] = useState(() => loadLS("sjv3-tab", "journal"));
  const setPage = (v) => { _setPage(v); saveLS("sjv3-page", v); };
  const [policyFocus, setPolicyFocus] = useState(null); // 정책·이벤트 상단 바로가기에서 고른 이벤트
  const [policyActive, setPolicyActive] = useState(null); // 정책·이벤트에서 지금 보고 있는 이벤트 (바로가기 강조용)
  const setMarket = (v) => { _setMarket(v); saveLS("sjv3-market", v); };
  const setTab = (v) => { _setTab(v); saveLS("sjv3-tab", v); };
  const [breakingCount, setBreakingCount] = useState(0);
  const [researchCode, setResearchCode] = useState(null); // 증권사 리포트 → 주식 검색 이동 시 종목코드
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);

  const requireLogin = (action) => {
    if (user) { action(); } else { setShowLoginPrompt(true); }
  };
  // 둘러보기 상태에서 저장돼 있던 매매일지·배당금 화면으로 열리면 홈으로
  useEffect(() => {
    if (!user && (page === "journal" || page === "dividend")) setPage("home");
  }, [user, page]); // eslint-disable-line
  const [showTrade, setShowTrade] = useState(false);
  const [showDiv, setShowDiv] = useState(false);
  const [krForm, setKrForm] = useState(EMPTY_KR);
  const [usForm, setUsForm] = useState(EMPTY_US);
  const [divForm, setDivForm] = useState(EMPTY_DIV);
  // Design Ref: dividend-tax-tracking §3.3 — 수동 override 보존 dirty flag (tax/afterTax/perShare/rate)
  const [divFormDirty, setDivFormDirty] = useState({ tax: false, afterTax: false, perShare: false, rate: false });
  const [editId, setEditId] = useState(null);
  // Design Ref: position-tracking §5.1 (Addendum) — 매도 결과 모달 payload
  const [sellResult, setSellResult] = useState(null);
  const [filterMonth, setFilterMonth] = useState(today().slice(0, 7));
  const [divFilter, setDivFilter] = useState("");
  // sum-exclude: 체크된 종목명 Set (세션 내만 유지)
  // 국내/해외 통합 — 거래 데이터는 name 기반 식별
  const [excludedNames, setExcludedNames] = useState(() => new Set());
  const toggleExclude = useCallback((name) => {
    if (!name) return;
    setExcludedNames(prev => {
      const next = new Set(prev);
      next.has(name) ? next.delete(name) : next.add(name);
      return next;
    });
  }, []);
  const clearExcludes = useCallback(() => setExcludedNames(new Set()), []);
  const [toast, setToast] = useState(null);
  const [sortKey, setSortKey] = useState("buyDate");
  const [sortDir, setSortDir] = useState("desc");
  const [importing, setImporting] = useState(false);
  const [exchangeRate, setExchangeRate] = useState(null);
  const [zoom, setZoom] = useState(() => parseFloat(localStorage.getItem("sjv3-zoom")) || 1);
  const [marketRaw, setMarketRaw] = useState(null);
  const [marketUpdate, setMarketUpdate] = useState(null);
  const handleMarketData = useCallback((data, ts) => { setMarketRaw(data); setMarketUpdate(ts); }, []);
  const fileInputRef = useRef(null);

  useEffect(() => {
    const fetchRate = async () => {
      try {
        const res = await fetch(`${BASE_API}/exchange-rate`);
        const data = await res.json();
        if (data && data.rate) setExchangeRate(data.rate);
      } catch {}
    };
    fetchRate();
    const timer = setInterval(fetchRate, 60 * 1000);
    return () => clearInterval(timer);
  }, []);

  const showToast = (msg, ok = true) => {
    setToast({ msg, ok });
    setTimeout(() => setToast(null), 3500);
  };

  const userId = user?.id;
  const saveKrTrades = useCallback((t) => { setKrTrades(t); saveLS(SK, t); syncToServer(userId, 'kr', t); }, [userId]);
  const saveUsTrades = useCallback((t) => { setUsTrades(t); saveLS(USK, t); syncToServer(userId, 'us', t); }, [userId]);
  const saveDivs = useCallback((d) => { setDividends(d); saveLS(DK, d); syncToServer(userId, 'dividends', d); }, [userId]);
  const saveWatchlist = useCallback((w) => { setWatchlist(w); saveLS("sjv3-watchlist", w); syncToServer(userId, 'watchlist', w); }, [userId]);

  // 증권사 즐겨찾기(★) — watchlist JSONB 안 메타 항목(_type:'favBrokers')으로 저장 → 계정 동기화
  // (localStorage 만 쓰면 다른 기기/재로그인 시 사라짐. watchlist 는 이미 서버 sync + alerts 와 같은 패턴)
  const favBrokers = useMemo(() => {
    const meta = (watchlist || []).find(w => w && w._type === 'favBrokers');
    return Array.isArray(meta?.list) ? meta.list : [];
  }, [watchlist]);
  const toggleFavBroker = useCallback((name) => {
    const meta = (watchlist || []).find(w => w && w._type === 'favBrokers');
    const cur = Array.isArray(meta?.list) ? meta.list : [];
    const nextList = cur.includes(name) ? cur.filter(b => b !== name) : [...cur, name];
    const rest = (watchlist || []).filter(w => !(w && w._type === 'favBrokers'));
    saveWatchlist([...rest, { _type: 'favBrokers', list: nextList }]);
  }, [watchlist, saveWatchlist]);

  // 배당 종목명 즐겨찾기(★) — watchlist 메타 _type:'favDivNames' (증권사와 동일 패턴)
  const favDivNames = useMemo(() => {
    const meta = (watchlist || []).find(w => w && w._type === 'favDivNames');
    return Array.isArray(meta?.list) ? meta.list : [];
  }, [watchlist]);
  const toggleFavDivName = useCallback((name) => {
    if (!name) return;
    const meta = (watchlist || []).find(w => w && w._type === 'favDivNames');
    const cur = Array.isArray(meta?.list) ? meta.list : [];
    const nextList = cur.includes(name) ? cur.filter(b => b !== name) : [...cur, name];
    const rest = (watchlist || []).filter(w => !(w && w._type === 'favDivNames'));
    saveWatchlist([...rest, { _type: 'favDivNames', list: nextList }]);
  }, [watchlist, saveWatchlist]);
  // 금융소득 종합과세 입력값(연도별 이자소득·다른소득 과세표준) — watchlist 메타 _type:'finTax' (계정 동기화)
  const finTaxSettings = useMemo(() => {
    const meta = (watchlist || []).find(w => w && w._type === 'finTax');
    return meta && meta.years && typeof meta.years === 'object' ? meta.years : {};
  }, [watchlist]);
  const saveFinTax = useCallback((year, patch) => {
    const meta = (watchlist || []).find(w => w && w._type === 'finTax');
    const years = { ...(meta?.years || {}) };
    years[year] = { ...(years[year] || {}), ...patch };
    const rest = (watchlist || []).filter(w => !(w && w._type === 'finTax'));
    saveWatchlist([...rest, { _type: 'finTax', years }]);
  }, [watchlist, saveWatchlist]);
  // 배당 보유종목 — watchlist 메타 _type:'divHoldings' (계정 동기화). 매매일지와 별도로 배당주만 관리
  const divHoldings = useMemo(() => {
    const meta = (watchlist || []).find(w => w && w._type === 'divHoldings');
    return Array.isArray(meta?.items) ? meta.items : [];
  }, [watchlist]);
  const saveDivHoldings = useCallback((items) => {
    const rest = (watchlist || []).filter(w => !(w && w._type === 'divHoldings'));
    saveWatchlist([...rest, { _type: 'divHoldings', items }]);
  }, [watchlist, saveWatchlist]);
  // 배당 자동 기록 설정 — watchlist 메타 _type:'divAuto' (기본 켜짐)
  const divAutoEnabled = useMemo(() => {
    const meta = (watchlist || []).find(w => w && w._type === 'divAuto');
    return meta ? meta.enabled !== false : true;
  }, [watchlist]);
  const setDivAutoEnabled = useCallback((enabled) => {
    const rest = (watchlist || []).filter(w => !(w && w._type === 'divAuto'));
    saveWatchlist([...rest, { _type: 'divAuto', enabled }]);
  }, [watchlist, saveWatchlist]);
  // 종목별 1주당 세금 이력 (최신순) — 월배당 부분과세 종목처럼 달마다 달라지는 값을 빠르게 고르기 위함
  const divTaxHistory = useMemo(() => {
    const norm = (s) => String(s || "").replace(/\s+/g, "").toLowerCase();
    const m = {};
    dividends.forEach(d => {
      const sh = parseFloat(d.shares);
      if (d.taxExempt || !(sh > 0) || !d.name) return;
      const hasTps = d.taxPerShare !== "" && d.taxPerShare !== undefined && d.taxPerShare !== null && Number.isFinite(parseFloat(d.taxPerShare));
      const tax = parseFloat(d.tax);
      const tps = hasTps ? parseFloat(d.taxPerShare) : (Number.isFinite(tax) ? tax / sh : null);
      if (tps === null) return;
      const k = norm(d.name);
      (m[k] = m[k] || []).push({ date: d.date || "", tps: +tps.toFixed(d.currency === "USD" ? 4 : 2), id: d.id });
    });
    Object.values(m).forEach(arr => arr.sort((a, b) => b.date.localeCompare(a.date)));
    return m;
  }, [dividends]);
  // 종목명 후보: 즐겨찾기 + 배당 보유종목 + 기존 배당에 쓰인 종목명 (중복 제거)
  const divNameOptions = useMemo(() => {
    const used = dividends.map(d => d.name).filter(Boolean);
    return [...new Set([...favDivNames, ...divHoldings.map(h => h.name), ...used])];
  }, [dividends, favDivNames, divHoldings]);
  // 기존 localStorage 즐겨찾기(sjv3-fav-brokers) → watchlist 메타로 1회 이전
  const favMigratedRef = useRef(false);
  useEffect(() => {
    if (favMigratedRef.current) return;
    favMigratedRef.current = true;
    const old = loadLS("sjv3-fav-brokers", []);
    if (Array.isArray(old) && old.length > 0) {
      const hasMeta = (watchlist || []).some(w => w && w._type === 'favBrokers');
      if (!hasMeta) {
        const rest = (watchlist || []).filter(w => !(w && w._type === 'favBrokers'));
        saveWatchlist([...rest, { _type: 'favBrokers', list: old }]);
      }
      localStorage.removeItem("sjv3-fav-brokers");
    }
  }, [watchlist, saveWatchlist]);

  // ⚠️ 데이터 손실 방지 (2026-06-04 chch 40→21 사고) — 마운트 시 서버에서 최신 데이터 재로드.
  // 페이지 새로고침/다른 기기에서 오래된 localStorage 가 서버 최신본을 덮어쓰는 사고를 막는다.
  // 서버 동기화 완료 전까지 backfill(자동 저장)을 보류해 stale 덮어쓰기를 차단.
  const serverSyncedRef = useRef(false);
  const [serverSynced, setServerSynced] = useState(false);
  useEffect(() => {
    if (serverSyncedRef.current) return;
    if (!userId) { serverSyncedRef.current = true; setServerSynced(true); return; }  // 게스트: 로컬만
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${BASE_API}/user/load?userId=${userId}`);
        if (res.ok && !cancelled) {
          const data = await res.json();
          // 서버를 진실의 원천으로 — 응답 배열이 있으면 state+localStorage 갱신
          if (Array.isArray(data.krTrades)) { setKrTrades(data.krTrades); saveLS(SK, data.krTrades); }
          if (Array.isArray(data.usTrades)) { setUsTrades(data.usTrades); saveLS(USK, data.usTrades); }
          if (Array.isArray(data.dividends)) { setDividends(data.dividends); saveLS(DK, data.dividends); }
          if (Array.isArray(data.watchlist)) { setWatchlist(data.watchlist); saveLS("sjv3-watchlist", data.watchlist); }
        }
      } catch {}
      if (!cancelled) { serverSyncedRef.current = true; setServerSynced(true); }
    })();
    return () => { cancelled = true; };
  }, [userId]);

  // 저장이 보류(409)됐을 때 '서버 기록 불러오기' — 그 종류만 서버 것으로 바꿈
  const reloadFromServer = useCallback(async (type) => {
    if (!userId) return false;
    try {
      const res = await fetch(`${BASE_API}/user/load?userId=${encodeURIComponent(userId)}`);
      if (!res.ok) { showToast("서버 기록을 불러오지 못했습니다", false); return false; }
      const d = await res.json();
      const map = { kr: [d.krTrades, setKrTrades, SK], us: [d.usTrades, setUsTrades, USK], dividends: [d.dividends, setDividends, DK], watchlist: [d.watchlist, setWatchlist, "sjv3-watchlist"] };
      const [arr, set, key] = map[type] || [];
      if (!Array.isArray(arr)) return false;
      set(arr); saveLS(key, arr);
      showToast(`${withObj(SYNC_LABEL[type] || type)} 서버 기록(${arr.length}건)으로 되돌렸습니다`);
      return true;
    } catch { showToast("서버에 연결할 수 없습니다", false); return false; }
  }, [userId]); // eslint-disable-line

  // 배당 자동 기록 — 앱을 열 때(서버 최신 데이터 로드 후) 1회 + "지금 확인" 버튼.
  // 서버는 공개 분배 정보만 주고, 저장은 이 사용자 세션의 saveDivs 로만 (서버가 사용자 기록을 직접 쓰지 않음)
  const dividendsRef = useRef(dividends);
  dividendsRef.current = dividends;
  const divAutoRanRef = useRef(false);
  const [divAutoTick, setDivAutoTick] = useState(0);
  const [divAutoStatus, setDivAutoStatus] = useState(null); // { at, running, added, updated, log, error }
  useEffect(() => {
    if (!serverSynced || !userId || !divAutoEnabled) return;
    if (divAutoTick === 0 && divAutoRanRef.current) return;
    if (!divHoldings.length) return;
    divAutoRanRef.current = true;
    let cancelled = false;
    (async () => {
      setDivAutoStatus(s => ({ ...(s || {}), running: true }));
      try {
        const plan = await planAutoDividends({
          holdings: divHoldings, dividends: dividendsRef.current, today: kstToday(),
          fetch: {
            usDist: (t) => fetchUsDist(t),
            krEtf: (code, name) => fetchEtfTaxBase(code, name),
            rate: (d) => fetchHistoricalRate(d),
            krCode: (n) => resolveKrCode(n),
          },
        });
        if (cancelled) return;
        if (plan.add.length || plan.update.length) {
          saveDivs(applyAutoPlan(dividendsRef.current, plan));
          const first = plan.add[0];
          const fmt = (d) => `${d.name} ${d.date.slice(5)} ${d.currency === "USD" ? "$" + d.amount : Number(d.amount).toLocaleString() + "원"}`;
          showToast(plan.add.length
            ? `배당 ${plan.add.length}건 자동 기록 — ${fmt(first)}${plan.add.length > 1 ? " 외" : ""}`
            : `과세표준 공시 반영 ${plan.update.length}건`);
        }
        setDivAutoStatus({ at: Date.now(), running: false, added: plan.add, updated: plan.update, log: plan.log });
      } catch (e) {
        if (!cancelled) setDivAutoStatus({ at: Date.now(), running: false, error: e.message, added: [], updated: [], log: [] });
      }
    })();
    return () => { cancelled = true; };
  }, [serverSynced, userId, divAutoEnabled, divHoldings, divAutoTick]); // eslint-disable-line

  // Design Ref: dividend-tax-tracking §1.3 / §3.2 — 기존 dividend 자동 backfill (mount 1회)
  // - tax 필드 없는 row 만 대상. USD 면 tax/afterTax 자동, KRW 면 tax=''만 추가
  // - 의심 행 (after > pre × 1.05 등) 은 skip + console.warn — 사용자가 수동 점검 필요
  // - Layer 1 안전망 호환: prev_count = new_count → severity=safe 통과
  const backfillDoneRef = useRef(false);
  useEffect(() => {
    if (backfillDoneRef.current) return;
    if (!serverSynced) return;  // 서버 최신 로드 후에만 (stale 덮어쓰기 방지)
    if (!userId) return;
    if (!Array.isArray(dividends) || dividends.length === 0) return;
    const { migrated, suspicious, migratedCount } = backfillDividends(dividends);
    if (migratedCount === 0 && suspicious.length === 0) {
      backfillDoneRef.current = true;
      return;
    }
    if (suspicious.length > 0) {
      console.warn(`[dividend backfill] ${suspicious.length}건 의심 행 skip (수동 점검 필요):`, suspicious);
    }
    if (migratedCount > 0) {
      console.log(`[dividend backfill] ${migratedCount}건 자동 마이그레이션 완료 (세금 필드 추가)`);
      saveDivs(migrated);
    }
    backfillDoneRef.current = true;
  }, [serverSynced, userId, dividends, saveDivs]);

  const downloadTemplate = async () => {
    await ensureXLSX();
    // Design Ref: position-tracking M6 — 1행 = 1이벤트 (BUY 또는 SELL). 분할매수/분할매도 자연 지원
    const krHeaders = ["구분(매수/매도)", "증권사", "거래일(YYYY-MM-DD)", "종목명", "단가(원)", "수량(주)", "매매이유", "메모"];
    const usHeaders = ["구분(매수/매도)", "증권사", "거래일(YYYY-MM-DD)", "종목명", "단가($)", "수량(주)", "매매이유", "메모"];

    // 샘플: 분할매수 + 분할매도 시나리오 (사용자가 양식만 보고 즉시 이해 가능)
    const krSample = [
      ["매수", "키움증권", "2025-10-01", "삼성전자", 50000, 100, "초기 진입", "신호: 골든크로스"],
      ["매수", "키움증권", "2025-10-15", "삼성전자", 45000, 50, "추가매수 (저점 분할매수)", "평단 낮추기"],
      ["매도", "키움증권", "2025-10-25", "삼성전자", 55000, 80, "분할 익절", "목표가 부분 도달"],
      ["매수", "유안타증권", "2025-10-17", "에코프로", 66979, 453, "실적 개선 기대", ""],
    ];
    const usSample = [
      ["매수", "키움증권", "2025-10-01", "CONY", 25.80, 467, "배당/현금흐름용", ""],
      ["매수", "Interactive Brokers", "2025-11-01", "AAPL", 180.50, 50, "장기 보유 (1차 분할매수)", ""],
      ["매도", "Interactive Brokers", "2025-12-15", "AAPL", 200.00, 25, "분할 익절", "+10%"],
    ];

    const wb = XLSX.utils.book_new();
    const ws1 = XLSX.utils.aoa_to_sheet([krHeaders, ...krSample]);
    const ws2 = XLSX.utils.aoa_to_sheet([usHeaders, ...usSample]);
    ws1["!cols"] = [12, 18, 16, 18, 14, 12, 30, 30].map(w => ({ wch: w }));
    ws2["!cols"] = [12, 22, 16, 16, 14, 12, 30, 30].map(w => ({ wch: w }));
    XLSX.utils.book_append_sheet(wb, ws1, "국내주식");
    XLSX.utils.book_append_sheet(wb, ws2, "미국주식");
    XLSX.writeFile(wb, "주식매매일지_양식.xlsx");
    showToast("양식 다운로드 완료! (분할매수/매도 지원)");
  };

  // Design Ref: position-tracking M6 (Excel 익스포트) — 신 양식 호환 익스포트
  // 각 거래 행을 legacyToEvents로 펼친 뒤 1행=1이벤트(BUY|SELL)로 출력
  // 익스포트 → 즉시 재임포트 시 round-trip 보존
  const exportJournal = async () => {
    await ensureXLSX();
    const krHeaders = ["구분(매수/매도)", "증권사", "거래일(YYYY-MM-DD)", "종목명", "단가(원)", "수량(주)", "매매이유", "메모"];
    const usHeaders = ["구분(매수/매도)", "증권사", "거래일(YYYY-MM-DD)", "종목명", "단가($)", "수량(주)", "매매이유", "메모"];

    const buildRows = (trades) => trades.flatMap(t => {
      const events = legacyToEvents(t);
      return events.map(ev => [
        ev.kind === "BUY" ? "매수" : "매도",
        t.broker || "",
        ev.date || "",
        t.name || "",
        ev.price || 0,
        ev.qty || 0,
        t.reason || "",
        t.memo || "",
      ]);
    }).sort((a, b) => (b[2] || "").localeCompare(a[2] || ""));  // 거래일 내림차순

    const krRows = buildRows(krTrades);
    const usRows = buildRows(usTrades);

    if (krRows.length === 0 && usRows.length === 0) {
      showToast("내보낼 거래가 없습니다", false);
      return;
    }

    const wb = XLSX.utils.book_new();
    const ws1 = XLSX.utils.aoa_to_sheet([krHeaders, ...krRows]);
    const ws2 = XLSX.utils.aoa_to_sheet([usHeaders, ...usRows]);
    ws1["!cols"] = [12, 18, 16, 18, 14, 12, 30, 30].map(w => ({ wch: w }));
    ws2["!cols"] = [12, 22, 16, 16, 14, 12, 30, 30].map(w => ({ wch: w }));
    XLSX.utils.book_append_sheet(wb, ws1, "국내주식");
    XLSX.utils.book_append_sheet(wb, ws2, "미국주식");

    const dateStr = today();
    XLSX.writeFile(wb, `주식매매일지_${dateStr}.xlsx`);
    showToast(`내보내기 완료! KR ${krRows.length}건, US ${usRows.length}건`);
  };

  const handleImport = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImporting(true);
    await ensureXLSX();  // xlsx lazy load — 임포트 시점에 ~430KB 동적 로드
    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const wb = XLSX.read(evt.target.result, { type: "array" });
        // 양식 파일인지 확인 (국내주식/미국주식 시트명)
        const isTemplate = wb.SheetNames.includes("국내주식") || wb.SheetNames.includes("미국주식");
        const allKr = isTemplate ? parseTemplateKr(wb) : [
          ...parseKrSheet(wb, "유안타증권"),
          ...parseKrSheet(wb, "하나금융증권"),
          ...parseIsaKr(wb),
        ];
        // Design Ref: position-tracking M6 — 이벤트 단위 중복 검사 키 (kind/name/broker/date/qty/price)
        // price 포함: 같은 날 같은 종목 분할매수(qty 동일·price 다름)가 silently drop되지 않도록
        const tradeKey = (t) => {
          const date = t.kind === 'SELL' ? (t.sellDate || '') : (t.buyDate || '');
          const price = t.kind === 'SELL'
            ? (t.sellPrice ?? t.sellPriceUsd ?? '')
            : (t.buyPrice ?? t.buyPriceUsd ?? '');
          return `${t.kind || 'LEG'}|${t.name}|${t.broker || ''}|${date}|${t.quantity}|${price}`;
        };
        const exKr = loadLS(SK, []);
        const exKrKeys = new Set(exKr.map(tradeKey));
        const newKr = allKr.filter(t => !exKrKeys.has(tradeKey(t)));
        const krSorted = [...exKr, ...newKr].sort((a, b) => {
          const ad = a.kind === 'SELL' ? (a.sellDate || '') : (a.buyDate || '');
          const bd = b.kind === 'SELL' ? (b.sellDate || '') : (b.buyDate || '');
          return bd.localeCompare(ad);
        });
        saveKrTrades(krSorted);

        const allUs = isTemplate ? parseTemplateUs(wb) : parseUsSheet(wb);
        const exUs = loadLS(USK, []);
        const exUsKeys = new Set(exUs.map(tradeKey));
        const newUs = allUs.filter(t => !exUsKeys.has(tradeKey(t)));
        const usSorted = [...exUs, ...newUs].sort((a, b) => {
          const ad = a.kind === 'SELL' ? (a.sellDate || '') : (a.buyDate || '');
          const bd = b.kind === 'SELL' ? (b.sellDate || '') : (b.buyDate || '');
          return bd.localeCompare(ad);
        });
        saveUsTrades(usSorted);

        showToast(`국내 ${newKr.length}건, 미국 ${newUs.length}건 임포트 완료!`);
      } catch (err) {
        showToast("파일 읽기 실패: " + err.message, false);
      }
      setImporting(false);
      e.target.value = "";
    };
    reader.readAsArrayBuffer(file);
  };

  const rate = exchangeRate || 1380;
  const isKr = market === "KR";
  const currentTabs = isKr ? KR_TABS : US_TABS;

  const krEnriched = useMemo(() => krTrades.map(t => {
    const bp = parseFloat(t.buyPrice) || 0;
    const sp = parseFloat(t.sellPrice) || 0;
    const qty = parseInt(t.quantity) || 0;
    const buyAmt = bp * qty;
    const isClosed = !!t.sellDate && sp > 0;
    const profit = isClosed ? (sp - bp) * qty : null;
    const profitRate = isClosed && bp > 0 ? ((sp - bp) / bp) * 100 : null;
    const holdDays = t.sellDate ? daysBetween(t.buyDate, t.sellDate) : daysBetween(t.buyDate, today());
    return { ...t, bp, sp, qty, buyAmt, isClosed, profit, profitRate, holdDays };
  }), [krTrades]);

  const usEnriched = useMemo(() => usTrades.map(t => {
    const bp = parseFloat(t.buyPriceUsd) || 0;
    const sp = parseFloat(t.sellPriceUsd) || 0;
    const qty = parseInt(t.quantity) || 0;
    const buyAmtUsd = bp * qty;
    const buyAmtKrw = buyAmtUsd * rate;
    const isClosed = !!t.sellDate && sp > 0;
    const profitUsd = isClosed ? (sp - bp) * qty : null;
    const profitKrw = profitUsd != null ? profitUsd * rate : null;
    const profitRate = isClosed && bp > 0 ? ((sp - bp) / bp) * 100 : null;
    const holdDays = t.sellDate ? daysBetween(t.buyDate, t.sellDate) : daysBetween(t.buyDate, today());
    return { ...t, bp, sp, qty, buyAmtUsd, buyAmtKrw, isClosed, profitUsd, profitKrw, profitRate, holdDays };
  }), [usTrades, rate]);

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortKey(key); setSortDir("desc"); }
  };
  const si = (key) => sortKey === key ? (sortDir === "asc" ? " ↑" : " ↓") : "";

  const sortedKr = useMemo(() => [...krEnriched].sort((a, b) => {
    let av = a[sortKey] ?? "", bv = b[sortKey] ?? "";
    if (["profit","profitRate","buyAmt","holdDays","qty"].includes(sortKey)) {
      av = av === null || av === "" ? -Infinity : av;
      bv = bv === null || bv === "" ? -Infinity : bv;
    }
    return sortDir === "asc" ? (av > bv ? 1 : -1) : (av < bv ? 1 : -1);
  }), [krEnriched, sortKey, sortDir]);

  const sortedUs = useMemo(() => [...usEnriched].sort((a, b) => {
    let av = a[sortKey] ?? "", bv = b[sortKey] ?? "";
    if (["profitUsd","profitRate","buyAmtUsd","holdDays","qty"].includes(sortKey)) {
      av = av === null || av === "" ? -Infinity : av;
      bv = bv === null || bv === "" ? -Infinity : bv;
    }
    return sortDir === "asc" ? (av > bv ? 1 : -1) : (av < bv ? 1 : -1);
  }), [usEnriched, sortKey, sortDir]);

  const krHolding = useMemo(() => krEnriched.filter(t => !t.isClosed), [krEnriched]);
  const krClosed = useMemo(() => krEnriched.filter(t => t.isClosed), [krEnriched]);
  const usHolding = useMemo(() => usEnriched.filter(t => !t.isClosed), [usEnriched]);
  const usClosed = useMemo(() => usEnriched.filter(t => t.isClosed), [usEnriched]);

  const allMonths = useMemo(() => {
    // position-tracking: SELL-only 행은 buyDate=""이므로 sellDate로 fallback
    const tradeMonth = (t) => (t.buyDate || t.sellDate || "").slice(0, 7);
    return [...new Set([
      ...krTrades.map(tradeMonth),
      ...usTrades.map(tradeMonth),
      ...dividends.map(d => (d.date || "").slice(0, 7)),
      today().slice(0, 7),
    ].filter(Boolean))].sort((a, b) => b.localeCompare(a));
  }, [krTrades, usTrades, dividends]);

  const monthKrClosed = useMemo(() => krEnriched.filter(t => t.isClosed && t.sellDate && t.sellDate.startsWith(filterMonth)), [krEnriched, filterMonth]);
  const monthUsClosed = useMemo(() => usEnriched.filter(t => t.isClosed && t.sellDate && t.sellDate.startsWith(filterMonth)), [usEnriched, filterMonth]);
  const monthDivs = useMemo(() => dividends.filter(d => d.date.startsWith(filterMonth)), [dividends, filterMonth]);

  const krStats = useMemo(() => {
    const krStockNames = [...new Set(krEnriched.map(t => t.name))];
    return krStockNames.map(name => {
      const ts = krEnriched.filter(t => t.name === name);
      const cl = ts.filter(t => t.isClosed);
      const totalProfit = cl.reduce((a, t) => a + t.profit, 0);
      const totalBuy = cl.reduce((a, t) => a + t.buyAmt, 0);
      return {
        name, count: ts.length, closedCount: cl.length, totalProfit,
        rate: totalBuy > 0 ? (totalProfit / totalBuy) * 100 : null,
        wins: cl.filter(t => t.profit > 0).length,
        divs: dividends.filter(d => d.name === name && d.currency === "KRW").reduce((a, d) => a + (parseFloat(d.afterTax) || 0), 0),
      };
    }).sort((a, b) => b.totalProfit - a.totalProfit);
  }, [krEnriched, dividends]);

  const usStats = useMemo(() => {
    const usStockNames = [...new Set(usEnriched.map(t => t.name))];
    return usStockNames.map(name => {
      const ts = usEnriched.filter(t => t.name === name);
      const cl = ts.filter(t => t.isClosed);
      const totalProfitUsd = cl.reduce((a, t) => a + t.profitUsd, 0);
      const totalBuyUsd = cl.reduce((a, t) => a + t.buyAmtUsd, 0);
      return {
        name, count: ts.length, closedCount: cl.length, totalProfitUsd,
        totalProfitKrw: totalProfitUsd * rate,
        rate: totalBuyUsd > 0 ? (totalProfitUsd / totalBuyUsd) * 100 : null,
        wins: cl.filter(t => t.profitUsd > 0).length,
      };
    }).sort((a, b) => b.totalProfitUsd - a.totalProfitUsd);
  }, [usEnriched, rate]);

  const Th = ({ k, children }) => (
    <th onClick={() => toggleSort(k)} style={{ padding: "12px 14px", textAlign: "left", fontSize: 10, color: sortKey === k ? "#f0c040" : "#4a5568", fontWeight: 600, letterSpacing: "0.1em", textTransform: "uppercase", cursor: "pointer", whiteSpace: "nowrap", userSelect: "none", borderBottom: "1px solid var(--border-light)", fontFamily: "'DM Mono', monospace" }}>
      {children}{si(k)}
    </th>
  );

  const openKrEdit = (t) => { setKrForm({...t}); setEditId(t.id); setShowTrade(true); };
  const openUsEdit = (t) => { setUsForm({...t}); setEditId(t.id); setShowTrade(true); };
  const openDivEdit = (d) => {
    // Design Ref: dividend-tax-tracking §3.3 — 편집 시 기존 값은 "수동" 으로 간주 → dirty=true
    // (단 old row 에 없는 필드는 dirty=false 로 자동 채움)
    setDivForm({ tax: "", perShare: "", rate: "", taxExempt: false, ...d });
    const has = (v) => v !== '' && v !== undefined && v !== null;
    setDivFormDirty({ tax: has(d.tax), afterTax: has(d.afterTax), perShare: has(d.perShare), rate: has(d.rate) });
    setEditId(d.id);
    setShowDiv(true);
  };
  const openDivNew = () => {
    setDivForm(EMPTY_DIV);
    setDivFormDirty({ tax: false, afterTax: false, perShare: false, rate: false });
    setEditId(null);
    setShowDiv(true);
  };

  // 수령일 기준 USD→KRW 환율 자동 조회 (USD + date 있고 수동 미입력 시). 실패 시 빈값 → 현재 환율 fallback
  useEffect(() => {
    if (!showDiv) return;
    if (divForm.currency !== 'USD') return;
    if (divFormDirty.rate) return;
    if (!divForm.date) return;
    let cancelled = false;
    fetchHistoricalRate(divForm.date).then(r => {
      if (cancelled || !r) return;
      setDivForm(prev => {
        if (prev.currency !== 'USD') return prev;
        const rr = +Number(r).toFixed(2);
        if (parseFloat(prev.rate) === rr) return prev;
        return { ...prev, rate: rr };
      });
    });
    return () => { cancelled = true; };
  }, [showDiv, divForm.currency, divForm.date, divFormDirty.rate]);

  // ETF 주당 과세표준 자동 조회 — 국내 ETF(KODEX·TIGER·ACE·RISE·SOL·PLUS) + 종목명·수령일 있으면 운용사 공시값 조회.
  // 새로 입력하는 배당이고 과세표준/1주당 세금을 직접 넣지 않았으면 자동 적용. 수정 중이면 버튼으로 적용.
  const [etfTax, setEtfTax] = useState({ status: "idle" });
  const etfAutoRef = useRef(null); // 마지막으로 자동 적용한 과세표준 값 (사용자가 바꾸면 더 이상 덮어쓰지 않음)
  const [etfTaxTick, setEtfTaxTick] = useState(0);
  useEffect(() => {
    if (!showDiv) { setEtfTax({ status: "idle" }); etfAutoRef.current = null; return; }
    const name = (divForm.name || "").trim();
    if (divForm.currency !== "KRW" || !name || !etfBrandOf(name)) { setEtfTax({ status: "idle" }); return; }
    let cancelled = false;
    const t = setTimeout(async () => {
      const norm = (s) => String(s || "").replace(/\s+/g, "").toLowerCase();
      const holding = divHoldings.find(h => norm(h.name) === norm(name));
      setEtfTax({ status: "loading" });
      const code = (holding && /^[0-9A-Z]{6}$/i.test(holding.code || "") ? holding.code.toUpperCase() : null) || await resolveKrCode(name);
      if (cancelled) return;
      if (!code) { setEtfTax({ status: "nocode" }); return; }
      try {
        const data = await fetchEtfTaxBase(code, name, divForm.date);
        if (cancelled) return;
        if (!data.supported) { setEtfTax({ status: "unsupported" }); return; }
        if (data.notFound) { setEtfTax({ status: "notfound", data, code }); return; }
        const m = data.match;
        const status = !m ? "nomatch" : (m.taxBase == null ? "pending" : "ok");
        setEtfTax({ status, data, match: m, code });
        // 날짜를 바꿔 해당 회차 값이 없으면, 앞서 자동으로 넣은 값은 지움 (다른 달 과세표준이 남지 않게)
        if (status !== "ok" && etfAutoRef.current != null) {
          const auto = etfAutoRef.current;
          etfAutoRef.current = null;
          setDivForm(prev => String(prev.taxBasePerShare) === auto ? { ...prev, taxBasePerShare: "" } : prev);
        }
        if (status === "ok" && !editId) {
          const v = String(m.taxBase);
          setDivForm(prev => {
            const tbEmpty = prev.taxBasePerShare === "" || prev.taxBasePerShare == null;
            const tbAuto = etfAutoRef.current != null && String(prev.taxBasePerShare) === etfAutoRef.current;
            const tpsEmpty = prev.taxPerShare === "" || prev.taxPerShare == null;
            if (prev.taxExempt || !tpsEmpty || !(tbEmpty || tbAuto)) return prev;
            etfAutoRef.current = v;
            return String(prev.taxBasePerShare) === v ? prev : { ...prev, taxBasePerShare: v };
          });
          setDivFormDirty(d => (d.tax || d.afterTax ? { ...d, tax: false, afterTax: false } : d));
        }
      } catch {
        if (!cancelled) setEtfTax({ status: "error" });
      }
    }, 450);
    return () => { cancelled = true; clearTimeout(t); };
  }, [showDiv, divForm.name, divForm.date, divForm.currency, editId, etfTaxTick]); // eslint-disable-line

  // 미국 고배당 ETF(YieldMax: CONY 등) — 공식 분배 표에서 수령일 근처 회차(미국 지급일 당일~다음 주 초)를 찾아 세전 = 1주당 분배금 × 주수
  const [usDist, setUsDist] = useState({ status: "idle" });
  const usAutoRef = useRef(null);
  const [usDistTick, setUsDistTick] = useState(0);
  useEffect(() => {
    if (!showDiv) { setUsDist({ status: "idle" }); usAutoRef.current = null; return; }
    const norm = (s) => String(s || "").replace(/\s+/g, "").toLowerCase();
    const name = (divForm.name || "").trim();
    const holding = divHoldings.find(h => norm(h.name) === norm(name));
    const ticker = (holding && holding.market === "US" && /^[A-Za-z]{1,6}$/.test(holding.code || "") ? holding.code : (/^[A-Za-z]{1,6}$/.test(name) ? name : "")).toUpperCase();
    if (divForm.currency !== "USD" || !ticker || !divForm.date) { setUsDist({ status: "idle" }); return; }
    let cancelled = false;
    const t = setTimeout(async () => {
      setUsDist(p => (p.ticker === ticker && p.status !== "idle" ? { ...p, status: "loading" } : { status: "loading", ticker }));
      try {
        const data = await fetchUsDist(ticker, divForm.date);
        if (cancelled) return;
        if (!data.supported) { setUsDist({ status: "idle" }); return; }
        const m = data.match;
        setUsDist({ status: m ? "ok" : "nomatch", data, match: m, ticker });
        if (!m && usAutoRef.current != null) {
          const auto = usAutoRef.current; usAutoRef.current = null;
          setDivForm(prev => String(prev.amount) === auto ? { ...prev, amount: "", afterTax: "", tax: "", perShare: "" } : prev);
        }
      } catch {
        if (!cancelled) setUsDist({ status: "error", ticker });
      }
    }, 450);
    return () => { cancelled = true; clearTimeout(t); };
  }, [showDiv, divForm.name, divForm.date, divForm.currency, usDistTick]); // eslint-disable-line
  // 매칭된 회차가 있고 새 입력이면: 세전이 비었거나 앞서 자동으로 넣은 값일 때만 1주당 분배금 × 주수 로 채움 (주수 바꾸면 다시 계산)
  useEffect(() => {
    if (!showDiv || editId || usDist.status !== "ok" || !usDist.match) return;
    const sh = parseFloat(divForm.shares);
    if (!(sh > 0)) return;
    const v = String(+(usDist.match.amount * sh).toFixed(2));
    setDivForm(prev => {
      const empty = prev.amount === "" || prev.amount == null;
      const wasAuto = usAutoRef.current != null && String(prev.amount) === usAutoRef.current;
      if (!(empty || wasAuto) || String(prev.amount) === v) { if (String(prev.amount) === v) usAutoRef.current = v; return prev; }
      usAutoRef.current = v;
      return { ...prev, amount: v };
    });
  }, [showDiv, editId, usDist, divForm.shares]); // eslint-disable-line

  // Design Ref: dividend-tax-tracking §3.3 / §5 — 파생값 자동 계산 (dirty 우선)
  // 세후(USD 자동) / 세금=세전−세후 / 주당배당금=세전÷주수. 사용자가 직접 입력한 필드는 skip.
  useEffect(() => {
    if (!showDiv) return;
    const derived = computeDividendDerived(
      { amount: divForm.amount, afterTax: divForm.afterTax, shares: divForm.shares, currency: divForm.currency, taxExempt: divForm.taxExempt, taxBasePerShare: divForm.taxBasePerShare, taxPerShare: divForm.taxPerShare },
      divFormDirty
    );
    setDivForm(prev => {
      let changed = false;
      const next = { ...prev };
      for (const k of ['afterTax', 'tax', 'perShare']) {
        if (derived[k] !== undefined && prev[k] !== derived[k]) { next[k] = derived[k]; changed = true; }
      }
      return changed ? next : prev;
    });
  }, [showDiv, divForm.amount, divForm.afterTax, divForm.shares, divForm.currency, divForm.taxExempt, divForm.taxBasePerShare, divForm.taxPerShare, divFormDirty.tax, divFormDirty.afterTax, divFormDirty.perShare]);

  // Design Ref: position-tracking §5.2.5 (Addendum) — 빠른 추가매수/분할매도 (행 액션에서 진입)
  const openKrAdd = (t, kind) => {
    setKrForm({ ...EMPTY_KR, kind, name: t.name || "", broker: t.broker || "" });
    setEditId(null);
    setShowTrade(true);
  };
  const openUsAdd = (t, kind) => {
    setUsForm({ ...EMPTY_US, kind, name: t.name || "", broker: t.broker || "" });
    setEditId(null);
    setShowTrade(true);
  };

  // Design Ref: position-tracking §5.1 — 레거시 행(kind 미존재) 편집 모드 감지
  const editingLegacyKr = !!editId && krForm.kind === undefined;
  const editingLegacyUs = !!editId && usForm.kind === undefined;

  // Design Ref: position-tracking §5.1 — 매도 입력 시 보유/평단 힌트 (Plan SC-04 부분)
  const computeCurrentPos = (trades, form) => {
    if (form.kind !== "SELL" || !form.name) return null;
    const groupKey = posKey(form.name, form.broker);
    const events = trades
      .filter(t => t.id !== editId && posKey(t.name, t.broker) === groupKey)
      .flatMap(legacyToEvents);
    let buyQty = 0, buyAmt = 0, sellQty = 0;
    events.forEach(ev => {
      if (ev.kind === "BUY") { buyQty += ev.qty; buyAmt += ev.price * ev.qty; }
      else { sellQty += ev.qty; }
    });
    return {
      remaining: buyQty - sellQty,
      avg: buyQty > 0 ? buyAmt / buyQty : 0,
      qtyRequest: parseInt(form.quantity) || 0,
    };
  };
  const krCurrentPosition = useMemo(() => computeCurrentPos(krTrades, krForm),
    [krTrades, krForm.kind, krForm.name, krForm.broker, krForm.quantity, editId]);
  const usCurrentPosition = useMemo(() => computeCurrentPos(usTrades, usForm),
    [usTrades, usForm.kind, usForm.name, usForm.broker, usForm.quantity, editId]);

  // Design Ref: position-tracking §4.2 — 종목별 집계 (그룹 뷰 데이터)
  const krPositions = useMemo(() => computePositions(krTrades), [krTrades]);
  const usPositions = useMemo(() => computePositions(usTrades), [usTrades]);

  // Design Ref: position-tracking Addendum — 그룹 펼침/접힘 상태 (기본: 활성=펼침, 완료=접힘)
  const [expandedGroups, setExpandedGroups] = useState({});
  const isGroupExpanded = (pos) => expandedGroups[pos.key] !== undefined ? expandedGroups[pos.key] : pos.isActive;
  const toggleGroup = (pos) => setExpandedGroups(prev => ({ ...prev, [pos.key]: !isGroupExpanded(pos) }));

  // Design Ref: position-tracking §3.1 — 행마다 BUY/SELL/레거시 별 정렬 키
  const tradeSortKey = (t) => t.kind === "SELL" ? (t.sellDate || "") : (t.buyDate || "");

  const saveKrTrade = () => {
    if (!krForm.name || !krForm.quantity) { showToast("종목명, 수량은 필수입니다", false); return; }
    // Plan SC-01: kind에 따른 검증 분기
    if (krForm.kind === "BUY" && !krForm.buyPrice) { showToast("매수가는 필수입니다", false); return; }
    if (krForm.kind === "SELL" && (!krForm.sellPrice || !krForm.sellDate)) { showToast("매도일과 매도가는 필수입니다", false); return; }
    if (krForm.kind === undefined && !krForm.buyPrice) { showToast("매수가는 필수입니다", false); return; }

    // BUY 행은 sell_* 비우고, SELL 행은 buy_* 비움 (스키마 일관성)
    let entry = { ...krForm, id: editId || Date.now().toString() };
    if (entry.kind === "BUY")  { entry.sellDate = ""; entry.sellPrice = ""; }
    if (entry.kind === "SELL") { entry.buyDate  = ""; entry.buyPrice  = ""; }

    // Design Ref: position-tracking §5.1 (Addendum) — 매도 결과(실현손익) 캡처
    let sellPayload = null;
    if (entry.kind === "SELL" && krCurrentPosition) {
      const avg = krCurrentPosition.avg;
      const sellP = parseFloat(entry.sellPrice) || 0;
      const q = parseInt(entry.quantity) || 0;
      const realized = (sellP - avg) * q;
      const realizedPercent = avg > 0 ? ((sellP - avg) / avg) * 100 : null;
      sellPayload = { name: entry.name, broker: entry.broker, sellPrice: sellP, qty: q, avg, realized, realizedPercent, currency: "KRW" };
    }

    const updated = editId ? krTrades.map(t => t.id === editId ? entry : t) : [...krTrades, entry];
    saveKrTrades(updated.sort((a, b) => tradeSortKey(b).localeCompare(tradeSortKey(a))));

    // Design Ref: position-tracking Addendum — 토스트에 평단/잔여/실현손익 풍부 표시
    const post = computePostSavePosition(updated, entry.name, entry.broker);
    const fmtKR = (n) => Math.round(n).toLocaleString();
    let toastMsg;
    if (entry.kind === "BUY") {
      toastMsg = editId ? `수정 완료! 보유 ${post.qty}주, 평단 ${fmtKR(post.avgCost)}원` : `매수! 평단 ${fmtKR(post.avgCost)}원 / 보유 ${post.qty}주`;
    } else if (entry.kind === "SELL" && sellPayload) {
      const sgn = sellPayload.realized > 0 ? "+" : sellPayload.realized < 0 ? "-" : "";
      toastMsg = post.qty <= 0
        ? `전량매도! 누적 실현손익 ${sgn}${fmtKR(Math.abs(sellPayload.realized))}원`
        : `매도! ${sgn}${fmtKR(Math.abs(sellPayload.realized))}원 (${sellPayload.realizedPercent != null ? (sellPayload.realized >= 0 ? "+" : "") + sellPayload.realizedPercent.toFixed(2) + "%" : "—"}) / 잔여 ${post.qty}주`;
    } else {
      toastMsg = editId ? "수정 완료!" : "거래 기록 완료!";
    }

    setShowTrade(false); setEditId(null); setKrForm(EMPTY_KR);
    showToast(toastMsg);
    if (sellPayload) setSellResult(sellPayload);
  };

  const saveUsTrade = () => {
    if (!usForm.name || !usForm.quantity) { showToast("종목명, 수량은 필수입니다", false); return; }
    if (usForm.kind === "BUY" && !usForm.buyPriceUsd) { showToast("매수가($)는 필수입니다", false); return; }
    if (usForm.kind === "SELL" && (!usForm.sellPriceUsd || !usForm.sellDate)) { showToast("매도일과 매도가($)는 필수입니다", false); return; }
    if (usForm.kind === undefined && !usForm.buyPriceUsd) { showToast("매수가($)는 필수입니다", false); return; }

    let entry = { ...usForm, id: editId || Date.now().toString() };
    if (entry.kind === "BUY")  { entry.sellDate = ""; entry.sellPriceUsd = ""; }
    if (entry.kind === "SELL") { entry.buyDate  = ""; entry.buyPriceUsd  = ""; }

    // Design Ref: position-tracking §5.1 (Addendum) — 매도 결과(실현손익) 캡처
    let sellPayload = null;
    if (entry.kind === "SELL" && usCurrentPosition) {
      const avg = usCurrentPosition.avg;
      const sellP = parseFloat(entry.sellPriceUsd) || 0;
      const q = parseInt(entry.quantity) || 0;
      const realized = (sellP - avg) * q;
      const realizedPercent = avg > 0 ? ((sellP - avg) / avg) * 100 : null;
      sellPayload = { name: entry.name, broker: entry.broker, sellPrice: sellP, qty: q, avg, realized, realizedPercent, currency: "USD" };
    }

    const updated = editId ? usTrades.map(t => t.id === editId ? entry : t) : [...usTrades, entry];
    saveUsTrades(updated.sort((a, b) => tradeSortKey(b).localeCompare(tradeSortKey(a))));

    // Design Ref: position-tracking Addendum — 토스트에 평단/잔여/실현손익 풍부 표시
    const post = computePostSavePosition(updated, entry.name, entry.broker);
    const fmtUSD = (n) => `$${Math.abs(n).toFixed(2)}`;
    let toastMsg;
    if (entry.kind === "BUY") {
      toastMsg = editId ? `수정 완료! 보유 ${post.qty}주, 평단 ${fmtUSD(post.avgCost)}` : `매수! 평단 ${fmtUSD(post.avgCost)} / 보유 ${post.qty}주`;
    } else if (entry.kind === "SELL" && sellPayload) {
      const sgn = sellPayload.realized > 0 ? "+" : sellPayload.realized < 0 ? "-" : "";
      toastMsg = post.qty <= 0
        ? `전량매도! 누적 실현손익 ${sgn}${fmtUSD(Math.abs(sellPayload.realized))}`
        : `매도! ${sgn}${fmtUSD(Math.abs(sellPayload.realized))} (${sellPayload.realizedPercent != null ? (sellPayload.realized >= 0 ? "+" : "") + sellPayload.realizedPercent.toFixed(2) + "%" : "—"}) / 잔여 ${post.qty}주`;
    } else {
      toastMsg = editId ? "수정 완료!" : "거래 기록 완료!";
    }

    setShowTrade(false); setEditId(null); setUsForm(EMPTY_US);
    showToast(toastMsg);
    if (sellPayload) setSellResult(sellPayload);
  };

  const saveDiv = () => {
    if (!divForm.name || !divForm.amount) { showToast("종목명과 배당금은 필수입니다", false); return; }
    let entry = { ...divForm, id: editId || Date.now().toString() };
    if (editId && entry.auto) { const { autoPending, ...rest } = entry; entry = { ...rest, autoEdited: true }; } // 직접 고친 자동 기록은 이후 자동 갱신 안 함
    const updated = editId ? dividends.map(d => d.id === editId ? entry : d) : [...dividends, entry];
    saveDivs(updated.sort((a, b) => b.date.localeCompare(a.date)));
    setShowDiv(false); setEditId(null); setDivForm(EMPTY_DIV);
    setDivFormDirty({ tax: false, afterTax: false, perShare: false, rate: false });
    showToast("배당금 기록 완료!");
  };

  const delItem = (list, saveFn) => (id) => { if (!window.confirm("삭제하시겠습니까?")) return; saveFn(list.filter(t => t.id !== id)); showToast("삭제되었습니다", false); };
  const delKr = delItem(krTrades, saveKrTrades);
  const delUs = delItem(usTrades, saveUsTrades);
  const delDiv = delItem(dividends, saveDivs);
  const delAllKr = () => { if (!window.confirm(`국내 거래 ${krTrades.length}건을 모두 삭제하시겠습니까?`)) return; saveKrTrades([]); showToast("삭제 완료", false); };
  const delAllUs = () => { if (!window.confirm(`미국 거래 ${usTrades.length}건을 모두 삭제하시겠습니까?`)) return; saveUsTrades([]); showToast("삭제 완료", false); };
  const delAll = () => { if (!window.confirm("모든 데이터를 초기화하시겠습니까?")) return; saveKrTrades([]); saveUsTrades([]); saveDivs([]); showToast("초기화 완료", false); };

  return (
    <div style={{ minHeight: "100vh", background: "var(--bg-base)", color: "var(--text)", fontFamily: "'DM Sans', sans-serif", display: "flex" }}>

      {userId && <SyncBanner userId={userId} onReload={reloadFromServer} />}

      {toast && (
        <div style={{ position: "fixed", top: 20, right: 20, zIndex: 9999, background: toast.ok ? "rgba(52,211,153,0.15)" : "rgba(251,113,133,0.15)", border: `1px solid ${toast.ok ? "rgba(52,211,153,0.4)" : "rgba(251,113,133,0.4)"}`, borderRadius: 12, padding: "12px 22px", color: toast.ok ? "#34d399" : "#fb7185", fontSize: 13, fontWeight: 500, animation: "slideIn 0.2s ease", boxShadow: "0 8px 32px rgba(0,0,0,0.5)", maxWidth: 400 }}>
          {toast.msg}
        </div>
      )}

      {/* 사이드바 */}
      <div className="sidebar">
        <div className="sidebar-header">
          <div className="sidebar-logo">
            <div className="sidebar-logo-bar" />
            <span className="sidebar-logo-text">DGDS</span>
          </div>
        </div>
        <div className="sidebar-body">
          {(() => {
            const ab = (active) => active ? { background: "var(--sb-active-bg)", color: "var(--sb-active-text)", fontWeight: 600, boxShadow: "inset 3px 0 0 var(--primary)" } : {};
            const inact = user ? {} : { color: "var(--text-muted)" };
            const lock = !user ? <span style={{ fontSize: 9, color: "#64748B", marginLeft: "auto" }}><Ico tint={false} n="lock" mr={0} /></span> : null;
            return <>
          <button className="sidebar-btn" onClick={() => setPage("home")} style={ab(page === "home")}><Ico n="house" />홈</button>
          <div className="sidebar-divider" style={{ margin: "8px 0" }} />
          {/* 매매일지는 로그인한 사용자에게만 보임 (둘러보기 상태에선 카테고리 자체를 숨김) */}
          {user && <>
          <div className="sidebar-label">매매일지</div>
          <button className="sidebar-btn" onClick={() => requireLogin(() => { setPage("journal"); setMarket("KR"); setTab("journal"); })} style={{ ...ab(page === "journal" && isKr), ...inact }}>🇰🇷 국내주식 {lock}</button>
          <button className="sidebar-btn" onClick={() => requireLogin(() => { setPage("journal"); setMarket("US"); setTab("us-journal"); })} style={{ ...ab(page === "journal" && !isKr), ...inact }}>🇺🇸 미국주식 {lock}</button>
          <button className="sidebar-btn" onClick={() => requireLogin(() => { setPage("dividend"); setTab("div-list"); })} style={{ ...ab(page === "dividend"), ...inact, marginBottom: 8 }}><Ico n="coins" />배당금 {lock}</button>
          </>}
          <div className="sidebar-label">분석</div>
          <button className="sidebar-btn" onClick={() => setPage("daily-analysis")} style={ab(page === "daily-analysis")}><Ico n="chart-line" />당일 시장 분석</button>
          <button className="sidebar-btn" onClick={() => setPage("news")} style={ab(page === "news")}>
            <Ico n="newspaper" />글로벌 뉴스
            {breakingCount > 0 && <span style={{ marginLeft: "auto", width: 18, height: 18, borderRadius: "50%", background: "#EF4444", color: "#fff", fontSize: 10, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center", animation: "pulse 1.5s infinite" }}>!</span>}
          </button>
          <button className="sidebar-btn" onClick={() => setPage("calendar")} style={ab(page === "calendar")}><Ico n="calendar-days" />증시일정</button>
          <button className="sidebar-btn" onClick={() => setPage("market")} style={ab(page === "market")}><Ico n="chart-column" />시장 지수</button>
          <button className="sidebar-btn" onClick={() => { setResearchCode(null); setPage("analysis"); }} style={ab(page === "analysis")}><Ico n="search" />주식 검색</button>
          <button className="sidebar-btn" onClick={() => setPage("earnings")} style={ab(page === "earnings")}><Ico n="clipboard-list" />실적발표</button>
          <button className="sidebar-btn" onClick={() => setPage("reports")} style={ab(page === "reports")}><Ico n="file-text" />증권사 리포트</button>
          <button className="sidebar-btn" onClick={() => setPage("policy")} style={ab(page === "policy")}><Ico n="landmark" />정책·이벤트</button>
          <button className="sidebar-btn" onClick={() => setPage("memory")} style={ab(page === "memory")}><Ico n="memory-stick" />현물·고정가</button>
          <button className="sidebar-btn" onClick={() => setPage("ytc")} style={ab(page === "ytc")}><Ico n="square-play" />유튜브 댓글 분석</button>
          <div className="sidebar-divider" />
          <div className="sidebar-label">시스템</div>
          <button className="sidebar-btn" onClick={() => setPage("settings")} style={ab(page === "settings")}><Ico n="settings" />설정</button>
          {user ? (
            <div style={{ marginTop: 16, padding: "12px", borderRadius: 8, background: "var(--sb-btn-bg)", border: "1px solid var(--sb-line)" }}>
              <div style={{ fontSize: 11, color: "var(--sb-login)", marginBottom: 6 }}><Ico tint={false} n="user" />{user.nickname}</div>
              <button onClick={onLogout} style={{ width: "100%", padding: "7px", borderRadius: 6, border: "1px solid rgba(251,113,133,0.2)", background: "rgba(251,113,133,0.06)", color: "#fb7185", fontSize: 11, cursor: "pointer", fontFamily: "inherit" }}>로그아웃</button>
            </div>
          ) : (
            <button className="sidebar-btn" onClick={onLogout} style={{ border: "1px solid var(--sb-login-line)", background: "var(--sb-login-bg)", color: "var(--sb-login)", marginTop: 8 }}><Ico tint={false} n="log-in" />로그인</button>
          )}
            </>;
          })()}
        </div>
        <div className="sidebar-footer">
          <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between" }}>
            <div className="sidebar-rate-label" style={{ marginBottom: 0 }}>USD/KRW</div>
            <div className="sidebar-rate-value">{exchangeRate ? `₩${exchangeRate.toLocaleString()}` : "..."}</div>
          </div>
          <div className="sidebar-rate-label" style={{ marginTop: 12, marginBottom: 5 }}>글자 크기</div>
          <div style={{ display: "flex", gap: 5 }}>
            {[{ label: "기본", value: 1 }, { label: "크게", value: 1.25 }, { label: "더 크게", value: 1.5 }].map(z => (
              <button key={z.value} className="sidebar-zoom-btn" onClick={() => { setZoom(z.value); localStorage.setItem("sjv3-zoom", z.value); }} style={{ background: zoom === z.value ? "#3C50E0" : "var(--zoom-btn-bg)", color: zoom === z.value ? "#FFFFFF" : "var(--zoom-btn-text)" }}>{z.label}</button>
            ))}
          </div>
          <div style={{ padding: "8px 0 0" }}>
            <button className="sidebar-theme-btn" onClick={toggleTheme}>
              {theme === "dark" ? <><Ico n="sun" />라이트 모드</> : <><Ico n="moon" />다크 모드</>}
            </button>
          </div>
        </div>
      </div>

      {/* 메인 영역 */}
      <div style={{ marginLeft: 220, flex: 1, display: "flex", flexDirection: "column", minHeight: "100vh", minWidth: 0, overflow: "hidden", zoom: zoom, transformOrigin: "top left" }}>
        {/* 상단 헤더 */}
        <div className="main-header">
          <div className="main-header-inner">
            <div style={{ display: "flex", gap: 2 }}>
              {page === "journal" && currentTabs.map(t => (
                <button key={t.id} onClick={() => { setTab(t.id); setSortKey("buyDate"); setSortDir("desc"); }} style={{ padding: "6px 14px", borderRadius: 8, border: "none", background: tab === t.id ? (isKr ? "rgba(240,192,64,0.1)" : "rgba(56,189,248,0.1)") : "transparent", color: tab === t.id ? (isKr ? "#f0c040" : "#38bdf8") : "#4a5568", fontSize: 12, fontWeight: 500, fontFamily: "inherit", cursor: "pointer" }}>{t.label}</button>
              ))}
              {page === "dividend" && [
                { id: "div-list", label: "배당내역" },
                { id: "div-holdings", label: "보유종목" },
                { id: "div-monthly", label: "월별수익" },
                { id: "div-yearly", label: "연간" },
                { id: "div-stats", label: "종목통계" },
              ].map(t => (
                <button key={t.id} onClick={() => setTab(t.id)} style={{ padding: "6px 14px", borderRadius: 8, border: "none", background: tab === t.id ? "rgba(167,139,250,0.1)" : "transparent", color: tab === t.id ? "#a78bfa" : "#4a5568", fontSize: 12, fontWeight: 500, fontFamily: "inherit", cursor: "pointer" }}>{t.label}</button>
              ))}
              {page === "home" && <div style={{ display: "flex", alignItems: "center", color: "var(--text)", fontSize: 15, fontWeight: 700 }}><Ico n="house" />홈</div>}
              {page === "analysis" && <div style={{ display: "flex", alignItems: "center", color: "var(--text)", fontSize: 15, fontWeight: 700 }}><Ico n="search" />주식 검색</div>}
{page === "market" && <div style={{ display: "flex", alignItems: "center", color: "var(--text)", fontSize: 15, fontWeight: 700 }}><Ico n="chart-column" />실시간 시장 지수</div>}
              {page === "daily-analysis" && <div style={{ display: "flex", alignItems: "center", color: "var(--text)", fontSize: 15, fontWeight: 700 }}><Ico n="chart-line" />당일 시장 분석</div>}
              {page === "news" && <div style={{ display: "flex", alignItems: "center", color: "var(--text)", fontSize: 15, fontWeight: 700 }}><Ico n="newspaper" />글로벌 뉴스</div>}
              {page === "calendar" && <div style={{ display: "flex", alignItems: "center", color: "var(--text)", fontSize: 15, fontWeight: 700 }}><Ico n="calendar-days" />증시일정</div>}
              {page === "earnings" && <div style={{ display: "flex", alignItems: "center", color: "var(--text)", fontSize: 15, fontWeight: 700 }}><Ico n="clipboard-list" />실적발표</div>}
              {page === "reports" && <div style={{ display: "flex", alignItems: "center", color: "var(--text)", fontSize: 15, fontWeight: 700 }}><Ico n="file-text" />증권사 리포트</div>}
              {page === "policy" && <div style={{ display: "flex", alignItems: "center", color: "var(--text)", fontSize: 15, fontWeight: 700, minWidth: 0 }}><Ico n="landmark" /><span style={{ whiteSpace: "nowrap" }}>정책·이벤트</span><PolicyQuickNav activeId={policyActive} onPick={(id) => setPolicyFocus({ id, at: Date.now() })} /></div>}
              {page === "ytc" && <div style={{ display: "flex", alignItems: "center", color: "var(--text)", fontSize: 15, fontWeight: 700 }}><Ico n="square-play" />유튜브 댓글 분석</div>}
              {page === "memory" && <div style={{ display: "flex", alignItems: "center", color: "var(--text)", fontSize: 15, fontWeight: 700 }}><Ico n="memory-stick" />현물·고정거래가격</div>}
              {page === "settings" && <div style={{ display: "flex", alignItems: "center", color: "var(--text)", fontSize: 15, fontWeight: 700 }}><Ico n="settings" />설정</div>}
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <input ref={fileInputRef} type="file" accept=".xlsx,.xls" onChange={handleImport} style={{ display: "none" }} />
              {page === "journal" && (
                <>
                  <button onClick={downloadTemplate} style={{ padding: "6px 14px", borderRadius: 8, border: "1px solid rgba(52,211,153,0.4)", background: "rgba(52,211,153,0.1)", color: "#34d399", fontSize: 11, fontWeight: 600, fontFamily: "inherit", whiteSpace: "nowrap", cursor: "pointer" }}>
                    <Ico n="download" />양식 다운로드
                  </button>
                  {/* Design Ref: position-tracking M6 — 신 양식 익스포트 (round-trip 가능) */}
                  <button onClick={exportJournal} style={{ padding: "6px 14px", borderRadius: 8, border: "1px solid rgba(251,191,36,0.4)", background: "rgba(251,191,36,0.1)", color: "#fbbf24", fontSize: 11, fontWeight: 600, fontFamily: "inherit", whiteSpace: "nowrap", cursor: "pointer" }}>
                    <Ico n="upload" />내 거래 내보내기
                  </button>
                  <button onClick={() => fileInputRef.current && fileInputRef.current.click()} disabled={importing} style={{ padding: "6px 14px", borderRadius: 8, border: "1px solid rgba(167,139,250,0.4)", background: "rgba(167,139,250,0.1)", color: "#a78bfa", fontSize: 11, fontWeight: 600, fontFamily: "inherit", whiteSpace: "nowrap", cursor: "pointer" }}>
                    {importing ? <><Ico n="hourglass" />처리중...</> : <><Ico n="folder-open" />엑셀 가져오기</>}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>

        {/* 로그인 필요 팝업 */}
        {showLoginPrompt && (
          <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(0,0,0,0.6)", zIndex: 999, display: "flex", alignItems: "center", justifyContent: "center" }} onClick={() => setShowLoginPrompt(false)}>
            <div style={{ background: "var(--bg-modal)", borderRadius: 16, padding: "32px 36px", maxWidth: 380, border: "1px solid var(--border)" }} onClick={e => e.stopPropagation()}>
              <div style={{ fontSize: 18, fontWeight: 700, color: "var(--text)", marginBottom: 12, textAlign: "center" }}>로그인이 필요합니다</div>
              <div style={{ fontSize: 13, color: "#718096", marginBottom: 24, textAlign: "center", lineHeight: 1.7 }}>
                매매일지를 이용하려면 로그인이 필요합니다.<br/>닉네임과 PIN으로 간편하게 가입할 수 있습니다.
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <button onClick={() => setShowLoginPrompt(false)} style={{ flex: 1, padding: "12px", borderRadius: 10, border: "1px solid var(--border)", background: "transparent", color: "#718096", fontSize: 14, fontFamily: "inherit", cursor: "pointer" }}>닫기</button>
                <button onClick={() => { setShowLoginPrompt(false); onLogout(); }} style={{ flex: 1, padding: "12px", borderRadius: 10, border: "none", background: "linear-gradient(135deg, #f0c040, #d4a017)", color: "#0d1117", fontSize: 14, fontWeight: 700, fontFamily: "inherit", cursor: "pointer" }}>로그인하기</button>
              </div>
            </div>
          </div>
        )}

        <NewsTicker onClickBreaking={() => setPage("news")} onBreakingCount={setBreakingCount} />

        {/* Alert Center (로그인 시 우측 상단) */}
        {userId && (
          <div style={{ position: "fixed", top: 12, right: 16, zIndex: 500 }}>
            <AlertCenter userId={userId} />
          </div>
        )}

        <div style={{ padding: "28px 24px 80px" }}>
          <Suspense fallback={<LazyFallback />}>
          {page === "home" && <Home watchlist={watchlist} onNavigate={setPage} />}
          {page === "analysis" && <StockAnalysis onGoSettings={() => setPage("settings")} watchlist={watchlist} onWatchlistChange={saveWatchlist} initialCode={researchCode} onOpenPolicy={() => setPage("policy")} />}
          {page === "market" && <><MarketIndicesLoader onData={handleMarketData} /><BondYieldsLoader data={marketRaw} lastUpdate={marketUpdate} /><MarketFunds /></>}
          {page === "daily-analysis" && <DailyMarketAnalysis />}
          {page === "news" && <NewsPage watchlist={watchlist} onOpenStock={(code) => { setResearchCode(code); setPage("analysis"); }} />}
          {page === "calendar" && <MarketCalendar />}
          {page === "earnings" && <EarningsPage />}
          {page === "reports" && <BrokerReports onOpenStock={(code) => { setResearchCode(code); setPage("analysis"); }} />}
          {page === "policy" && <PolicyEvents watchlist={watchlist} focus={policyFocus} onActiveChange={setPolicyActive} onOpenStock={(code) => { setResearchCode(code); setPage("analysis"); }} />}
          {page === "memory" && <MemoryPrices />}
          {page === "ytc" && <OpinionPage />}
          {page === "settings" && <Settings userId={userId} />}
          </Suspense>

        {/* Portfolio Overview 대시보드 (국내/해외) */}
        {page === "journal" && (tab === "overview" || tab === "us-overview") && (
          <Portfolio
            stocks={tab === "overview" ? krEnriched : usEnriched}
            market={tab === "overview" ? "KR" : "US"}
          />
        )}

        {/* 국내 거래일지 */}
        {page === "journal" && tab === "journal" && (
          <>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 24, flexWrap: "wrap", gap: 12 }}>
              <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 22, color: "#f0c040" }}>국내 거래 기록</div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <select value={filterMonth} onChange={e => setFilterMonth(e.target.value)} style={{ ...IS, width: "auto", padding: "8px 14px", fontSize: 12 }}>
                  {allMonths.map(m => <option key={m}>{m}</option>)}
                </select>
                <button onClick={delAllKr} style={{ padding: "9px 16px", borderRadius: 10, border: "1px solid rgba(251,113,133,0.3)", background: "rgba(251,113,133,0.06)", color: "#fb7185", fontSize: 12, fontFamily: "inherit" }}><Ico tint={false} n="trash-2" />전체삭제</button>
                <button onClick={delAll} style={{ padding: "9px 16px", borderRadius: 10, border: "1px solid rgba(251,113,133,0.5)", background: "rgba(251,113,133,0.1)", color: "#fb7185", fontSize: 12, fontWeight: 700, fontFamily: "inherit" }}><Ico tint={false} n="triangle-alert" />초기화</button>
                <button onClick={() => { setKrForm(EMPTY_KR); setEditId(null); setShowTrade(true); }} style={{ padding: "9px 16px", borderRadius: 10, border: "none", background: "linear-gradient(135deg, #f0c040, #d4a017)", color: "#0d1117", fontSize: 12, fontWeight: 700, fontFamily: "inherit" }}>+ 거래 입력</button>
              </div>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 10, marginBottom: 24 }}>
              <Stat label="전체 거래" value={`${krEnriched.length}건`} small />
              <Stat label="보유중" value={`${krHolding.length}건`} color="#fbbf24" small />
              <Stat label="매도완료" value={`${krClosed.length}건`} small />
              {(() => {
                const total = krClosed.reduce((a, t) => a + t.profit, 0);
                const filtered = krClosed.filter(t => !excludedNames.has(t.name)).reduce((a, t) => a + t.profit, 0);
                const hasExclusion = excludedNames.size > 0 && total !== filtered;
                return hasExclusion ? (
                  <Stat label={`총 실현손익 (제외 ${excludedNames.size}종목)`} value={fmt(filtered)} value2={`전체: ${fmt(total)}`} color={filtered >= 0 ? "#f04452" : "#3182f6"} small />
                ) : (
                  <Stat label="총 실현손익" value={fmt(total)} color={total >= 0 ? "#f04452" : "#3182f6"} small />
                );
              })()}
              <Stat label="총 배당금(세후)" value={fmt(dividends.filter(d => d.currency === "KRW").reduce((a, d) => a + (parseFloat(d.afterTax) || 0), 0))} color="#a78bfa" small />
            </div>
            {/* Design Ref: position-tracking Addendum — 종목 그룹 카드 뷰 (활성/완료 분리, 접고 펼치기) */}
            {krPositions.length === 0 ? (
              <div style={{ textAlign: "center", padding: "70px 20px", color: "var(--text-muted)", fontSize: 14, background: "var(--bg-table)", borderRadius: 16 }}>
                거래 내역이 없습니다. <Ico n="folder-open" />엑셀 가져오기 또는 + 거래 입력으로 시작하세요
              </div>
            ) : (
              <div>
                {krPositions.map(pos => {
                  const groupTrades = krTrades.filter(t => pos.rowIds.includes(t.id))
                    .sort((a, b) => tradeSortKey(a).localeCompare(tradeSortKey(b)));
                  const expanded = isGroupExpanded(pos);
                  return (
                    <PositionGroupCard
                      key={pos.key}
                      pos={pos}
                      expanded={expanded}
                      onToggle={() => toggleGroup(pos)}
                      currency="KRW"
                      onAddBuy={() => openKrAdd({ name: pos.name, broker: pos.broker }, "BUY")}
                      onAddSell={() => openKrAdd({ name: pos.name, broker: pos.broker }, "SELL")}
                    >
                      <div style={{ overflowX: "auto" }}>
                        <table style={{ width: "100%", minWidth: 700, borderCollapse: "collapse", fontSize: 12 }}>
                          <thead>
                            <tr style={{ background: "rgba(45,55,72,0.3)" }}>
                              <th style={{ padding: "8px 12px", fontSize: 10, color: "var(--text-sub)", fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase", textAlign: "left" }}>구분</th>
                              <th style={{ padding: "8px 12px", fontSize: 10, color: "var(--text-sub)", fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase", textAlign: "left" }}>날짜</th>
                              <th style={{ padding: "8px 12px", fontSize: 10, color: "var(--text-sub)", fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase", textAlign: "left" }}>가격</th>
                              <th style={{ padding: "8px 12px", fontSize: 10, color: "var(--text-sub)", fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase", textAlign: "left" }}>수량</th>
                              <th style={{ padding: "8px 12px", fontSize: 10, color: "var(--text-sub)", fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase", textAlign: "left" }}>금액</th>
                              <th style={{ padding: "8px 12px", fontSize: 10, color: "var(--text-sub)", fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase", textAlign: "left" }}>메모</th>
                              <th style={{ padding: "8px 12px" }} />
                            </tr>
                          </thead>
                          <tbody>
                            {groupTrades.map(t => {
                              const isLegacy = t.kind === undefined;
                              const isBuyRow = t.kind === "BUY" || (isLegacy && t.buyDate);
                              const isSellRow = t.kind === "SELL" || (isLegacy && t.sellDate && (t.sellPrice));
                              const showBoth = isLegacy && t.buyDate && t.sellDate && t.sellPrice;
                              const labelStyle = (kind) => ({ padding: "2px 8px", borderRadius: 6, fontSize: 10, fontWeight: 600, background: kind === "BUY" ? "rgba(240,68,82,0.12)" : "rgba(49,130,246,0.12)", color: kind === "BUY" ? "#f04452" : "#3182f6" });
                              return (
                                <tr key={t.id} style={{ borderTop: "1px solid var(--divider-row)" }}>
                                  <td style={{ padding: "10px 12px" }}>
                                    {showBoth ? (
                                      <div style={{ display: "flex", gap: 4 }}>
                                        <span style={labelStyle("BUY")}>매수</span>
                                        <span style={labelStyle("SELL")}>매도</span>
                                      </div>
                                    ) : isBuyRow ? <span style={labelStyle("BUY")}>매수</span>
                                      : isSellRow ? <span style={labelStyle("SELL")}>매도</span>
                                      : <span style={{ color: "#a0aec0" }}>—</span>}
                                  </td>
                                  <td style={{ padding: "10px 12px", fontFamily: "'DM Mono', monospace", fontSize: 11, color: "#a0aec0", whiteSpace: "nowrap" }}>
                                    {showBoth ? <>{t.buyDate}<br/><span style={{ color: "#718096" }}>→ {t.sellDate}</span></>
                                      : isBuyRow ? t.buyDate
                                      : isSellRow ? t.sellDate
                                      : "—"}
                                  </td>
                                  <td style={{ padding: "10px 12px", fontFamily: "'DM Mono', monospace", fontSize: 11, whiteSpace: "nowrap" }}>
                                    {showBoth ? <><span style={{ color: "#38bdf8" }}>{(parseFloat(t.buyPrice)||0).toLocaleString()}</span><br/><span style={{ color: "#fb7185" }}>{(parseFloat(t.sellPrice)||0).toLocaleString()}</span></>
                                      : isBuyRow ? <span style={{ color: "#38bdf8" }}>{(parseFloat(t.buyPrice)||0).toLocaleString()}</span>
                                      : isSellRow ? <span style={{ color: "#fb7185" }}>{(parseFloat(t.sellPrice)||0).toLocaleString()}</span>
                                      : "—"}
                                  </td>
                                  <td style={{ padding: "10px 12px", fontFamily: "'DM Mono', monospace", fontSize: 11, color: "#cbd5e0" }}>{(parseInt(t.quantity)||0).toLocaleString()}주</td>
                                  <td style={{ padding: "10px 12px", fontFamily: "'DM Mono', monospace", fontSize: 11, color: "#718096" }}>
                                    {(() => {
                                      const q = parseInt(t.quantity) || 0;
                                      if (showBoth) return (q * (parseFloat(t.buyPrice)||0)).toLocaleString();
                                      if (isBuyRow) return (q * (parseFloat(t.buyPrice)||0)).toLocaleString();
                                      if (isSellRow) return (q * (parseFloat(t.sellPrice)||0)).toLocaleString();
                                      return "—";
                                    })()}
                                  </td>
                                  <td style={{ padding: "10px 12px", fontSize: 11, color: "var(--text-sub)", maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{[t.reason, t.memo].filter(Boolean).join(" / ") || "—"}</td>
                                  <td style={{ padding: "10px 12px", whiteSpace: "nowrap" }}>
                                    <div style={{ display: "flex", gap: 4 }}>
                                      <button onClick={() => openKrEdit(t)} style={{ background: "none", border: "1px solid var(--border)", borderRadius: 6, color: "#718096", padding: "3px 8px", fontSize: 10 }}>수정</button>
                                      <button onClick={() => delKr(t.id)} style={{ background: "none", border: "1px solid rgba(251,113,133,0.2)", borderRadius: 6, color: "#fb7185", padding: "3px 8px", fontSize: 10 }}>삭제</button>
                                    </div>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </PositionGroupCard>
                  );
                })}
              </div>
            )}
          </>
        )}

        {/* 국내 보유현황 */}
        {page === "journal" && tab === "holding" && (
          <>
            <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 22, color: "#f0c040", marginBottom: 24 }}>국내 보유 포지션</div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12, marginBottom: 24 }}>
              <Stat label="보유 종목수" value={`${krHolding.length}개`} color="#fbbf24" />
              {(() => {
                const total = krHolding.reduce((a, t) => a + t.buyAmt, 0);
                const filtered = krHolding.filter(t => !excludedNames.has(t.name)).reduce((a, t) => a + t.buyAmt, 0);
                const hasExclusion = excludedNames.size > 0 && total !== filtered;
                return hasExclusion ? (
                  <Stat label={`총 매수금액 (제외 ${excludedNames.size}종목)`} value={fmt(filtered)} value2={`전체: ${fmt(total)}`} />
                ) : (
                  <Stat label="총 매수금액" value={fmt(total)} />
                );
              })()}
              <Stat label="평균 보유일" value={krHolding.length > 0 ? Math.round(krHolding.reduce((a, t) => a + t.holdDays, 0) / krHolding.length) + "일" : "—"} />
            </div>
            {excludedNames.size > 0 && (
              <div style={{ marginBottom: 14, padding: "10px 14px", borderRadius: 8, background: "rgba(167,139,250,0.08)", border: "1px solid rgba(167,139,250,0.25)", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 8 }}>
                <div style={{ fontSize: 12, color: "#a78bfa" }}>
                  <span style={{ fontWeight: 600 }}>제외 {excludedNames.size}종목:</span> {[...excludedNames].join(", ")}
                </div>
                <button onClick={clearExcludes} style={{ padding: "4px 12px", borderRadius: 6, border: "1px solid rgba(167,139,250,0.4)", background: "transparent", color: "#a78bfa", fontSize: 11, cursor: "pointer", fontFamily: "inherit" }}>모두 해제</button>
              </div>
            )}
            {krHolding.length === 0 ? (
              <div style={{ textAlign: "center", padding: 80, color: "var(--text-muted)" }}>보유중인 포지션이 없습니다</div>
            ) : krHolding.sort((a, b) => b.buyAmt - a.buyAmt).map(t => (
              <div key={t.id} style={{ background: "var(--bg-table)", border: "1px solid var(--border)", borderLeft: "3px solid #fbbf24", borderRadius: 14, padding: "18px 22px", display: "grid", gridTemplateColumns: "32px 2fr 1fr 1fr 1fr 1fr 120px", gap: 14, alignItems: "center", marginBottom: 10, opacity: excludedNames.has(t.name) ? 0.45 : 1 }}>
                <input type="checkbox" checked={excludedNames.has(t.name)} onChange={() => toggleExclude(t.name)} title="합계에서 제외" style={{ cursor: "pointer" }} />
                <div>
                  <div style={{ fontSize: 15, fontWeight: 600, color: "var(--text)" }}>{t.name}</div>
                  <div style={{ fontSize: 11, color: "var(--text-sub)", marginTop: 3, fontFamily: "'DM Mono', monospace" }}>{t.buyDate}</div>
                </div>
                <div><div style={{ fontSize: 10, color: "var(--text-sub)", marginBottom: 3 }}>매수가</div><div style={{ fontFamily: "'DM Mono', monospace", fontSize: 13, color: "#38bdf8" }}>{t.bp.toLocaleString()}원</div></div>
                <div><div style={{ fontSize: 10, color: "var(--text-sub)", marginBottom: 3 }}>수량</div><div style={{ fontFamily: "'DM Mono', monospace", fontSize: 13 }}>{t.qty.toLocaleString()}주</div></div>
                <div><div style={{ fontSize: 10, color: "var(--text-sub)", marginBottom: 3 }}>매수금액</div><div style={{ fontFamily: "'DM Mono', monospace", fontSize: 13 }}>{t.buyAmt.toLocaleString()}원</div></div>
                <div><div style={{ fontSize: 10, color: "var(--text-sub)", marginBottom: 3 }}>보유일</div><div style={{ fontFamily: "'DM Mono', monospace", fontSize: 13, color: "#fbbf24" }}>{t.holdDays}일</div></div>
                <button onClick={() => openKrAdd(t, "SELL")} style={{ padding: "8px 14px", borderRadius: 8, border: "1px solid rgba(240,192,64,0.35)", background: "rgba(240,192,64,0.06)", color: "#f0c040", fontSize: 12, fontFamily: "inherit" }}>매도 입력</button>
              </div>
            ))}
          </>
        )}

        {/* 국내 월별손익 */}
        {page === "journal" && tab === "monthly" && (
          <>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 24, flexWrap: "wrap", gap: 12 }}>
              <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 22, color: "#f0c040" }}>국내 월별 손익</div>
              <select value={filterMonth} onChange={e => setFilterMonth(e.target.value)} style={{ ...IS, width: "auto", padding: "8px 14px", fontSize: 12 }}>
                {allMonths.map(m => <option key={m}>{m}</option>)}
              </select>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12, marginBottom: 24 }}>
              <Stat label="매도 거래수" value={`${monthKrClosed.length}건`} />
              {(() => {
                const total = monthKrClosed.reduce((a, t) => a + t.profit, 0);
                const filtered = monthKrClosed.filter(t => !excludedNames.has(t.name)).reduce((a, t) => a + t.profit, 0);
                const divTotal = monthDivs.filter(d => d.currency === "KRW").reduce((a, d) => a + (parseFloat(d.afterTax) || 0), 0);
                const divFiltered = monthDivs.filter(d => d.currency === "KRW" && !excludedNames.has(d.name)).reduce((a, d) => a + (parseFloat(d.afterTax) || 0), 0);
                const hasExc = excludedNames.size > 0 && (total !== filtered || divTotal !== divFiltered);
                return (
                  <>
                    {hasExc ? (
                      <Stat label={`실현 손익 (제외 ${excludedNames.size})`} value={fmt(filtered)} value2={`전체: ${fmt(total)}`} color={filtered >= 0 ? "#f04452" : "#3182f6"} />
                    ) : (
                      <Stat label="실현 손익" value={fmt(total)} color={total >= 0 ? "#f04452" : "#3182f6"} />
                    )}
                    <Stat label="배당금(세후)" value={fmt(hasExc ? divFiltered : divTotal)} color="#a78bfa" />
                    {hasExc ? (
                      <Stat label="합계 (제외 반영)" value={fmt(filtered + divFiltered)} value2={`전체: ${fmt(total + divTotal)}`} color="#34d399" />
                    ) : (
                      <Stat label="합계" value={fmt(total + divTotal)} color="#34d399" />
                    )}
                  </>
                );
              })()}
            </div>
            {monthKrClosed.length === 0 ? (
              <div style={{ textAlign: "center", padding: 40, color: "var(--text-muted)" }}>이 달에 매도한 거래가 없습니다</div>
            ) : monthKrClosed.sort((a, b) => b.profit - a.profit).map(t => (
              <div key={t.id} style={{ background: "var(--bg-table)", border: "1px solid var(--border-light)", borderLeft: `3px solid ${t.profit >= 0 ? "#f04452" : "#3182f6"}`, borderRadius: 12, padding: "14px 20px", display: "grid", gridTemplateColumns: "32px 1.5fr 1.5fr 0.8fr 1.2fr 1fr 1fr", gap: 14, alignItems: "center", marginBottom: 8, opacity: excludedNames.has(t.name) ? 0.45 : 1 }}>
                <input type="checkbox" checked={excludedNames.has(t.name)} onChange={() => toggleExclude(t.name)} title="합계에서 제외" style={{ cursor: "pointer" }} />
                <div style={{ fontWeight: 600, color: "var(--text)" }}>{t.name}</div>
                <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 11, color: "#718096" }}>{t.buyDate} → {t.sellDate}</div>
                <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 12, color: "#718096" }}>{t.holdDays}일</div>
                <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 12, color: "#a0aec0" }}>{t.qty}주 × {t.bp.toLocaleString()}</div>
                <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 14, fontWeight: 700, color: t.profit >= 0 ? "#f04452" : "#3182f6" }}>{(t.profit >= 0 ? "+" : "") + Math.round(t.profit).toLocaleString()}원</div>
                <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 14, fontWeight: 700, color: t.profitRate >= 0 ? "#f04452" : "#3182f6" }}>{fmtPct(t.profitRate)}</div>
              </div>
            ))}
          </>
        )}

        {/* 국내 종목통계 */}
        {page === "journal" && tab === "stats" && (
          <>
            <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 22, color: "#f0c040", marginBottom: 24 }}>국내 종목별 통계</div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12, marginBottom: 24 }}>
              <Stat label="거래 종목수" value={`${krStats.length}개`} small />
              <Stat label="전체 승률" value={krClosed.length > 0 ? ((krClosed.filter(t => t.profit > 0).length / krClosed.length) * 100).toFixed(1) + "%" : "—"} color="#34d399" small />
              <Stat label="총 실현손익" value={fmt(krClosed.reduce((a, t) => a + t.profit, 0))} color={krClosed.reduce((a, t) => a + t.profit, 0) >= 0 ? "#f04452" : "#3182f6"} small />
              <Stat label="총 배당금(세후)" value={fmt(dividends.filter(d => d.currency === "KRW").reduce((a, d) => a + (parseFloat(d.afterTax) || 0), 0))} color="#a78bfa" small />
            </div>
            <div style={{ background: "var(--bg-table)", border: "1px solid var(--border-light)", borderRadius: 16, overflow: "hidden" }}>
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", minWidth: 900, borderCollapse: "collapse", fontSize: 13 }}>
                  <thead>
                    <tr style={{ background: "var(--bg-card)" }}>
                      {["종목명","총 거래","매도 완료","승/패","실현 손익","수익률","배당금"].map(h => (
                        <th key={h} style={{ padding: "13px 16px", textAlign: "left", fontSize: 10, color: "var(--text-sub)", fontWeight: 600, letterSpacing: "0.1em", textTransform: "uppercase", borderBottom: "1px solid var(--border-light)", fontFamily: "'DM Mono', monospace", whiteSpace: "nowrap" }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {krStats.map(s => (
                      <tr key={s.name} style={{ borderBottom: "1px solid var(--divider-row)" }}>
                        <td style={{ padding: "13px 16px", fontWeight: 600, color: "var(--text)" }}>{s.name}</td>
                        <td style={{ padding: "13px 16px", fontFamily: "'DM Mono', monospace", fontSize: 12, color: "#718096" }}>{s.count}건</td>
                        <td style={{ padding: "13px 16px", fontFamily: "'DM Mono', monospace", fontSize: 12, color: "#718096" }}>{s.closedCount}건</td>
                        <td style={{ padding: "13px 16px", fontFamily: "'DM Mono', monospace", fontSize: 12 }}>
                          <span style={{ color: "#34d399" }}>{s.wins}승</span>
                          <span style={{ color: "var(--text-sub)", margin: "0 4px" }}>/</span>
                          <span style={{ color: "#fb7185" }}>{s.closedCount - s.wins}패</span>
                        </td>
                        <td style={{ padding: "13px 16px", fontFamily: "'DM Mono', monospace", fontSize: 14, fontWeight: 700, color: s.closedCount > 0 ? s.totalProfit >= 0 ? "#f04452" : "#3182f6" : "#2d3748", whiteSpace: "nowrap" }}>{s.closedCount > 0 ? (s.totalProfit >= 0 ? "+" : "") + Math.round(s.totalProfit).toLocaleString() + "원" : "—"}</td>
                        <td style={{ padding: "13px 16px", fontFamily: "'DM Mono', monospace", fontSize: 14, fontWeight: 700, color: s.rate != null ? s.rate >= 0 ? "#f04452" : "#3182f6" : "#2d3748" }}>{s.rate != null ? fmtPct(s.rate) : "—"}</td>
                        <td style={{ padding: "13px 16px", fontFamily: "'DM Mono', monospace", fontSize: 13, color: s.divs > 0 ? "#a78bfa" : "#2d3748", whiteSpace: "nowrap" }}>{s.divs > 0 ? "+" + s.divs.toLocaleString() + "원" : "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}

        {/* 배당금 - 배당내역 탭 */}
        {page === "dividend" && tab === "div-list" && (
          <>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 24, flexWrap: "wrap", gap: 12 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 22, color: "#a78bfa" }}>배당금 내역</div>
                {divFilter && (
                  <div style={{ display: "flex", alignItems: "center", gap: 6, background: "rgba(167,139,250,0.12)", border: "1px solid rgba(167,139,250,0.3)", borderRadius: 8, padding: "4px 12px" }}>
                    <span style={{ fontSize: 12, color: "#a78bfa", fontWeight: 600 }}>{divFilter}</span>
                    <button onClick={() => setDivFilter("")} style={{ background: "none", border: "none", color: "#a78bfa", cursor: "pointer", fontSize: 14, padding: "0 2px", lineHeight: 1 }}>×</button>
                  </div>
                )}
              </div>
              <button onClick={openDivNew} style={{ padding: "9px 16px", borderRadius: 10, border: "none", background: "linear-gradient(135deg, #a78bfa, #7c3aed)", color: "#fff", fontSize: 12, fontWeight: 700, fontFamily: "inherit" }}>+ 배당금 입력</button>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 10, marginBottom: 24 }}>
              <Stat label="총 수령 횟수" value={`${dividends.length}회`} small />
              <Stat label="배당 종목수" value={`${[...new Set(dividends.map(d => d.name))].length}개`} small />
              <Stat label="총 세후 배당 (원화)" value={fmt(dividends.filter(d => (d.currency || "KRW") === "KRW").reduce((a, d) => a + (parseFloat(d.afterTax) || 0), 0))} color="#a78bfa" small />
              <Stat label="총 세후 배당 (달러)" value={fmtD(dividends.filter(d => d.currency === "USD").reduce((a, d) => a + (parseFloat(d.afterTax) || 0), 0))} color="#38bdf8" small />
              {/* Design Ref: dividend-tax-tracking §6.3 — 통계 카드 5→7 (USD 만) */}
              <Stat label="총 세금 (USD)" value={fmtD(dividends.filter(d => d.currency === "USD" && !d.taxExempt).reduce((a, d) => a + (parseFloat(d.tax) || 0), 0))} color="#9ca3af" small />
              <Stat label="면세 총액 (USD)" value={fmtD(dividends.filter(d => d.currency === "USD" && d.taxExempt).reduce((a, d) => a + (parseFloat(d.amount) || 0), 0))} color="#34d399" small />
              <Stat label="합산 (원화 환산(세후))" value={fmt(dividends.filter(d => (d.currency || "KRW") === "KRW").reduce((a, d) => a + (parseFloat(d.afterTax) || 0), 0) + dividends.filter(d => d.currency === "USD").reduce((a, d) => a + (parseFloat(d.afterTax) || 0) * ((d.rate && parseFloat(d.rate) > 0) ? parseFloat(d.rate) : rate), 0))} color="#34d399" small />
            </div>
            {dividends.length === 0 ? (
              <div style={{ textAlign: "center", padding: 80, color: "var(--text-muted)" }}>배당금 기록이 없습니다</div>
            ) : (
              <div style={{ background: "var(--bg-table)", border: "1px solid rgba(167,139,250,0.08)", borderRadius: 16, overflow: "hidden" }}>
                <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", minWidth: 900, borderCollapse: "collapse", fontSize: 13 }}>
                  <thead>
                    <tr style={{ background: "var(--bg-card)" }}>
                      {["수령일","증권사","종목명","통화","주수","세전 배당금","세금","세후 배당금","주당 배당금","원화 환산(세후)","메모","관리"].map((h, i, arr) => (
                        <th key={i} style={{ padding: "13px 16px", textAlign: "left", fontSize: 10, color: "var(--text-sub)", fontWeight: 600, letterSpacing: "0.1em", textTransform: "uppercase", borderBottom: "1px solid var(--border-light)", fontFamily: "'DM Mono', monospace", ...(i === arr.length - 1 ? { position: "sticky", right: 0, background: "var(--bg-card)", zIndex: 2, borderLeft: "1px solid var(--border-light)" } : {}) }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {dividends.filter(d => !divFilter || d.name === divFilter).map(d => {
                      const amt = parseFloat(d.amount) || 0;
                      const atAmt = parseFloat(d.afterTax) || 0;
                      const taxAmt = parseFloat(d.tax) || 0;
                      const shares = parseFloat(d.shares) || 0;
                      // 저장된 주당배당금 우선, 없으면 세전÷주수
                      const perShare = (d.perShare !== '' && d.perShare !== undefined && d.perShare !== null)
                        ? (parseFloat(d.perShare) || 0)
                        : (shares > 0 ? amt / shares : 0);
                      const isUsd = d.currency === "USD";
                      // 수령일 환율(d.rate) 우선, 없으면 현재 환율
                      const effRate = (d.rate && parseFloat(d.rate) > 0) ? parseFloat(d.rate) : rate;
                      const krwVal = isUsd ? atAmt * effRate : atAmt;
                      const isExempt = !!d.taxExempt;
                      return (
                      <tr key={d.id} style={{ borderBottom: "1px solid var(--divider-row)" }}>
                        <td style={{ padding: "13px 16px", fontFamily: "'DM Mono', monospace", fontSize: 12, color: "#718096", whiteSpace: "nowrap" }}>
                          {/* Design Ref: dividend-tax-tracking §6.5 — 면세 뱃지 */}
                          {isExempt && <span style={{ fontSize: 10, padding: "1px 6px", borderRadius: 4, background: "rgba(52,211,153,0.15)", color: "#34d399", marginRight: 6, fontWeight: 600, fontFamily: "'DM Sans', sans-serif" }}>면세</span>}
                          {d.auto && <span title={d.memo || "자동 기록"} style={{ fontSize: 10, padding: "1px 6px", borderRadius: 4, background: "rgba(96,165,250,0.15)", color: "#60a5fa", marginRight: 6, fontWeight: 600, fontFamily: "'DM Sans', sans-serif" }}>자동</span>}
                          {d.date}
                        </td>
                        <td style={{ padding: "13px 16px", fontSize: 12, color: d.broker ? "#cbd5e0" : "#4a5568", whiteSpace: "nowrap" }}>{d.broker || "—"}</td>
                        <td style={{ padding: "13px 16px", fontWeight: 600, color: "var(--text)" }}>{d.name}</td>
                        <td style={{ padding: "13px 16px" }}>
                          <span style={{ fontSize: 11, fontWeight: 600, padding: "2px 10px", borderRadius: 6, background: isUsd ? "rgba(56,189,248,0.12)" : "rgba(240,192,64,0.12)", color: isUsd ? "#38bdf8" : "#f0c040", border: isUsd ? "1px solid rgba(56,189,248,0.3)" : "1px solid rgba(240,192,64,0.3)" }}>{isUsd ? "USD" : "KRW"}</span>
                        </td>
                        <td style={{ padding: "13px 16px", fontFamily: "'DM Mono', monospace", fontSize: 12, color: "#718096", whiteSpace: "nowrap" }}>{shares > 0 ? `${shares.toLocaleString()}주` : "—"}</td>
                        <td style={{ padding: "13px 16px", fontFamily: "'DM Mono', monospace", fontSize: 14, fontWeight: 700, color: "#a78bfa", whiteSpace: "nowrap" }}>{isUsd ? `$${amt.toFixed(2)}` : `+${amt.toLocaleString()}원`}</td>
                        <td style={{ padding: "13px 16px", fontFamily: "'DM Mono', monospace", fontSize: 13, fontWeight: 600, color: isExempt ? "#34d399" : (taxAmt > 0 ? "#9ca3af" : "#4a5568"), whiteSpace: "nowrap" }}>{isExempt ? "면세" : (taxAmt > 0 ? (isUsd ? `$${taxAmt.toFixed(2)}` : `${taxAmt.toLocaleString()}원`) : ((d.taxPerShare !== "" && d.taxPerShare != null && parseFloat(d.taxPerShare) === 0) || (d.taxBasePerShare !== "" && d.taxBasePerShare != null && parseFloat(d.taxBasePerShare) === 0) ? "0원" : "—"))}
                          {!isExempt && !isUsd && (d.taxPerShare === "" || d.taxPerShare == null) && d.taxBasePerShare !== "" && d.taxBasePerShare != null && Number.isFinite(parseFloat(d.taxBasePerShare)) ? (
                            <div style={{ fontSize: 10, fontWeight: 500, color: "#60a5fa", marginTop: 2 }}>
                              과세표준 {parseFloat(d.taxBasePerShare).toLocaleString()}원{perShare > 0 ? ` / ${Math.round(perShare).toLocaleString()}원` : ""}{parseFloat(d.taxBasePerShare) === 0 ? " · 비과세" : ""}
                            </div>
                          ) : !isExempt && shares > 0 && (taxAmt > 0 || (d.taxPerShare !== "" && d.taxPerShare != null)) && (
                            <div style={{ fontSize: 10, fontWeight: 500, color: (d.taxPerShare !== "" && d.taxPerShare != null) ? "#fbbf24" : "#718096", marginTop: 2 }}>
                              주당 {isUsd ? `$${(taxAmt / shares).toFixed(4)}` : `${(Math.round((taxAmt / shares) * 100) / 100).toLocaleString()}원`}{(d.taxPerShare !== "" && d.taxPerShare != null) ? " · 부분과세" : ""}
                            </div>
                          )}</td>
                        <td style={{ padding: "13px 16px", fontFamily: "'DM Mono', monospace", fontSize: 14, fontWeight: 700, color: "#34d399", whiteSpace: "nowrap" }}>{atAmt > 0 ? (isUsd ? `$${atAmt.toFixed(2)}` : `+${atAmt.toLocaleString()}원`) : "—"}</td>
                        <td style={{ padding: "13px 16px", fontFamily: "'DM Mono', monospace", fontSize: 12, fontWeight: 600, color: perShare > 0 ? "#f0c040" : "#2d3748", whiteSpace: "nowrap" }}>{perShare > 0 ? (isUsd ? `$${perShare.toFixed(4)}` : `${Math.round(perShare).toLocaleString()}원`) : "—"}</td>
                        <td style={{ padding: "13px 16px", fontFamily: "'DM Mono', monospace", fontSize: 13, color: isUsd ? "#718096" : "#4a5568", whiteSpace: "nowrap" }}>{isUsd && atAmt > 0 ? <>≈ {Math.round(krwVal).toLocaleString()}원<span style={{ fontSize: 10, color: "#4a5568", marginLeft: 5 }}>@₩{Math.round(effRate).toLocaleString()}</span></> : "—"}</td>
                        <td style={{ padding: "13px 16px", fontSize: 12, color: "var(--text-sub)", maxWidth: 160, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{d.memo || "—"}</td>
                        <td style={{ padding: "13px 16px", whiteSpace: "nowrap", position: "sticky", right: 0, background: "var(--bg-table)", zIndex: 1, borderLeft: "1px solid var(--divider-row)" }}>
                          <div style={{ display: "flex", gap: 6 }}>
                            <button onClick={() => openDivEdit(d)} style={{ background: "none", border: "1px solid var(--border)", borderRadius: 6, color: "#718096", padding: "4px 10px", fontSize: 11 }}>수정</button>
                            <button onClick={() => delDiv(d.id)} style={{ background: "none", border: "1px solid rgba(251,113,133,0.2)", borderRadius: 6, color: "#fb7185", padding: "4px 10px", fontSize: 11 }}>삭제</button>
                          </div>
                        </td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
                </div>
              </div>
            )}
          </>
        )}

        {/* 배당금 - 월별수익 탭 */}
        {page === "dividend" && tab === "div-monthly" && (() => {
          const divMonths = [...new Set(dividends.map(d => d.date.slice(0, 7)))].sort((a, b) => b.localeCompare(a));
          const allDivKrw = dividends.filter(d => (d.currency || "KRW") === "KRW").reduce((a, d) => a + (parseFloat(d.afterTax) || 0), 0);
          const allDivUsd = dividends.filter(d => d.currency === "USD").reduce((a, d) => a + (parseFloat(d.afterTax) || 0), 0);
          return (
          <>
            <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 22, color: "#a78bfa", marginBottom: 24 }}>월별 배당 수익</div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 10, marginBottom: 24 }}>
              <Stat label="배당 발생 월수" value={`${divMonths.length}개월`} small />
              <Stat label="총 세후 배당 (원화)" value={fmt(allDivKrw)} color="#a78bfa" small />
              <Stat label="총 세후 배당 (달러)" value={fmtD(allDivUsd)} color="#38bdf8" small />
              <Stat label="합산 (원화 환산(세후))" value={fmt(allDivKrw + allDivUsd * rate)} color="#34d399" small />
            </div>
            {divMonths.length === 0 ? (
              <div style={{ textAlign: "center", padding: 80, color: "var(--text-muted)" }}>배당금 기록이 없습니다</div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {divMonths.map(month => {
                  const ds = dividends.filter(d => d.date.startsWith(month));
                  const krwSum = ds.filter(d => (d.currency || "KRW") === "KRW").reduce((a, d) => a + (parseFloat(d.afterTax) || 0), 0);
                  const usdSum = ds.filter(d => d.currency === "USD").reduce((a, d) => a + (parseFloat(d.afterTax) || 0), 0);
                  const total = krwSum + (usdSum * rate);
                  const names = [...new Set(ds.map(d => d.name))];
                  return (
                  <div key={month} style={{ background: "var(--bg-table)", border: "1px solid rgba(167,139,250,0.08)", borderLeft: "3px solid #a78bfa", borderRadius: 14, padding: "18px 22px" }}>
                    <div style={{ display: "grid", gridTemplateColumns: "120px 1fr 1fr 1fr 1fr", gap: 14, alignItems: "center", marginBottom: ds.length > 1 ? 14 : 0 }}>
                      <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 15, fontWeight: 700, color: "var(--text)" }}>{month}</div>
                      <div><div style={{ fontSize: 10, color: "var(--text-sub)", marginBottom: 3 }}>수령 횟수</div><div style={{ fontFamily: "'DM Mono', monospace", fontSize: 13, color: "#718096" }}>{ds.length}회</div></div>
                      <div><div style={{ fontSize: 10, color: "var(--text-sub)", marginBottom: 3 }}>세후 원화</div><div style={{ fontFamily: "'DM Mono', monospace", fontSize: 13, fontWeight: 600, color: krwSum > 0 ? "#a78bfa" : "#2d3748" }}>{krwSum > 0 ? `+${Math.round(krwSum).toLocaleString()}원` : "—"}</div></div>
                      <div><div style={{ fontSize: 10, color: "var(--text-sub)", marginBottom: 3 }}>세후 달러</div><div style={{ fontFamily: "'DM Mono', monospace", fontSize: 13, fontWeight: 600, color: usdSum > 0 ? "#38bdf8" : "#2d3748" }}>{usdSum > 0 ? `+$${usdSum.toFixed(2)}` : "—"}</div></div>
                      <div><div style={{ fontSize: 10, color: "var(--text-sub)", marginBottom: 3 }}>합산 (원화 환산(세후))</div><div style={{ fontFamily: "'DM Mono', monospace", fontSize: 16, fontWeight: 700, color: "#34d399" }}>+{Math.round(total).toLocaleString()}원</div></div>
                    </div>
                    {ds.length > 0 && (
                      <div style={{ borderTop: "1px solid var(--divider-row)", paddingTop: 12, display: "flex", flexWrap: "wrap", gap: 8 }}>
                        {ds.map(d => {
                          const atAmt = parseFloat(d.afterTax) || 0;
                          const isUsd = d.currency === "USD";
                          return (
                          <div key={d.id} style={{ background: "var(--bg-input)", border: "1px solid var(--border-light)", borderRadius: 8, padding: "6px 12px", display: "flex", alignItems: "center", gap: 8 }}>
                            <span style={{ fontSize: 12, color: "#a0aec0" }}>{d.name}</span>
                            <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 12, fontWeight: 600, color: isUsd ? "#38bdf8" : "#a78bfa" }}>{isUsd ? `$${atAmt.toFixed(2)}` : `${atAmt.toLocaleString()}원`}</span>
                          </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                  );
                })}
              </div>
            )}
          </>
          );
        })()}

        {/* 배당금 - 종목통계 탭 */}
        {page === "dividend" && tab === "div-stats" && (() => {
          const divNames = [...new Set(dividends.map(d => d.name))];
          const allDivKrw = dividends.filter(d => (d.currency || "KRW") === "KRW").reduce((a, d) => a + (parseFloat(d.afterTax) || 0), 0);
          const allDivUsd = dividends.filter(d => d.currency === "USD").reduce((a, d) => a + (parseFloat(d.afterTax) || 0), 0);
          return (
          <>
            <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 22, color: "#a78bfa", marginBottom: 24 }}>종목별 배당 통계</div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 10, marginBottom: 24 }}>
              <Stat label="배당 종목수" value={`${divNames.length}개`} small />
              <Stat label="총 세후 배당 (원화)" value={fmt(allDivKrw)} color="#a78bfa" small />
              <Stat label="총 세후 배당 (달러)" value={fmtD(allDivUsd)} color="#38bdf8" small />
              <Stat label="합산 (원화 환산(세후))" value={fmt(allDivKrw + allDivUsd * rate)} color="#34d399" small />
            </div>
            {divNames.length === 0 ? (
              <div style={{ textAlign: "center", padding: 80, color: "var(--text-muted)" }}>배당금 기록이 없습니다</div>
            ) : (
              <div style={{ background: "var(--bg-table)", border: "1px solid rgba(167,139,250,0.08)", borderRadius: 16, overflow: "hidden" }}>
                <table style={{ width: "100%", minWidth: 900, borderCollapse: "collapse", fontSize: 13 }}>
                  <thead>
                    <tr style={{ background: "var(--bg-card)" }}>
                      {["종목명","수령 횟수","세후 원화 배당","세후 달러 배당","합산 (원화 환산(세후))"].map((h, i) => (
                        <th key={i} style={{ padding: "13px 16px", textAlign: "left", fontSize: 10, color: "var(--text-sub)", fontWeight: 600, letterSpacing: "0.1em", textTransform: "uppercase", borderBottom: "1px solid var(--border-light)", fontFamily: "'DM Mono', monospace" }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {divNames.map(name => {
                      const ds = dividends.filter(d => d.name === name);
                      const krwSum = ds.filter(d => (d.currency || "KRW") === "KRW").reduce((a, d) => a + (parseFloat(d.afterTax) || 0), 0);
                      const usdSum = ds.filter(d => d.currency === "USD").reduce((a, d) => a + (parseFloat(d.afterTax) || 0), 0);
                      const total = krwSum + (usdSum * rate);
                      return (
                      <tr key={name} style={{ borderBottom: "1px solid var(--divider-row)" }}>
                        <td style={{ padding: "13px 16px", fontWeight: 600, color: "var(--text)" }}>{name}</td>
                        <td style={{ padding: "13px 16px" }}>
                          <button onClick={() => { setDivFilter(name); setTab("div-list"); }} style={{ background: "none", border: "none", cursor: "pointer", fontFamily: "'DM Mono', monospace", fontSize: 12, color: "#a78bfa", textDecoration: "underline", textUnderlineOffset: 3, padding: 0 }} title={`${name} 배당내역 보기`}>{ds.length}회</button>
                          <div style={{ marginTop: 6, display: "flex", flexWrap: "wrap", gap: 4 }}>
                            {(() => {
                              const months = [...new Set(ds.map(d => d.date.slice(0, 7)))].sort();
                              return months.map(m => {
                                const mds = ds.filter(d => d.date.startsWith(m));
                                const mKrw = mds.filter(d => (d.currency || "KRW") === "KRW").reduce((a, d) => a + (parseFloat(d.afterTax) || 0), 0);
                                const mUsd = mds.filter(d => d.currency === "USD").reduce((a, d) => a + (parseFloat(d.afterTax) || 0), 0);
                                const amtText = mKrw > 0 && mUsd > 0 ? `${Math.round(mKrw).toLocaleString()}원+$${mUsd.toFixed(2)}` : mUsd > 0 ? `$${mUsd.toFixed(2)}` : `${Math.round(mKrw).toLocaleString()}원`;
                                return <span key={m} style={{ fontSize: 10, color: "#718096", background: "var(--bg-hover)", border: "1px solid var(--border-light)", borderRadius: 4, padding: "2px 6px", whiteSpace: "nowrap" }}>{m.slice(5)}월 {mds.length}회 {amtText}</span>;
                              });
                            })()}
                          </div>
                        </td>
                        <td style={{ padding: "13px 16px", fontFamily: "'DM Mono', monospace", fontSize: 13, fontWeight: 600, color: krwSum > 0 ? "#a78bfa" : "#2d3748" }}>{krwSum > 0 ? `+${Math.round(krwSum).toLocaleString()}원` : "—"}</td>
                        <td style={{ padding: "13px 16px", fontFamily: "'DM Mono', monospace", fontSize: 13, fontWeight: 600, color: usdSum > 0 ? "#38bdf8" : "#2d3748" }}>{usdSum > 0 ? `+$${usdSum.toFixed(2)}` : "—"}</td>
                        <td style={{ padding: "13px 16px", fontFamily: "'DM Mono', monospace", fontSize: 14, fontWeight: 700, color: "#34d399" }}>{`+${Math.round(total).toLocaleString()}원`}</td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </>
          );
        })()}

        {/* 배당금 - 연간 탭 (Design Ref: dividend-tax-tracking §6.4) */}
        {page === "dividend" && tab === "div-holdings" && (
          <>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 10, padding: "12px 16px", borderRadius: 12, marginBottom: 16,
            border: `1px solid ${divAutoEnabled ? "rgba(96,165,250,0.35)" : "var(--border)"}`, background: divAutoEnabled ? "rgba(96,165,250,0.06)" : "transparent" }}>
            <div style={{ fontSize: 12, color: "var(--text-sub)", lineHeight: 1.7 }}>
              <b style={{ color: divAutoEnabled ? "#60a5fa" : "var(--text-sub)", fontSize: 13 }}>배당 자동 기록 {divAutoEnabled ? "켜짐" : "꺼짐"}</b>
              <span style={{ marginLeft: 8 }}>앱을 열 때 보유종목의 지급일 지난 배당을 공시 기준으로 기록해요 · 국내 ETF(KODEX·TIGER·ACE·RISE·SOL·PLUS) · 미국 YieldMax</span>
              {divAutoStatus && (
                <div style={{ fontSize: 11, color: "var(--text-muted)" }}>
                  {divAutoStatus.running ? <><Ico n="hourglass" />확인 중…</> : divAutoStatus.error ? `확인 실패: ${divAutoStatus.error}` :
                    `마지막 확인 ${new Date(divAutoStatus.at).toLocaleTimeString("ko-KR", { hour: "2-digit", minute: "2-digit" })} · 새로 기록 ${divAutoStatus.added.length}건${divAutoStatus.updated.length ? ` · 과세표준 반영 ${divAutoStatus.updated.length}건` : ""}${divAutoStatus.added.length ? " (" + divAutoStatus.added.map(d => `${d.name} ${d.date.slice(5)}`).join(", ") + ")" : ""}`}
                  {!divAutoStatus.running && divAutoStatus.log && divAutoStatus.log.length > 0 && <div>{divAutoStatus.log.join(" · ")}</div>}
                </div>
              )}
            </div>
            <div style={{ display: "flex", gap: 6 }}>
              {divAutoEnabled && <button type="button" disabled={divAutoStatus?.running} onClick={() => setDivAutoTick(n => n + 1)} style={{ padding: "6px 12px", borderRadius: 8, fontSize: 12, fontFamily: "inherit", cursor: "pointer", border: "1px solid rgba(96,165,250,0.5)", background: "transparent", color: "#60a5fa" }}>지금 확인</button>}
              <button type="button" onClick={() => setDivAutoEnabled(!divAutoEnabled)} style={{ padding: "6px 12px", borderRadius: 8, fontSize: 12, fontFamily: "inherit", cursor: "pointer", border: "1px solid var(--border)", background: "transparent", color: "var(--text-sub)" }}>{divAutoEnabled ? "끄기" : "켜기"}</button>
            </div>
          </div>
          <DividendHoldings holdings={divHoldings} onChange={saveDivHoldings} krPositions={krPositions} usPositions={usPositions}
            dividends={dividends} rate={rate} brokers={BROKER_DIV} />
          </>
        )}

        {page === "dividend" && tab === "div-yearly" && (() => {
          const yearly = aggregateByYear(dividends, rate);
          const grandTotal = yearly.reduce((a, y) => ({
            pre: a.pre + y.taxable.pre,
            tax: a.tax + y.taxable.tax,
            after: a.after + y.taxable.after,
            exemptTotal: a.exemptTotal + y.exempt.total,
          }), { pre: 0, tax: 0, after: 0, exemptTotal: 0 });
          return (
          <>
            <FinancialIncomeTax dividends={dividends} rate={rate} settings={finTaxSettings} onSave={saveFinTax} />

            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 24, flexWrap: "wrap", gap: 12 }}>
              <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 22, color: "#a78bfa" }}>연간 합계 (USD 기준)</div>
              <div style={{ fontSize: 11, color: "#4a5568", fontStyle: "italic" }}>수령일 환율 기준 환산 (없으면 현재 ₩{Math.round(rate).toLocaleString()})</div>
            </div>

            {yearly.length === 0 ? (
              <div style={{ textAlign: "center", padding: 80, color: "var(--text-muted)" }}>USD 배당 기록이 없습니다</div>
            ) : (
              <>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(380px, 1fr))", gap: 16, marginBottom: 24 }}>
                  {yearly.map(y => (
                    <div key={y.year} style={{ background: "var(--bg-table)", border: "1px solid rgba(167,139,250,0.15)", borderRadius: 16, padding: 20 }}>
                      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 16, paddingBottom: 12, borderBottom: "1px solid var(--divider-row)" }}>
                        <div style={{ fontSize: 28, fontWeight: 700, color: "#a78bfa", fontFamily: "'Playfair Display', serif" }}>{y.year}</div>
                        <div style={{ fontSize: 11, color: "var(--text-sub)" }}>{y.count}건 <span style={{ opacity: 0.6 }}>(면세 {y.exemptCount}건)</span></div>
                      </div>

                      {/* 과세 대상 */}
                      <div style={{ marginBottom: 16 }}>
                        <div style={{ fontSize: 10, color: "var(--text-sub)", letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 8, fontWeight: 600 }}>과세 대상</div>
                        <div style={{ display: "grid", gridTemplateColumns: "auto 1fr", gap: "6px 16px", fontSize: 13, fontFamily: "'DM Mono', monospace" }}>
                          <span style={{ color: "var(--text-sub)" }}>세전:</span>
                          <span style={{ color: "#a78bfa", fontWeight: 600, textAlign: "right" }}>${y.taxable.pre.toFixed(2)}</span>
                          <span style={{ color: "var(--text-sub)" }}>세금:</span>
                          <span style={{ color: "#9ca3af", fontWeight: 600, textAlign: "right" }}>${y.taxable.tax.toFixed(2)}</span>
                          <span style={{ color: "var(--text-sub)" }}>세후:</span>
                          <span style={{ color: "#34d399", fontWeight: 700, textAlign: "right" }}>${y.taxable.after.toFixed(2)}</span>
                          <span style={{ color: "var(--text-sub)", fontSize: 11 }}>≈ KRW:</span>
                          <span style={{ color: "#4a5568", fontWeight: 500, textAlign: "right", fontSize: 12 }}>₩{y.taxable.krw.toLocaleString()}</span>
                        </div>
                      </div>

                      {/* 면세 */}
                      {y.exemptCount > 0 && (
                        <div style={{ padding: 12, background: "rgba(52,211,153,0.06)", borderRadius: 10, border: "1px solid rgba(52,211,153,0.2)" }}>
                          <div style={{ fontSize: 10, color: "#34d399", letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 8, fontWeight: 600 }}>면세 {y.exemptCount}건</div>
                          <div style={{ display: "grid", gridTemplateColumns: "auto 1fr", gap: "6px 16px", fontSize: 13, fontFamily: "'DM Mono', monospace" }}>
                            <span style={{ color: "var(--text-sub)" }}>합계:</span>
                            <span style={{ color: "#34d399", fontWeight: 700, textAlign: "right" }}>${y.exempt.total.toFixed(2)}</span>
                            <span style={{ color: "var(--text-sub)", fontSize: 11 }}>≈ KRW:</span>
                            <span style={{ color: "#4a5568", fontWeight: 500, textAlign: "right", fontSize: 12 }}>₩{y.exempt.krw.toLocaleString()}</span>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                {/* 전체 합계 */}
                <div style={{ background: "var(--bg-card)", border: "1px solid rgba(167,139,250,0.25)", borderRadius: 16, padding: 20 }}>
                  <div style={{ fontSize: 12, color: "var(--text-sub)", letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 12, fontWeight: 600 }}>전체 합계 (모든 연도)</div>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 16, fontFamily: "'DM Mono', monospace" }}>
                    <div><div style={{ fontSize: 10, color: "var(--text-sub)" }}>세전 (과세)</div><div style={{ fontSize: 18, color: "#a78bfa", fontWeight: 700 }}>${grandTotal.pre.toFixed(2)}</div></div>
                    <div><div style={{ fontSize: 10, color: "var(--text-sub)" }}>세금</div><div style={{ fontSize: 18, color: "#9ca3af", fontWeight: 700 }}>${grandTotal.tax.toFixed(2)}</div></div>
                    <div><div style={{ fontSize: 10, color: "var(--text-sub)" }}>세후 (과세)</div><div style={{ fontSize: 18, color: "#34d399", fontWeight: 700 }}>${grandTotal.after.toFixed(2)}</div></div>
                    <div><div style={{ fontSize: 10, color: "var(--text-sub)" }}>면세 합계</div><div style={{ fontSize: 18, color: "#34d399", fontWeight: 700 }}>${grandTotal.exemptTotal.toFixed(2)}</div></div>
                  </div>
                </div>
              </>
            )}
          </>
          );
        })()}

        {/* 미국 거래일지 */}
        {page === "journal" && tab === "us-journal" && (
          <>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 24, flexWrap: "wrap", gap: 12 }}>
              <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 22, color: "#38bdf8" }}>미국 거래 기록</div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <select value={filterMonth} onChange={e => setFilterMonth(e.target.value)} style={{ ...IS, width: "auto", padding: "8px 14px", fontSize: 12 }}>
                  {allMonths.map(m => <option key={m}>{m}</option>)}
                </select>
                <button onClick={delAllUs} style={{ padding: "9px 16px", borderRadius: 10, border: "1px solid rgba(251,113,133,0.3)", background: "rgba(251,113,133,0.06)", color: "#fb7185", fontSize: 12, fontFamily: "inherit" }}><Ico tint={false} n="trash-2" />전체삭제</button>
                <button onClick={() => { setUsForm(EMPTY_US); setEditId(null); setShowTrade(true); }} style={{ padding: "9px 16px", borderRadius: 10, border: "none", background: "linear-gradient(135deg, #38bdf8, #0ea5e9)", color: "#0d1117", fontSize: 12, fontWeight: 700, fontFamily: "inherit" }}>+ 거래 입력</button>
              </div>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 10, marginBottom: 24 }}>
              <Stat label="전체 거래" value={`${usEnriched.length}건`} small />
              <Stat label="보유중" value={`${usHolding.length}건`} color="#38bdf8" small />
              <Stat label="매도완료" value={`${usClosed.length}건`} small />
              {(() => {
                const totalUsd = usClosed.reduce((a, t) => a + t.profitUsd, 0);
                const filteredUsd = usClosed.filter(t => !excludedNames.has(t.name)).reduce((a, t) => a + t.profitUsd, 0);
                const totalKrw = usClosed.reduce((a, t) => a + t.profitKrw, 0);
                const filteredKrw = usClosed.filter(t => !excludedNames.has(t.name)).reduce((a, t) => a + t.profitKrw, 0);
                const hasExclusion = excludedNames.size > 0 && totalUsd !== filteredUsd;
                return hasExclusion ? (
                  <Stat label={`총 손익($) (제외 ${excludedNames.size})`} value={fmtD(filteredUsd)} value2={`전체: ${fmtD(totalUsd)}`} color={filteredUsd >= 0 ? "#f04452" : "#3182f6"} small />
                ) : (
                  <Stat label="총 손익($)" value={fmtD(totalUsd)} value2={fmt(totalKrw)} color={totalUsd >= 0 ? "#f04452" : "#3182f6"} small />
                );
              })()}
              <Stat label="현재 환율" value={exchangeRate ? `₩${exchangeRate.toLocaleString()}` : "—"} color="#38bdf8" small />
            </div>
            {/* Design Ref: position-tracking Addendum — 종목 그룹 카드 뷰 (US) */}
            {usPositions.length === 0 ? (
              <div style={{ textAlign: "center", padding: "70px 20px", color: "var(--text-muted)", fontSize: 14, background: "var(--bg-table)", borderRadius: 16 }}>
                미국 거래 내역이 없습니다
              </div>
            ) : (
              <div>
                {usPositions.map(pos => {
                  const groupTrades = usTrades.filter(t => pos.rowIds.includes(t.id))
                    .sort((a, b) => tradeSortKey(a).localeCompare(tradeSortKey(b)));
                  const expanded = isGroupExpanded(pos);
                  return (
                    <PositionGroupCard
                      key={pos.key}
                      pos={pos}
                      expanded={expanded}
                      onToggle={() => toggleGroup(pos)}
                      currency="USD"
                      onAddBuy={() => openUsAdd({ name: pos.name, broker: pos.broker }, "BUY")}
                      onAddSell={() => openUsAdd({ name: pos.name, broker: pos.broker }, "SELL")}
                    >
                      <div style={{ overflowX: "auto" }}>
                        <table style={{ width: "100%", minWidth: 700, borderCollapse: "collapse", fontSize: 12 }}>
                          <thead>
                            <tr style={{ background: "rgba(45,55,72,0.3)" }}>
                              <th style={{ padding: "8px 12px", fontSize: 10, color: "var(--text-sub)", fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase", textAlign: "left" }}>구분</th>
                              <th style={{ padding: "8px 12px", fontSize: 10, color: "var(--text-sub)", fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase", textAlign: "left" }}>날짜</th>
                              <th style={{ padding: "8px 12px", fontSize: 10, color: "var(--text-sub)", fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase", textAlign: "left" }}>가격($)</th>
                              <th style={{ padding: "8px 12px", fontSize: 10, color: "var(--text-sub)", fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase", textAlign: "left" }}>수량</th>
                              <th style={{ padding: "8px 12px", fontSize: 10, color: "var(--text-sub)", fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase", textAlign: "left" }}>금액($)</th>
                              <th style={{ padding: "8px 12px", fontSize: 10, color: "var(--text-sub)", fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase", textAlign: "left" }}>메모</th>
                              <th style={{ padding: "8px 12px" }} />
                            </tr>
                          </thead>
                          <tbody>
                            {groupTrades.map(t => {
                              const isLegacy = t.kind === undefined;
                              const isBuyRow = t.kind === "BUY" || (isLegacy && t.buyDate);
                              const isSellRow = t.kind === "SELL" || (isLegacy && t.sellDate && t.sellPriceUsd);
                              const showBoth = isLegacy && t.buyDate && t.sellDate && t.sellPriceUsd;
                              const labelStyle = (kind) => ({ padding: "2px 8px", borderRadius: 6, fontSize: 10, fontWeight: 600, background: kind === "BUY" ? "rgba(240,68,82,0.12)" : "rgba(49,130,246,0.12)", color: kind === "BUY" ? "#f04452" : "#3182f6" });
                              const bp = parseFloat(t.buyPriceUsd) || 0;
                              const sp = parseFloat(t.sellPriceUsd) || 0;
                              const q = parseInt(t.quantity) || 0;
                              return (
                                <tr key={t.id} style={{ borderTop: "1px solid var(--divider-row)" }}>
                                  <td style={{ padding: "10px 12px" }}>
                                    {showBoth ? (
                                      <div style={{ display: "flex", gap: 4 }}>
                                        <span style={labelStyle("BUY")}>매수</span>
                                        <span style={labelStyle("SELL")}>매도</span>
                                      </div>
                                    ) : isBuyRow ? <span style={labelStyle("BUY")}>매수</span>
                                      : isSellRow ? <span style={labelStyle("SELL")}>매도</span>
                                      : <span style={{ color: "#a0aec0" }}>—</span>}
                                  </td>
                                  <td style={{ padding: "10px 12px", fontFamily: "'DM Mono', monospace", fontSize: 11, color: "#a0aec0", whiteSpace: "nowrap" }}>
                                    {showBoth ? <>{t.buyDate}<br/><span style={{ color: "#718096" }}>→ {t.sellDate}</span></>
                                      : isBuyRow ? t.buyDate
                                      : isSellRow ? t.sellDate
                                      : "—"}
                                  </td>
                                  <td style={{ padding: "10px 12px", fontFamily: "'DM Mono', monospace", fontSize: 11, whiteSpace: "nowrap" }}>
                                    {showBoth ? <><span style={{ color: "#38bdf8" }}>${bp.toFixed(2)}</span><br/><span style={{ color: "#fb7185" }}>${sp.toFixed(2)}</span></>
                                      : isBuyRow ? <span style={{ color: "#38bdf8" }}>${bp.toFixed(2)}</span>
                                      : isSellRow ? <span style={{ color: "#fb7185" }}>${sp.toFixed(2)}</span>
                                      : "—"}
                                  </td>
                                  <td style={{ padding: "10px 12px", fontFamily: "'DM Mono', monospace", fontSize: 11, color: "#cbd5e0" }}>{q}주</td>
                                  <td style={{ padding: "10px 12px", fontFamily: "'DM Mono', monospace", fontSize: 11, color: "#718096" }}>
                                    {showBoth || isBuyRow ? `$${(q * bp).toFixed(2)}` : isSellRow ? `$${(q * sp).toFixed(2)}` : "—"}
                                  </td>
                                  <td style={{ padding: "10px 12px", fontSize: 11, color: "var(--text-sub)", maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{[t.reason, t.memo].filter(Boolean).join(" / ") || "—"}</td>
                                  <td style={{ padding: "10px 12px", whiteSpace: "nowrap" }}>
                                    <div style={{ display: "flex", gap: 4 }}>
                                      <button onClick={() => openUsEdit(t)} style={{ background: "none", border: "1px solid var(--border)", borderRadius: 6, color: "#718096", padding: "3px 8px", fontSize: 10 }}>수정</button>
                                      <button onClick={() => delUs(t.id)} style={{ background: "none", border: "1px solid rgba(251,113,133,0.2)", borderRadius: 6, color: "#fb7185", padding: "3px 8px", fontSize: 10 }}>삭제</button>
                                    </div>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </PositionGroupCard>
                  );
                })}
              </div>
            )}
          </>
        )}

        {/* 미국 보유현황 */}
        {page === "journal" && tab === "us-holding" && (
          <>
            <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 22, color: "#38bdf8", marginBottom: 24 }}>미국 보유 포지션</div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12, marginBottom: 24 }}>
              <Stat label="보유 종목수" value={`${usHolding.length}개`} color="#38bdf8" />
              {(() => {
                const totalUsd = usHolding.reduce((a, t) => a + t.buyAmtUsd, 0);
                const filteredUsd = usHolding.filter(t => !excludedNames.has(t.name)).reduce((a, t) => a + t.buyAmtUsd, 0);
                const totalKrw = usHolding.reduce((a, t) => a + t.buyAmtKrw, 0);
                const filteredKrw = usHolding.filter(t => !excludedNames.has(t.name)).reduce((a, t) => a + t.buyAmtKrw, 0);
                const hasExclusion = excludedNames.size > 0 && totalUsd !== filteredUsd;
                return hasExclusion ? (
                  <Stat label={`총 매수금액($) (제외 ${excludedNames.size})`} value={fmtD(filteredUsd)} value2={`전체: ${fmtD(totalUsd)}`} />
                ) : (
                  <Stat label="총 매수금액($)" value={fmtD(totalUsd)} value2={fmt(totalKrw)} />
                );
              })()}
              <Stat label="평균 보유일" value={usHolding.length > 0 ? Math.round(usHolding.reduce((a, t) => a + t.holdDays, 0) / usHolding.length) + "일" : "—"} />
            </div>
            {excludedNames.size > 0 && (
              <div style={{ marginBottom: 14, padding: "10px 14px", borderRadius: 8, background: "rgba(167,139,250,0.08)", border: "1px solid rgba(167,139,250,0.25)", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 8 }}>
                <div style={{ fontSize: 12, color: "#a78bfa" }}>
                  <span style={{ fontWeight: 600 }}>제외 {excludedNames.size}종목:</span> {[...excludedNames].join(", ")}
                </div>
                <button onClick={clearExcludes} style={{ padding: "4px 12px", borderRadius: 6, border: "1px solid rgba(167,139,250,0.4)", background: "transparent", color: "#a78bfa", fontSize: 11, cursor: "pointer", fontFamily: "inherit" }}>모두 해제</button>
              </div>
            )}
            {usHolding.length === 0 ? (
              <div style={{ textAlign: "center", padding: 80, color: "var(--text-muted)" }}>보유중인 미국 포지션이 없습니다</div>
            ) : usHolding.sort((a, b) => b.buyAmtUsd - a.buyAmtUsd).map(t => (
              <div key={t.id} style={{ background: "rgba(56,189,248,0.03)", border: "1px solid rgba(56,189,248,0.12)", borderLeft: "3px solid #38bdf8", borderRadius: 14, padding: "18px 22px", display: "grid", gridTemplateColumns: "32px 2fr 1fr 1fr 1fr 1fr 120px", gap: 14, alignItems: "center", marginBottom: 10, opacity: excludedNames.has(t.name) ? 0.45 : 1 }}>
                <input type="checkbox" checked={excludedNames.has(t.name)} onChange={() => toggleExclude(t.name)} title="합계에서 제외" style={{ cursor: "pointer" }} />
                <div>
                  <div style={{ fontSize: 15, fontWeight: 600, color: "var(--text)" }}>{t.name}</div>
                  <div style={{ fontSize: 11, color: "var(--text-sub)", marginTop: 3, fontFamily: "'DM Mono', monospace" }}>{t.buyDate}</div>
                </div>
                <div><div style={{ fontSize: 10, color: "var(--text-sub)", marginBottom: 3 }}>매수가</div><div style={{ fontFamily: "'DM Mono', monospace", fontSize: 13, color: "#38bdf8" }}>${t.bp.toFixed(2)}</div></div>
                <div><div style={{ fontSize: 10, color: "var(--text-sub)", marginBottom: 3 }}>수량</div><div style={{ fontFamily: "'DM Mono', monospace", fontSize: 13 }}>{t.qty}주</div></div>
                <div>
                  <div style={{ fontSize: 10, color: "var(--text-sub)", marginBottom: 3 }}>매수금액</div>
                  <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 13 }}>${t.buyAmtUsd.toFixed(0)}</div>
                  <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 11, color: "var(--text-sub)" }}>{Math.round(t.buyAmtKrw).toLocaleString()}원</div>
                </div>
                <div><div style={{ fontSize: 10, color: "var(--text-sub)", marginBottom: 3 }}>보유일</div><div style={{ fontFamily: "'DM Mono', monospace", fontSize: 13, color: "#38bdf8" }}>{t.holdDays}일</div></div>
                <button onClick={() => openUsAdd(t, "SELL")} style={{ padding: "8px 14px", borderRadius: 8, border: "1px solid rgba(56,189,248,0.35)", background: "rgba(56,189,248,0.06)", color: "#38bdf8", fontSize: 12, fontFamily: "inherit" }}>매도 입력</button>
              </div>
            ))}
          </>
        )}

        {/* 미국 월별손익 */}
        {page === "journal" && tab === "us-monthly" && (
          <>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 24, flexWrap: "wrap", gap: 12 }}>
              <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 22, color: "#38bdf8" }}>미국 월별 손익</div>
              <select value={filterMonth} onChange={e => setFilterMonth(e.target.value)} style={{ ...IS, width: "auto", padding: "8px 14px", fontSize: 12 }}>
                {allMonths.map(m => <option key={m}>{m}</option>)}
              </select>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12, marginBottom: 24 }}>
              <Stat label="매도 거래수" value={`${monthUsClosed.length}건`} />
              {(() => {
                const totalUsd = monthUsClosed.reduce((a, t) => a + t.profitUsd, 0);
                const filteredUsd = monthUsClosed.filter(t => !excludedNames.has(t.name)).reduce((a, t) => a + t.profitUsd, 0);
                const totalKrw = monthUsClosed.reduce((a, t) => a + t.profitKrw, 0);
                const filteredKrw = monthUsClosed.filter(t => !excludedNames.has(t.name)).reduce((a, t) => a + t.profitKrw, 0);
                const hasExc = excludedNames.size > 0 && totalUsd !== filteredUsd;
                return (
                  <>
                    {hasExc ? (
                      <Stat label={`실현 손익 ($) (제외 ${excludedNames.size})`} value={fmtD(filteredUsd)} value2={`전체: ${fmtD(totalUsd)}`} color={filteredUsd >= 0 ? "#f04452" : "#3182f6"} />
                    ) : (
                      <Stat label="실현 손익 ($)" value={fmtD(totalUsd)} color={totalUsd >= 0 ? "#f04452" : "#3182f6"} />
                    )}
                    {hasExc ? (
                      <Stat label={`실현 손익 (원) (제외 ${excludedNames.size})`} value={fmt(filteredKrw)} value2={`전체: ${fmt(totalKrw)}`} color={filteredKrw >= 0 ? "#f04452" : "#3182f6"} />
                    ) : (
                      <Stat label="실현 손익 (원)" value={fmt(totalKrw)} color={totalKrw >= 0 ? "#f04452" : "#3182f6"} />
                    )}
                  </>
                );
              })()}
            </div>
            {monthUsClosed.length === 0 ? (
              <div style={{ textAlign: "center", padding: 40, color: "var(--text-muted)" }}>이 달에 매도한 미국 거래가 없습니다</div>
            ) : monthUsClosed.sort((a, b) => b.profitUsd - a.profitUsd).map(t => (
              <div key={t.id} style={{ background: "rgba(56,189,248,0.02)", border: "1px solid rgba(56,189,248,0.08)", borderLeft: `3px solid ${t.profitUsd >= 0 ? "#f04452" : "#3182f6"}`, borderRadius: 12, padding: "14px 20px", display: "grid", gridTemplateColumns: "32px 1.5fr 1.5fr 0.8fr 1fr 1fr 1fr 1fr", gap: 12, alignItems: "center", marginBottom: 8, opacity: excludedNames.has(t.name) ? 0.45 : 1 }}>
                <input type="checkbox" checked={excludedNames.has(t.name)} onChange={() => toggleExclude(t.name)} title="합계에서 제외" style={{ cursor: "pointer" }} />
                <div style={{ fontWeight: 600, color: "var(--text)" }}>{t.name}</div>
                <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 11, color: "#718096" }}>{t.buyDate} → {t.sellDate}</div>
                <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 12, color: "#718096" }}>{t.holdDays}일</div>
                <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 12, color: "#a0aec0" }}>{t.qty}주 × ${t.bp.toFixed(2)}</div>
                <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 13, fontWeight: 700, color: t.profitUsd >= 0 ? "#f04452" : "#3182f6" }}>{(t.profitUsd >= 0 ? "+" : "") + fmtD(t.profitUsd)}</div>
                <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 12, color: t.profitKrw >= 0 ? "#f04452" : "#3182f6" }}>{(t.profitKrw >= 0 ? "+" : "") + Math.round(t.profitKrw).toLocaleString()}원</div>
                <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 13, fontWeight: 700, color: t.profitRate >= 0 ? "#f04452" : "#3182f6" }}>{fmtPct(t.profitRate)}</div>
              </div>
            ))}
          </>
        )}

        {/* 미국 종목통계 */}
        {page === "journal" && tab === "us-stats" && (
          <>
            <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 22, color: "#38bdf8", marginBottom: 24 }}>미국 종목별 통계</div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12, marginBottom: 24 }}>
              <Stat label="거래 종목수" value={`${usStats.length}개`} small />
              <Stat label="전체 승률" value={usClosed.length > 0 ? ((usClosed.filter(t => t.profitUsd > 0).length / usClosed.length) * 100).toFixed(1) + "%" : "—"} color="#34d399" small />
              <Stat label="총 손익 ($)" value={fmtD(usClosed.reduce((a, t) => a + t.profitUsd, 0))} color={usClosed.reduce((a, t) => a + t.profitUsd, 0) >= 0 ? "#f04452" : "#3182f6"} small />
              <Stat label="총 손익 (원)" value={fmt(usClosed.reduce((a, t) => a + t.profitKrw, 0))} color={usClosed.reduce((a, t) => a + t.profitKrw, 0) >= 0 ? "#f04452" : "#3182f6"} small />
            </div>
            <div style={{ background: "var(--bg-table)", border: "1px solid rgba(56,189,248,0.08)", borderRadius: 16, overflow: "hidden" }}>
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", minWidth: 900, borderCollapse: "collapse", fontSize: 13 }}>
                  <thead>
                    <tr style={{ background: "var(--bg-card)" }}>
                      {["종목명","총 거래","매도 완료","승/패","손익($)","손익(원)","수익률"].map(h => (
                        <th key={h} style={{ padding: "13px 16px", textAlign: "left", fontSize: 10, color: "var(--text-sub)", fontWeight: 600, letterSpacing: "0.1em", textTransform: "uppercase", borderBottom: "1px solid var(--border-light)", fontFamily: "'DM Mono', monospace", whiteSpace: "nowrap" }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {usStats.map(s => (
                      <tr key={s.name} style={{ borderBottom: "1px solid var(--divider-row)" }}>
                        <td style={{ padding: "13px 16px", fontWeight: 600, color: "var(--text)" }}>{s.name}</td>
                        <td style={{ padding: "13px 16px", fontFamily: "'DM Mono', monospace", fontSize: 12, color: "#718096" }}>{s.count}건</td>
                        <td style={{ padding: "13px 16px", fontFamily: "'DM Mono', monospace", fontSize: 12, color: "#718096" }}>{s.closedCount}건</td>
                        <td style={{ padding: "13px 16px", fontFamily: "'DM Mono', monospace", fontSize: 12 }}>
                          <span style={{ color: "#34d399" }}>{s.wins}승</span>
                          <span style={{ color: "var(--text-sub)", margin: "0 4px" }}>/</span>
                          <span style={{ color: "#fb7185" }}>{s.closedCount - s.wins}패</span>
                        </td>
                        <td style={{ padding: "13px 16px", fontFamily: "'DM Mono', monospace", fontSize: 14, fontWeight: 700, color: s.closedCount > 0 ? s.totalProfitUsd >= 0 ? "#f04452" : "#3182f6" : "#2d3748", whiteSpace: "nowrap" }}>{s.closedCount > 0 ? (s.totalProfitUsd >= 0 ? "+" : "") + fmtD(s.totalProfitUsd) : "—"}</td>
                        <td style={{ padding: "13px 16px", fontFamily: "'DM Mono', monospace", fontSize: 13, color: s.closedCount > 0 ? s.totalProfitKrw >= 0 ? "#f04452" : "#3182f6" : "#2d3748", whiteSpace: "nowrap" }}>{s.closedCount > 0 ? (s.totalProfitKrw >= 0 ? "+" : "") + Math.round(s.totalProfitKrw).toLocaleString() + "원" : "—"}</td>
                        <td style={{ padding: "13px 16px", fontFamily: "'DM Mono', monospace", fontSize: 14, fontWeight: 700, color: s.rate != null ? s.rate >= 0 ? "#f04452" : "#3182f6" : "#2d3748" }}>{s.rate != null ? fmtPct(s.rate) : "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}

      </div>

      {/* 국내 거래 모달 */}
      {showTrade && isKr && (
        <Modal title={editId ? "국내 거래 수정" : "국내 거래 입력"} onClose={() => { setShowTrade(false); setEditId(null); }} onSave={saveKrTrade}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
            {/* Design Ref: position-tracking §5.1 — KindSegment + BrokerField */}
            {!editingLegacyKr && <KindSegment value={krForm.kind} onChange={v => setKrForm(p => ({...p, kind: v}))} />}
            {!editingLegacyKr && <div />}
            <BrokerField value={krForm.broker} onChange={v => setKrForm(p => ({...p, broker: v}))} options={BROKER_KR} favBrokers={favBrokers} onToggleFav={toggleFavBroker} />
            <Field label="종목명 *"><input type="text" placeholder="삼성전자" value={krForm.name} onChange={e => setKrForm(p => ({...p, name: e.target.value}))} style={IS} /></Field>

            {/* BUY 모드 */}
            {krForm.kind === "BUY" && (<>
              <Field label="매수일 *"><input type="date" value={krForm.buyDate} onChange={e => setKrForm(p => ({...p, buyDate: e.target.value}))} style={IS} /></Field>
              <Field label="수량 *"><input type="number" placeholder="10" value={krForm.quantity} onChange={e => setKrForm(p => ({...p, quantity: e.target.value}))} style={IS} /></Field>
              <Field label="매수가 (원) *"><input type="number" placeholder="75000" value={krForm.buyPrice} onChange={e => setKrForm(p => ({...p, buyPrice: e.target.value}))} style={IS} /></Field>
              <div />
            </>)}

            {/* SELL 모드 + 잔여 힌트 */}
            {krForm.kind === "SELL" && (<>
              <Field label="매도일 *"><input type="date" value={krForm.sellDate} onChange={e => setKrForm(p => ({...p, sellDate: e.target.value}))} style={IS} /></Field>
              <Field label="수량 *"><input type="number" placeholder="10" value={krForm.quantity} onChange={e => setKrForm(p => ({...p, quantity: e.target.value}))} style={IS} /></Field>
              <Field label="매도가 (원) *"><input type="number" placeholder="55000" value={krForm.sellPrice} onChange={e => setKrForm(p => ({...p, sellPrice: e.target.value}))} style={IS} /></Field>
              <div />
              {krCurrentPosition && (
                <div style={{ gridColumn: "1 / -1", padding: "10px 14px", borderRadius: 8, background: "var(--bg-input)", fontSize: 12, border: "1px solid var(--border)" }}>
                  <Ico n="lightbulb" />현재 보유 <strong>{krCurrentPosition.remaining}주</strong>{krCurrentPosition.avg > 0 && <>, 평단 <strong>{Math.round(krCurrentPosition.avg).toLocaleString()}원</strong></>}
                  {krCurrentPosition.qtyRequest > krCurrentPosition.remaining && krCurrentPosition.qtyRequest > 0 && (
                    <span style={{ color: "#f56565", marginLeft: 8 }}><Ico tint={false} n="triangle-alert" />매도 수량({krCurrentPosition.qtyRequest})이 보유보다 많음</span>
                  )}
                </div>
              )}
            </>)}

            {/* 레거시 편집 모드 (호환): 모든 필드 노출 */}
            {editingLegacyKr && (<>
              <Field label="매수일 *"><input type="date" value={krForm.buyDate} onChange={e => setKrForm(p => ({...p, buyDate: e.target.value}))} style={IS} /></Field>
              <Field label="매도일"><input type="date" value={krForm.sellDate} onChange={e => setKrForm(p => ({...p, sellDate: e.target.value}))} style={IS} /></Field>
              <Field label="수량 *"><input type="number" placeholder="10" value={krForm.quantity} onChange={e => setKrForm(p => ({...p, quantity: e.target.value}))} style={IS} /></Field>
              <Field label="매수가 (원) *"><input type="number" placeholder="75000" value={krForm.buyPrice} onChange={e => setKrForm(p => ({...p, buyPrice: e.target.value}))} style={IS} /></Field>
              <Field label="매도가 (원)"><input type="number" placeholder="매도 시 입력" value={krForm.sellPrice} onChange={e => setKrForm(p => ({...p, sellPrice: e.target.value}))} style={IS} /></Field>
              <div />
            </>)}

            <Field label="매매 이유"><input type="text" value={krForm.reason} onChange={e => setKrForm(p => ({...p, reason: e.target.value}))} style={IS} /></Field>
            <Field label="메모"><input type="text" value={krForm.memo} onChange={e => setKrForm(p => ({...p, memo: e.target.value}))} style={IS} /></Field>
          </div>
        </Modal>
      )}

      {/* 미국 거래 모달 */}
      {showTrade && !isKr && (
        <Modal title={editId ? "미국 거래 수정" : "미국 거래 입력"} onClose={() => { setShowTrade(false); setEditId(null); }} onSave={saveUsTrade}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
            {/* Design Ref: position-tracking §5.1 — KindSegment + BrokerField */}
            {!editingLegacyUs && <KindSegment value={usForm.kind} onChange={v => setUsForm(p => ({...p, kind: v}))} />}
            {!editingLegacyUs && <div />}
            <BrokerField value={usForm.broker} onChange={v => setUsForm(p => ({...p, broker: v}))} options={BROKER_US} favBrokers={favBrokers} onToggleFav={toggleFavBroker} />
            <Field label="종목명 *"><input type="text" placeholder="AAPL" value={usForm.name} onChange={e => setUsForm(p => ({...p, name: e.target.value}))} style={IS} /></Field>

            {/* BUY 모드 */}
            {usForm.kind === "BUY" && (<>
              <Field label="매수일 *"><input type="date" value={usForm.buyDate} onChange={e => setUsForm(p => ({...p, buyDate: e.target.value}))} style={IS} /></Field>
              <Field label="수량 *"><input type="number" placeholder="10" value={usForm.quantity} onChange={e => setUsForm(p => ({...p, quantity: e.target.value}))} style={IS} /></Field>
              <Field label="매수가 ($) *"><input type="number" step="0.01" placeholder="185.50" value={usForm.buyPriceUsd} onChange={e => setUsForm(p => ({...p, buyPriceUsd: e.target.value}))} style={IS} /></Field>
              <div />
            </>)}

            {/* SELL 모드 + 잔여 힌트 */}
            {usForm.kind === "SELL" && (<>
              <Field label="매도일 *"><input type="date" value={usForm.sellDate} onChange={e => setUsForm(p => ({...p, sellDate: e.target.value}))} style={IS} /></Field>
              <Field label="수량 *"><input type="number" placeholder="10" value={usForm.quantity} onChange={e => setUsForm(p => ({...p, quantity: e.target.value}))} style={IS} /></Field>
              <Field label="매도가 ($) *"><input type="number" step="0.01" placeholder="200.00" value={usForm.sellPriceUsd} onChange={e => setUsForm(p => ({...p, sellPriceUsd: e.target.value}))} style={IS} /></Field>
              <div />
              {usCurrentPosition && (
                <div style={{ gridColumn: "1 / -1", padding: "10px 14px", borderRadius: 8, background: "var(--bg-input)", fontSize: 12, border: "1px solid var(--border)" }}>
                  <Ico n="lightbulb" />현재 보유 <strong>{usCurrentPosition.remaining}주</strong>{usCurrentPosition.avg > 0 && <>, 평단 <strong>${usCurrentPosition.avg.toFixed(2)}</strong></>}
                  {usCurrentPosition.qtyRequest > usCurrentPosition.remaining && usCurrentPosition.qtyRequest > 0 && (
                    <span style={{ color: "#f56565", marginLeft: 8 }}><Ico tint={false} n="triangle-alert" />매도 수량({usCurrentPosition.qtyRequest})이 보유보다 많음</span>
                  )}
                </div>
              )}
            </>)}

            {/* 레거시 편집 모드 */}
            {editingLegacyUs && (<>
              <Field label="매수일 *"><input type="date" value={usForm.buyDate} onChange={e => setUsForm(p => ({...p, buyDate: e.target.value}))} style={IS} /></Field>
              <Field label="매도일"><input type="date" value={usForm.sellDate} onChange={e => setUsForm(p => ({...p, sellDate: e.target.value}))} style={IS} /></Field>
              <Field label="수량 *"><input type="number" placeholder="10" value={usForm.quantity} onChange={e => setUsForm(p => ({...p, quantity: e.target.value}))} style={IS} /></Field>
              <Field label="매수가 ($) *"><input type="number" step="0.01" placeholder="185.50" value={usForm.buyPriceUsd} onChange={e => setUsForm(p => ({...p, buyPriceUsd: e.target.value}))} style={IS} /></Field>
              <Field label="매도가 ($)"><input type="number" step="0.01" placeholder="매도 시 입력" value={usForm.sellPriceUsd} onChange={e => setUsForm(p => ({...p, sellPriceUsd: e.target.value}))} style={IS} /></Field>
              <div />
            </>)}

            <Field label="매매 이유"><input type="text" value={usForm.reason} onChange={e => setUsForm(p => ({...p, reason: e.target.value}))} style={IS} /></Field>
            <Field label="메모"><input type="text" value={usForm.memo} onChange={e => setUsForm(p => ({...p, memo: e.target.value}))} style={IS} /></Field>
          </div>
        </Modal>
      )}

      {/* 배당금 모달 — Design Ref: dividend-tax-tracking §6.1 */}
      {showDiv && (
        <Modal title={editId ? "배당금 수정" : "배당금 입력"} onClose={() => { setShowDiv(false); setEditId(null); setDivFormDirty({ tax: false, afterTax: false, perShare: false }); }} onSave={saveDiv}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
            <BrokerField value={divForm.broker} onChange={v => setDivForm(p => ({...p, broker: v}))} options={BROKER_DIV} favBrokers={favBrokers} onToggleFav={toggleFavBroker} />
            <div />
            <Field label="수령일 *"><input type="date" value={divForm.date} onChange={e => setDivForm(p => ({...p, date: e.target.value}))} style={IS} /></Field>
            <FavPicker label="종목명 *" value={divForm.name} onChange={v => setDivForm(p => (editId ? { ...p, name: v } : applyHoldingDefaults({ ...p, name: v }, divHoldings)))} options={divNameOptions} favs={favDivNames} onToggleFav={toggleFavDivName} placeholder="종목명 (자주 쓰면 ★)" />
            <Field label="세전 배당금 *"><input type="number" step="0.01" value={divForm.amount} onChange={e => setDivForm(p => ({...p, amount: e.target.value}))} style={IS} />
              {divForm.currency === "USD" && usDist.status !== "idle" && (() => {
                const box = { marginTop: 6, padding: "7px 9px", borderRadius: 8, fontSize: 11, lineHeight: 1.6, border: "1px solid var(--border)", background: "var(--bg-hover)", color: "var(--text-sub)" };
                const btn = (label, onClick) => (
                  <button type="button" onClick={onClick} style={{ marginLeft: 6, padding: "2px 8px", borderRadius: 6, fontSize: 11, fontFamily: "inherit", cursor: "pointer", border: "1px solid rgba(96,165,250,0.5)", background: "rgba(96,165,250,0.1)", color: "#60a5fa" }}>{label}</button>
                );
                const sh = parseFloat(divForm.shares);
                const apply = (it) => {
                  if (!(sh > 0)) return;
                  const v = String(+(it.amount * sh).toFixed(2));
                  usAutoRef.current = v;
                  setDivForm(p => ({ ...p, amount: v }));
                };
                if (usDist.status === "loading") return <div style={box}><Ico n="hourglass" />{usDist.ticker} YieldMax 공식 분배 표에서 이 날짜 회차를 찾는 중…</div>;
                if (usDist.status === "error") return <div style={box}>YieldMax 사이트 조회 실패 {btn("다시 시도", () => setUsDistTick(n => n + 1))}</div>;
                if (usDist.status === "nomatch") {
                  const recent = (usDist.data?.items || []).slice(0, 4);
                  return (
                    <div style={box}>
                      수령일({divForm.date}) 근처(미국 지급일 당일~+4일)에 {usDist.ticker} 분배가 없어요. 최근 지급:
                      {recent.map(x => <span key={x.payDate}>{btn(`${x.payDate.slice(5)} $${x.amount}`, () => apply(x))}</span>)}
                    </div>
                  );
                }
                const m = usDist.match;
                const calc = sh > 0 ? +(m.amount * sh).toFixed(2) : null;
                const applied = calc != null && Math.abs(parseFloat(divForm.amount) - calc) < 0.005;
                return (
                  <div style={{ ...box, borderColor: applied ? "rgba(52,211,153,0.4)" : "rgba(96,165,250,0.4)" }}>
                    <b style={{ color: applied ? "#34d399" : "#60a5fa" }}>{applied ? "✓ 자동 입력" : "YieldMax 공시"}</b>{" "}
                    미국 지급일 {m.payDate.slice(5)} · 1주당 <b style={{ color: "var(--text)" }}>${m.amount}</b>
                    {calc != null ? <> × {sh.toLocaleString()}주 = <b style={{ color: "var(--text)" }}>${calc.toLocaleString()}</b></> : " — 주수를 넣으면 세전이 계산돼요"}
                    {m.roc != null && <span style={{ color: "var(--text-muted)" }}> · ROC {m.roc}%</span>}
                    {!applied && calc != null && btn("적용", () => apply(m))}
                    {!applied && calc != null && divForm.amount !== "" && <div style={{ color: "#fbbf24" }}><Ico tint={false} n="triangle-alert" />입력한 세전(${divForm.amount})이 공시 기준(${calc})과 달라요. 명세서 금액이 맞으면 그대로 두세요.</div>}
                  </div>
                );
              })()}
            </Field>
            <Field label="통화">
              <select value={divForm.currency || "KRW"} onChange={e => { setDivForm(p => ({...p, currency: e.target.value})); setDivFormDirty({ tax: false, afterTax: false, perShare: false, rate: false }); }} style={IS}>
                <option value="KRW">KRW (원화)</option>
                <option value="USD">USD (달러)</option>
              </select>
            </Field>
            <Field label={divForm.currency === 'USD' && !divForm.taxExempt ? "세후 배당금 (자동: 세전×85%)" : "세후 배당금"}>
              <input
                type="number" step="0.01"
                value={divForm.afterTax === '' || divForm.afterTax === undefined ? "" : divForm.afterTax}
                onChange={e => { setDivForm(p => ({...p, afterTax: e.target.value})); setDivFormDirty(d => ({...d, afterTax: true})); }}
                disabled={divForm.taxExempt}
                placeholder={divForm.currency === 'KRW' ? "명세서 세후 금액" : ""}
                style={{ ...IS, opacity: divForm.taxExempt ? 0.5 : 1, cursor: divForm.taxExempt ? "not-allowed" : "text" }}
              />
            </Field>
            <Field label={divForm.taxExempt ? "세금" : "세금 (자동: 세전−세후)"}>
              <input
                type="number" step="0.01"
                value={divForm.tax === '' || divForm.tax === undefined ? "" : divForm.tax}
                onChange={e => { setDivForm(p => ({...p, tax: e.target.value})); setDivFormDirty(d => ({...d, tax: true})); }}
                disabled={divForm.taxExempt}
                placeholder="세전 − 세후"
                style={{ ...IS, opacity: divForm.taxExempt ? 0.5 : 1, cursor: divForm.taxExempt ? "not-allowed" : "text" }}
              />
            </Field>
            <Field label="주수"><input type="number" step="1" min="0" placeholder="보유 주수" value={divForm.shares || ""} onChange={e => setDivForm(p => ({...p, shares: e.target.value}))} style={IS} /></Field>
            <Field label="주당 배당금 (자동: 세전÷주수)">
              <input
                type="number" step="0.0001"
                value={divForm.perShare === '' || divForm.perShare === undefined ? "" : divForm.perShare}
                onChange={e => { setDivForm(p => ({...p, perShare: e.target.value})); setDivFormDirty(d => ({...d, perShare: true})); }}
                placeholder="세전 ÷ 주수"
                style={IS}
              />
            </Field>
            {divForm.currency === 'USD' && (
              <Field label="수령일 환율 (자동: $1=₩)">
                <input
                  type="number" step="0.01"
                  value={divForm.rate === '' || divForm.rate === undefined ? "" : divForm.rate}
                  onChange={e => { setDivForm(p => ({...p, rate: e.target.value})); setDivFormDirty(d => ({...d, rate: true})); }}
                  placeholder="수령일 ECB 환율"
                  style={IS}
                />
              </Field>
            )}
            <Field label="특수계좌/비과세">
              <label style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 12px", border: "1px solid var(--border)", borderRadius: 10, cursor: "pointer", background: divForm.taxExempt ? "rgba(52,211,153,0.08)" : "transparent", borderColor: divForm.taxExempt ? "rgba(52,211,153,0.4)" : "var(--border)" }}>
                <input
                  type="checkbox"
                  checked={!!divForm.taxExempt}
                  onChange={e => { setDivForm(p => ({...p, taxExempt: e.target.checked})); setDivFormDirty(d => ({ ...d, tax: false, afterTax: false })); }}
                  style={{ width: 16, height: 16, accentColor: "#34d399" }}
                />
                <span style={{ fontSize: 13, color: divForm.taxExempt ? "#34d399" : "var(--text-sub)" }}>ISA 등 비과세 (세금 0)</span>
              </label>
            </Field>
            <Field label="주당 과세표준 (운용사 공시)">
              <input
                type="number" step="0.0001"
                value={divForm.taxBasePerShare === '' || divForm.taxBasePerShare === undefined ? "" : divForm.taxBasePerShare}
                onChange={e => { setDivForm(p => ({...p, taxBasePerShare: e.target.value})); setDivFormDirty(d => ({ ...d, tax: false, afterTax: false })); }}
                disabled={divForm.taxExempt}
                placeholder="있으면 세금=과세표준×주수×15%"
                style={{ ...IS, opacity: divForm.taxExempt ? 0.5 : 1, cursor: divForm.taxExempt ? "not-allowed" : "text" }}
              />
              {!divForm.taxExempt && etfTax.status !== "idle" && (() => {
                const iss = etfTax.data?.issuer;
                const src = iss ? `${iss.brand} · ${iss.label}` : "운용사";
                const m = etfTax.match;
                const applied = m && m.taxBase != null && String(divForm.taxBasePerShare) === String(m.taxBase) && (divForm.taxPerShare === "" || divForm.taxPerShare == null);
                const apply = (v) => {
                  etfAutoRef.current = String(v);
                  setDivForm(p => ({ ...p, taxBasePerShare: String(v), taxPerShare: "" }));
                  setDivFormDirty(d => ({ ...d, tax: false, afterTax: false }));
                };
                const box = { marginTop: 6, padding: "7px 9px", borderRadius: 8, fontSize: 11, lineHeight: 1.6, border: "1px solid var(--border)", background: "var(--bg-hover)", color: "var(--text-sub)" };
                const btn = (label, onClick) => (
                  <button type="button" onClick={onClick} style={{ marginLeft: 6, padding: "2px 8px", borderRadius: 6, fontSize: 11, fontFamily: "inherit", cursor: "pointer", border: "1px solid rgba(96,165,250,0.5)", background: "rgba(96,165,250,0.1)", color: "#60a5fa" }}>{label}</button>
                );
                const recent = (etfTax.data?.items || []).filter(x => x.taxBase != null).slice(0, 3);
                const amt = parseFloat(divForm.amount), sh = parseFloat(divForm.shares);
                const mismatch = m && m.amount != null && amt > 0 && sh > 0 && Math.abs(amt / sh - m.amount) > 1;
                if (etfTax.status === "loading") return <div style={box}><Ico n="hourglass" />{etfBrandOf(divForm.name)} 운용사 공시에서 이번 분배의 주당 과세표준을 찾는 중…</div>;
                if (etfTax.status === "nocode") return <div style={box}>종목코드를 찾지 못해 자동 조회를 못 했어요. 배당 보유종목에 코드를 넣어주면 조회돼요.</div>;
                if (etfTax.status === "error") return <div style={box}>운용사 사이트 조회 실패 {btn("다시 시도", () => setEtfTaxTick(n => n + 1))}</div>;
                if (etfTax.status === "unsupported" || etfTax.status === "notfound") return <div style={box}>{src}에서 이 종목의 분배 내역을 찾지 못했어요. 직접 입력해 주세요.</div>;
                if (etfTax.status === "pending") return (
                  <div style={{ ...box, borderColor: "rgba(251,191,36,0.4)" }}>
                    <b style={{ color: "#fbbf24" }}>아직 미발표</b> — {m.baseDate?.slice(5)} 기준 분배({m.amount}원)의 과세표준이 {src} 사이트에 아직 안 올라왔어요. 보통 지급 후 1~2주 뒤 올라와요. 나중에 이 배당을 <b>수정</b>으로 열면 다시 찾아요.
                    {btn("다시 확인", () => setEtfTaxTick(n => n + 1))}
                  </div>
                );
                if (etfTax.status === "nomatch") return (
                  <div style={box}>
                    수령일({divForm.date}) 근처 분배 기록이 {src}에 없어요. 최근 공시:
                    {recent.length === 0 ? " 없음" : recent.map(x => <span key={x.baseDate}>{btn(`${x.payDate?.slice(5) || x.baseDate?.slice(5)} 지급 · ${x.taxBase}원`, () => apply(x.taxBase))}</span>)}
                  </div>
                );
                // ok
                const zero = m.taxBase === 0;
                const full = m.amount != null && m.taxBase >= m.amount;
                return (
                  <div style={{ ...box, borderColor: applied ? "rgba(52,211,153,0.4)" : "rgba(96,165,250,0.4)" }}>
                    <b style={{ color: applied ? "#34d399" : "#60a5fa" }}>{applied ? "✓ 자동 적용" : "운용사 공시"}</b>{" "}
                    {m.payDate?.slice(5)} 지급분 · 분배금 {m.amount}원 중 과세표준 <b style={{ color: "var(--text)" }}>{m.taxBase}원</b>
                    {zero ? " → 이번 달 비과세" : full ? " → 전액 과세" : ` → ${Math.round((m.taxBase / m.amount) * 100)}%만 과세`}
                    <span style={{ color: "var(--text-muted)" }}> ({src})</span>
                    {!applied && btn("적용", () => apply(m.taxBase))}
                    {mismatch && <div style={{ color: "#fbbf24" }}><Ico tint={false} n="triangle-alert" />입력한 주당 배당금({Math.round((amt / sh) * 100) / 100}원)이 공시 분배금({m.amount}원)과 달라요. 수령일·주수를 확인해 주세요.</div>}
                  </div>
                );
              })()}
            </Field>
            <Field label="1주당 세금 (부분과세 종목)">
              <input
                type="number" step="0.0001" min="0"
                value={divForm.taxPerShare === '' || divForm.taxPerShare === undefined ? "" : divForm.taxPerShare}
                onChange={e => { setDivForm(p => ({...p, taxPerShare: e.target.value})); setDivFormDirty(d => ({ ...d, tax: false, afterTax: false })); }}
                disabled={divForm.taxExempt}
                placeholder="예: 커버드콜 ETF — 있으면 세금=1주당 세금×주수"
                style={{ ...IS, opacity: divForm.taxExempt ? 0.5 : 1, cursor: divForm.taxExempt ? "not-allowed" : "text" }}
              />
              {!divForm.taxExempt && (() => {
                const norm = (s) => String(s || "").replace(/\s+/g, "").toLowerCase();
                const holding = divHoldings.find(h => norm(h.name) === norm(divForm.name));
                const isPartial = holding && (holding.taxType === "partial");
                const hist = (divTaxHistory[norm(divForm.name)] || []).filter(x => x.id !== editId);
                const last = hist[0];
                if (!isPartial && !last) return null;
                const unit = divForm.currency === "USD" ? "$" : "";
                const unitSuffix = divForm.currency === "USD" ? "" : "원";
                const setTps = (v) => { setDivForm(p => ({ ...p, taxPerShare: v })); setDivFormDirty(d => ({ ...d, tax: false, afterTax: false })); };
                const chip = (label, onClick, active) => (
                  <button type="button" onClick={onClick} style={{ padding: "4px 9px", borderRadius: 6, fontSize: 11, fontFamily: "inherit", cursor: "pointer",
                    border: `1px solid ${active ? "rgba(251,191,36,0.6)" : "var(--border)"}`, background: active ? "rgba(251,191,36,0.12)" : "transparent", color: active ? "#fbbf24" : "var(--text-sub)" }}>{label}</button>
                );
                const cur = String(divForm.taxPerShare ?? "");
                return (
                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 6 }}>
                    {chip("이번 달 비과세 (0)", () => setTps("0"), cur === "0")}
                    {last && chip(`지난번과 같게 (${unit}${last.tps}${unitSuffix} · ${last.date.slice(5)})`, () => setTps(String(last.tps)), cur === String(last.tps))}
                    {isPartial && holding.taxPerShare !== "" && holding.taxPerShare != null && chip(`기본값 (${unit}${holding.taxPerShare}${unitSuffix})`, () => setTps(String(holding.taxPerShare)), cur === String(holding.taxPerShare))}
                    {chip("일반 과세 (15%)", () => setTps(""), cur === "")}
                  </div>
                );
              })()}
            </Field>
            <Field label="메모"><input type="text" value={divForm.memo} onChange={e => setDivForm(p => ({...p, memo: e.target.value}))} style={IS} /></Field>
          </div>
        </Modal>
      )}

      {/* Design Ref: position-tracking §5.1 (Addendum) — 매도 결과 모달 */}
      <SellResultModal result={sellResult} onClose={() => setSellResult(null)} />

      </div>
    </div>
  );
}

// 로그인 유지: 서버 토큰이 있으면 토큰 만료(30일, 앱을 열 때 7일 이내면 자동 연장)까지, 없으면 예전처럼 1시간 (2026-10-14)
const SESSION_DURATION = 60 * 60 * 1000; // 토큰 없는 예전 로그인용 1시간
const WARN_BEFORE = 10 * 60 * 1000; // 만료 10분 전 알림
const MAX_TIMER_MS = 2147483000; // setTimeout 최대 대기(약 24.8일)

export default function App() {
  const [theme, setTheme] = useState(() => localStorage.getItem("sjv3-theme") || "dark");
  useEffect(() => {
    document.documentElement.setAttribute("data-theme", theme);
    localStorage.setItem("sjv3-theme", theme);
  }, [theme]);
  const toggleTheme = () => setTheme(t => t === "dark" ? "light" : "dark");

  const [user, setUser] = useState(() => {
    try { return JSON.parse(localStorage.getItem("sjv3-user")); } catch { return null; }
  });
  const [loggedIn, setLoggedIn] = useState(() => !!localStorage.getItem("sjv3-user") || !!localStorage.getItem("sjv3-guest"));
  const [showSessionWarn, setShowSessionWarn] = useState(false);
  const [sessionRemain, setSessionRemain] = useState(0);
  const sessionTimer = useRef(null);
  const warnTimer = useRef(null);
  const countdownTimer = useRef(null);

  const clearSessionTimers = useCallback(() => {
    if (sessionTimer.current) clearTimeout(sessionTimer.current);
    if (warnTimer.current) clearTimeout(warnTimer.current);
    if (countdownTimer.current) clearInterval(countdownTimer.current);
    sessionTimer.current = null;
    warnTimer.current = null;
    countdownTimer.current = null;
  }, []);

  const startSessionTimer = useCallback((expiresAt) => {
    clearSessionTimers();
    const now = Date.now();
    const remaining = expiresAt - now;
    if (remaining <= 0) return false;

    // 만료 시 자동 로그아웃
    // setTimeout 은 약 24.8일(2^31-1ms)이 최대라 30일 토큰은 나눠서 기다림 — 넘기면 즉시 실행돼 바로 로그아웃되는 문제 방지
    sessionTimer.current = setTimeout(() => {
      if (expiresAt - Date.now() > 1000) { startSessionTimerRef.current(expiresAt); return; }
      setShowSessionWarn(false);
      setUser(null);
      setLoggedIn(false);
      localStorage.removeItem("sjv3-user");
      localStorage.removeItem("sjv3-guest");
      localStorage.removeItem("sjv3-session-expires");
      clearAuth();
    }, Math.min(remaining, MAX_TIMER_MS));

    // 10분 전 경고
    const warnIn = remaining - WARN_BEFORE;
    if (warnIn > MAX_TIMER_MS) {
      // 아직 한참 남음 → 위 타이머가 다시 걸 때 설정
    } else if (warnIn > 0) {
      warnTimer.current = setTimeout(() => {
        setSessionRemain(WARN_BEFORE);
        setShowSessionWarn(true);
        countdownTimer.current = setInterval(() => {
          const left = Math.max(0, expiresAt - Date.now());
          setSessionRemain(left);
          if (left <= 0) clearInterval(countdownTimer.current);
        }, 1000);
      }, warnIn);
    } else {
      // 이미 10분 이내
      setSessionRemain(remaining);
      setShowSessionWarn(true);
      countdownTimer.current = setInterval(() => {
        const left = Math.max(0, expiresAt - Date.now());
        setSessionRemain(left);
        if (left <= 0) clearInterval(countdownTimer.current);
      }, 1000);
    }
    return true;
  }, [clearSessionTimers]);
  const startSessionTimerRef = useRef(startSessionTimer);
  startSessionTimerRef.current = startSessionTimer;

  const extendSession = useCallback(async () => {
    if (getAuth()) {
      const a = await refreshAuthIfNeeded(true);
      if (a) { localStorage.setItem("sjv3-session-expires", String(a.exp)); setShowSessionWarn(false); startSessionTimer(a.exp); return; }
    }
    const newExpires = Date.now() + SESSION_DURATION;
    localStorage.setItem("sjv3-session-expires", String(newExpires));
    setShowSessionWarn(false);
    startSessionTimer(newExpires);
  }, [startSessionTimer]);

  // 앱 시작 시 기존 세션 복원
  useEffect(() => {
    if (!loggedIn || !user) return;
    const auth = getAuth();
    if (auth) {
      // 토큰 로그인: 토큰 만료 시각까지 유지, 7일 이내로 남았으면 조용히 연장
      localStorage.setItem("sjv3-session-expires", String(auth.exp));
      startSessionTimer(auth.exp);
      refreshAuthIfNeeded().then(a => { if (a && a.exp !== auth.exp) { localStorage.setItem("sjv3-session-expires", String(a.exp)); startSessionTimer(a.exp); } });
      return clearSessionTimers;
    }
    const saved = localStorage.getItem("sjv3-session-expires");
    if (saved) {
      const expiresAt = parseInt(saved);
      if (!startSessionTimer(expiresAt)) {
        // 이미 만료됨
        setUser(null);
        setLoggedIn(false);
        localStorage.removeItem("sjv3-user");
        localStorage.removeItem("sjv3-guest");
        localStorage.removeItem("sjv3-session-expires");
      }
    } else {
      // 세션 시간 없으면 새로 시작
      const newExpires = Date.now() + SESSION_DURATION;
      localStorage.setItem("sjv3-session-expires", String(newExpires));
      startSessionTimer(newExpires);
    }
    return clearSessionTimers;
  }, []); // eslint-disable-line

  const handleLogin = useCallback(async (userData) => {
    if (userData) {
      // 토큰·기기 확인표는 따로 보관(authFetch / LoginScreen) — 사용자 정보에는 id·닉네임만
      const u = { id: userData.id, nickname: userData.nickname };
      setUser(u);
      localStorage.setItem("sjv3-user", JSON.stringify(u));
      // 세션 타이머 시작 (토큰이 있으면 30일, 없으면 1시간)
      const expiresAt = userData.tokenExp || (Date.now() + SESSION_DURATION);
      localStorage.setItem("sjv3-session-expires", String(expiresAt));
      startSessionTimer(expiresAt);
      try {
        const res = await fetch(`${BASE_API}/user/load?userId=${userData.id}`);
        if (res.ok) {
          const data = await res.json();
          if (data.krTrades?.length) saveLS(SK, data.krTrades);
          if (data.usTrades?.length) saveLS(USK, data.usTrades);
          if (data.dividends?.length) saveLS(DK, data.dividends);
          if (data.watchlist?.length) saveLS("sjv3-watchlist", data.watchlist);
        }
      } catch {}
    } else {
      localStorage.setItem("sjv3-guest", "true");
    }
    setLoggedIn(true);
  }, [startSessionTimer]);

  const handleLogout = useCallback(() => {
    clearSessionTimers();
    setUser(null);
    setLoggedIn(false);
    setShowSessionWarn(false);
    localStorage.removeItem("sjv3-user");
    localStorage.removeItem("sjv3-guest");
    localStorage.removeItem("sjv3-session-expires");
    clearAuth();
  }, [clearSessionTimers]);

  // 서버가 토큰을 거절(만료·PIN 변경)하면 로그아웃하고 안내
  useEffect(() => {
    const onExpired = () => { try { sessionStorage.setItem("dgds-login-msg", "로그인이 만료되었습니다. 다시 로그인해 주세요."); } catch {} handleLogout(); };
    window.addEventListener("dgds-auth-expired", onExpired);
    return () => window.removeEventListener("dgds-auth-expired", onExpired);
  }, [handleLogout]);

  if (!loggedIn) return <LoginScreen onLogin={handleLogin} />;
  return <>
    <AppMain user={user} onLogout={handleLogout} theme={theme} toggleTheme={toggleTheme} />
    {showSessionWarn && user && (
      <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(0,0,0,0.6)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div style={{ background: "var(--bg-modal)", borderRadius: 16, padding: "32px 36px", maxWidth: 380, border: "1px solid var(--border)", textAlign: "center" }}>
          <div style={{ fontSize: 36, marginBottom: 12 }}>&#9200;</div>
          <div style={{ fontSize: 18, fontWeight: 700, color: "var(--text)", marginBottom: 8 }}>세션이 곧 만료됩니다</div>
          <div style={{ fontSize: 28, fontWeight: 700, color: "#f0c040", marginBottom: 16, fontFamily: "'DM Mono', monospace" }}>
            {Math.floor(sessionRemain / 60000)}:{String(Math.floor((sessionRemain % 60000) / 1000)).padStart(2, '0')}
          </div>
          <div style={{ fontSize: 13, color: "#718096", marginBottom: 24, lineHeight: 1.7 }}>
            로그인 세션이 만료되면 자동으로 로그아웃됩니다.<br/>계속 이용하시려면 연장해주세요.
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={handleLogout} style={{ flex: 1, padding: "12px", borderRadius: 10, border: "1px solid var(--border)", background: "transparent", color: "#718096", fontSize: 14, fontFamily: "inherit", cursor: "pointer" }}>로그아웃</button>
            <button onClick={extendSession} style={{ flex: 1, padding: "12px", borderRadius: 10, border: "none", background: "linear-gradient(135deg, #f0c040, #d4a017)", color: "#0d1117", fontSize: 14, fontWeight: 700, fontFamily: "inherit", cursor: "pointer" }}>연장하기</button>
          </div>
        </div>
      </div>
    )}
  </>;
}
